import { useState, useEffect, useRef, useMemo } from 'react'
import { X, CreditCard, Banknote, Gift, Check, ArrowLeft, Loader2, AlertCircle, ScanLine, Printer, Trash2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { usePOSStore, DEFAULT_UI_FILTERS, isReceiptPartiallyPaid } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useUIStore } from '@/stores/uiStore'
import { useSessionStore } from '@/stores/sessionStore'
import { PAYMENT_TYPES, RECEIPT_STATUS, LOCKED_RECEIPT_STATUSES, receiptKeys, type PaymentTypeNumber, type Receipt } from '@/api/receipts'
import { useV2ProcessPayment as useProcessPayment, usePrintReceipt, useOpenCashDrawer, useCheckGiftCardBalance, useV2VoidPayment, useUpdateReceiptItem, useDeleteReceiptItem, useV2ApplyLoyaltyPoints, useSettleRewardShare, extractGiftCardBalance, extractGiftCardId, isBirchmountCard } from '@/api'
import { customerSpringbigKey, useCustomerSpringbig } from '@/api/customers'
import {
  LoyaltyRedemptionPicker,
  SPRINGBIG_PERCENT_TYPE,
  computePercentDollar,
} from '@/components/customers'
import { useCommitCart, buildCommitRequest, fetchCartPreview, cartToRequest } from '@/api/cart'
import {
  useStartTerminalPayment,
  useCancelTerminalPayment,
  useTerminalPaymentSession,
  type TerminalPaymentSession,
  type StartTerminalPaymentBody,
} from '@/api/terminalPayments'
import { V2ApiError, classifyPreviewError, idempotencyKey, v2ErrorCode, v2ErrorText } from '@/api/v2'
import { useStoreTaxLabel } from '@/api/stores'
import { usePOSConfig } from '@/api/config'
import { v2, v2api } from '@/api/v2'
import { formatPrice, extractTaxes, roundCashChange } from '@/lib/utils'
import { cartDiscountDisplayAmount, redemptionSavesNothing } from '@/lib/cart'
import {
  approvalSubtitle,
  approvedByNames,
  approvedByText,
  discountGateBannerText,
  gateDialogInitialError,
  isDiscountGateCode,
  limitFromErrorExtra,
  type ApprovalItem,
} from '@/lib/discountLimit'
import { ManagerApprovalDialog } from '@/components/functions/ManagerApprovalDialog'
import {
  approvalRows,
  approveCartItems,
  gateApprovalItems,
  useDiscountLimit,
} from '@/hooks/useDiscountApproval'
import { broadcastPaymentApproved } from '@/lib/cartChannel'
import { captureCheckoutCompleted } from '@/lib/saleAnalytics'
import type { PaymentType } from '@/types'
import { PaymentMethodLabel } from '@/components/CardBrandMark'

type CheckoutStep = 'method' | 'cash' | 'card' | 'card-type' | 'card-terminal' | 'online-card' | 'gift-card' | 'loyalty' | 'processing' | 'complete' | 'error'

interface PaymentMethod {
  id: PaymentType
  label: string
  icon: typeof CreditCard
  color: string
  paymentTypeNumber: PaymentTypeNumber
}

const paymentMethods: PaymentMethod[] = [
  { id: 'debit', label: 'Standalone', icon: CreditCard, color: 'bg-blue-500', paymentTypeNumber: PAYMENT_TYPES.DEBIT },
  { id: 'credit_visa', label: 'Credit/Debit', icon: CreditCard, color: 'bg-purple-500', paymentTypeNumber: PAYMENT_TYPES.VISA },
  { id: 'cash', label: 'Cash', icon: Banknote, color: 'bg-green-500', paymentTypeNumber: PAYMENT_TYPES.CASH },
  { id: 'gift_card', label: 'Gift Card', icon: Gift, color: 'bg-amber-500', paymentTypeNumber: PAYMENT_TYPES.GIFT_CARD },
]

// Refund-only method: gateway credit to the card behind an online
// (Moneris Checkout) capture. Rendered in the method grid only when the
// refund's source payments carry the synthetic online entry with
// remaining > 0. paymentTypeNumber is replaced at select time with the
// online card's actual brand int from that entry.
const ONLINE_CARD_METHOD: PaymentMethod = {
  id: 'online_card',
  label: 'Online Card',
  icon: CreditCard,
  color: 'bg-indigo-500',
  paymentTypeNumber: PAYMENT_TYPES.VISA,
}

// Standalone (manual) sub-options: which card brand the cashier ran on
// the non-integrated terminal. Picked before the 'card' confirm step so
// the JT records the right payment_type — without it the BE has no way
// to disambiguate brand and defaults the receipt label (currently
// surfaces as "Visa" for everything). Interac collapses onto DEBIT(215)
// because Canadian debit has no separate enum value.
interface StandaloneCardType {
  id: 'visa' | 'mastercard' | 'amex' | 'interac'
  label: string
  color: string
  paymentTypeNumber: PaymentTypeNumber
}

const STANDALONE_CARD_TYPES: StandaloneCardType[] = [
  { id: 'visa', label: 'Visa', color: 'bg-blue-600', paymentTypeNumber: PAYMENT_TYPES.VISA },
  { id: 'mastercard', label: 'Mastercard', color: 'bg-red-500', paymentTypeNumber: PAYMENT_TYPES.MASTER_CARD },
  { id: 'amex', label: 'AMEX', color: 'bg-cyan-600', paymentTypeNumber: PAYMENT_TYPES.AMEX },
  { id: 'interac', label: 'Interac', color: 'bg-amber-600', paymentTypeNumber: PAYMENT_TYPES.DEBIT },
]

// Card-rail methods that go to a physical terminal: integrated
// (credit_visa) and standalone (debit). Used to gate card-specific
// rules — notably the hard block on overpayment, since a card swipe
// can't return cash change. Cash overpayment is fine (becomes change);
// gift card overpayment goes through a different code path entirely.
const CARD_METHOD_IDS = new Set<PaymentType>(['debit', 'credit_visa'])
const isCardMethod = (m: PaymentMethod) => CARD_METHOD_IDS.has(m.id)

// True when the receipt already carries a SpringBig redemption as a POINTS
// payment (type 299). A staged reward/offer is converted into such a payment
// on the first tender; the BE only consumes it from SpringBig at receipt
// close, so the offer keeps listing as "available" mid-receipt. Without this
// signal the cashier can re-stage and re-apply the same redemption on a later
// partial, posting a second POINTS JT while SpringBig records one redemption.
// Voided JTs are excluded by the serializer, so voiding the loyalty payment
// flips this back to false and re-enables redemption.
const hasLoyaltyPayment = (
  payments: ReadonlyArray<{ payment_type?: number }> | undefined | null,
): boolean =>
  (payments ?? []).some(
    (p) => Number(p.payment_type) === PAYMENT_TYPES.SPRINGBIG_POINTS,
  )

// Source payment_type → FE method id. Refund flow uses this to gate
// which method buttons are selectable based on what the customer
// originally paid with. Card brands all collapse onto Credit/Debit
// (Visa/MC/Amex/OtherCC); the integrated terminal accepts any brand
// so the refund destination doesn't have to match the original brand
// — just the rail. Non-tender types (rounding diff, change due,
// points, e-transfer, AR, cheque, springbig) are intentionally
// absent: they aren't supported as refund methods in this UI.
const PAYMENT_TYPE_TO_METHOD_ID: Record<number, PaymentType> = {
  [PAYMENT_TYPES.CASH]: 'cash',
  [PAYMENT_TYPES.DEBIT]: 'debit',
  [PAYMENT_TYPES.VISA]: 'credit_visa',
  [PAYMENT_TYPES.MASTER_CARD]: 'credit_visa',
  [PAYMENT_TYPES.AMEX]: 'credit_visa',
  [PAYMENT_TYPES.OTHER_CC]: 'credit_visa',
  [PAYMENT_TYPES.GIFT_CARD]: 'gift_card',
}

// 'HH:MM' (24h, store-local) → '11:04 PM'. null on anything else.
const to12h = (hhmm: unknown): string | null => {
  if (typeof hhmm !== 'string') return null
  const m = hhmm.match(/^(\d{1,2}):(\d{2})$/)
  if (!m || Number(m[1]) > 23) return null
  const h = Number(m[1])
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`
}

// outside_sale_hours copy, rebuilt from the 409's envelope.extra
// ({start, end, store_time} as 'HH:MM') so the store time can sit on its
// own line. Falls back to the BE message verbatim if extra is missing or
// malformed.
const saleHoursCopy = (
  error: unknown,
  fallback: string,
): { message: string; storeTime: string | null } => {
  const extra = error instanceof V2ApiError ? error.extra : undefined
  const start = to12h(extra?.start)
  const end = to12h(extra?.end)
  if (!start || !end) return { message: fallback, storeTime: null }
  return {
    message: `Sales are not allowed outside ${start} - ${end}.`,
    storeTime: to12h(extra?.store_time),
  }
}

export function CheckoutModal() {
  const {
    cart,
    currentReceiptId,
    isRefundMode,
    clearReceipt,
    exitRefundMode,
    setReceiptId,
    setServerTotals,
    serverTotals,
    setUIFilters,
  } = usePOSStore()
  // Source payments from /copy-with-refund. Drives refund-method gating:
  // only methods used in the original payment can be selected, and each
  // is capped at the matching source amount minus any prior refund.
  const refundSourcePayments = usePOSStore((s) => s.refundSourcePayments)
  // The dst (refund) receipt from /copy-with-refund -- carries the
  // accumulated `payments` array after each partial refund (kept fresh
  // by `updateRefundReceipt`). Used to seed `lastReceipt` synchronously
  // on mount so the per-method cap is correct on the very first render
  // after a close+reopen; a previous version did this via an async GET
  // and let the cashier see already-refunded methods enabled for a
  // tick.
  const refundSourceReceipt = usePOSStore((s) => s.refundSourceReceipt)
  const updateRefundReceipt = usePOSStore((s) => s.updateRefundReceipt)
  // Staged SpringBig redemption — applied as a POINTS payment before the
  // cashier's tender. Selecting a reward in the LoyaltyPanel sets these;
  // we clear them after a successful pay (or when the customer changes).
  const pendingReward = usePOSStore((s) => s.pendingReward)
  const pendingOffer = usePOSStore((s) => s.pendingOffer)
  const clearPendingLoyalty = usePOSStore((s) => s.clearPendingLoyalty)
  const selectedCustomer = usePOSStore((s) => s.selectedCustomer)
  // v2 totals come from /cart/preview via cartStore in the sale flow. Refund
  // and unhold flows skip preview entirely and seed posStore.serverTotals
  // instead (refund: copy-with-refund response; unhold: receipt totals), so
  // we read both and prefer serverTotals when set — same Tier-0 precedence
  // CartSummary uses.
  const previewTotals = useCartStore((s) => s.preview?.totals ?? null)
  // "approved by <name>" under the Discount row: the last priced response's
  // manager approvals (for a bound receipt it also lists those already
  // recorded on it), else the committed receipt's. Cashier-side only; never
  // on the CFD.
  const previewOverrides = useCartStore((s) => s.preview?.discount_overrides ?? null)
  const committedOverrides = useCartStore((s) => s.committedOverrides)
  const discountOverrides = previewOverrides?.length
    ? previewOverrides
    : currentReceiptId != null && committedOverrides?.receiptId === currentReceiptId
      ? committedOverrides.rows
      : previewOverrides
  // Discount-limit gate hit on /cart/commit (handleDiscountGateError): the
  // same state the cart banner reads, shown on the method screen with the
  // same Approve flow instead of "Payment Failed".
  const gateError = useCartStore((s) => (
    s.previewStatus === 'error' && isDiscountGateCode(s.previewError?.code) ? s.previewError : null
  ))
  const { limit: clerkLimit } = useDiscountLimit()
  // The values the gate banner's Approve will approve, fixed when it opens
  // (listed in the dialog). null = closed.
  const [gateApprovalItemsOpen, setGateApprovalItemsOpen] = useState<ApprovalItem[] | null>(null)
  const gateBannerText = discountGateBannerText(gateError, clerkLimit)
  const gateApprovalSubtitle = (() => {
    const { limit, requested } = limitFromErrorExtra(gateError?.extra)
    const lim = limit ?? (clerkLimit < 100 ? clerkLimit : null)
    return lim != null
      ? approvalSubtitle(lim, requested)
      : 'A manager with approval rights enters their PIN.'
  })()

  /** /cart/commit refused by the discount-limit gate: 409
   *  discount_exceeds_limit (a stale limit, or an approval that no longer
   *  covers the value), a refused PIN, or 429 pin_throttled. Park the cart
   *  in the gate's error state (the banner + Approve, here and in the cart)
   *  and go back to the method screen. Nothing was charged: the commit
   *  failed before any tender. Returns true when handled. */
  const handleDiscountGateError = (err: unknown): boolean => {
    const code = v2ErrorCode(err)
    if (!isDiscountGateCode(code)) return false
    const cs = useCartStore.getState()
    const info = classifyPreviewError(err)
    // A refused / throttled PIN must stop riding the requests; a 409 over
    // the limit drops the refused tuple's approval.
    cs.handleGateError(code, info.extra)
    cs.setPreviewError(info)
    setStep('method')
    return true
  }
  const receiptApprovedBy = approvedByText(approvedByNames(discountOverrides, 'receipt'))
  // No cart discount on: every approval shows under the item line (a
  // recalled receipt's cart discount is replayed as line discounts).
  const lineApprovedBy = approvedByText(approvedByNames(discountOverrides, 'any'))
  // A percent SpringBig redemption (or a manual discount) rides
  // cartStore.discount; its dollar value comes back on the preview totals
  // (see cartDiscountDisplayAmount). Read here so the modal mirrors
  // CartSummary (the discount line) and the loyalty banner can reflect an
  // applied percent offer.
  const cartDiscount = useCartStore((s) => s.discount)
  const setCartDiscount = useCartStore((s) => s.setDiscount)
  // Live preview status — gates tendering on confirmed prices (see
  // pricesUnconfirmed below).
  const previewStatus = useCartStore((s) => s.previewStatus)
  // Loaded up here (not at first use below) so the staged-loyalty live
  // derivation can branch on `pos.apply_rewards_discounts_on_final_total`
  // to match the BE math. The same query result is reused for the
  // post-tender action gate later in the component.
  const { data: posConfig } = usePOSConfig()
  // Total dollar value of the staged redemption — drives the "Loyalty"
  // credit line + "To collect" row in the totals box, the cart-header
  // chip, and the partial-payment math. For percent redemptions we
  // recompute live against the cart subtotal (or grand_total when the
  // store has `pos.apply_rewards_discounts_on_final_total` enabled) so
  // the display tracks the cart and matches the BE's authoritative math
  // at frontend.py:600. The staged `amount` field is 0 for percent
  // redemptions; the percent itself rides on `rawAmount`. For flat-$
  // redemptions `amount` holds the dollar value directly. The BE
  // recomputes at pay-time regardless, so this is an FE estimate only.
  const stagedLoyaltyDiscountType =
    pendingReward?.discountType ?? pendingOffer?.discountType
  // Prefer the live cart preview, fall back to posStore.serverTotals.
  // Preview is NOT persisted across reloads (cartStore.ts:9) and isn't
  // run in refund/unhold flows, but `pendingReward`/`pendingOffer` ARE
  // persisted -- so a refresh mid-checkout (or an unhold of a held
  // sale with staged loyalty) would otherwise leave this estimate at 0
  // and the cashier would charge the customer's card for the full
  // pre-loyalty amount while the BE clamps the recorded payment to
  // amount_to_pay. Falling back to serverTotals (the BE-authoritative
  // totals seeded from receipt state) closes that window.
  const stagedLoyaltyPercentBase = posConfig?.posApplyRewardsDiscountsOnFinalTotal
    ? Number(previewTotals?.total ?? serverTotals?.total ?? 0)
    : Number(previewTotals?.subtotal ?? serverTotals?.subtotal ?? 0)
  const stagedLoyaltyAmount =
    stagedLoyaltyDiscountType === SPRINGBIG_PERCENT_TYPE
      ? computePercentDollar(
          pendingReward?.rawAmount ?? pendingOffer?.rawAmount ?? 0,
          stagedLoyaltyPercentBase,
        )
      : (pendingReward?.amount ?? pendingOffer?.amount ?? 0)
  // The register's integrated payment driver -- used in handleVoidPayment
  // to predict whether a card cancel will run an automatic Moneris void
  // ("moneris_go") or require a manual refund on the standalone terminal.
  const selectedRegister = useSessionStore((s) => s.selectedRegister)
  const queryClient = useQueryClient()
  const { label: taxName } = useStoreTaxLabel()

  const { closeModal, showToast, showConfirm } = useUIStore()

  const [step, setStep] = useState<CheckoutStep>('method')
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null)
  const [cashTendered, setCashTendered] = useState('')
  const [changeDue, setChangeDue] = useState(0)
  const [errorMessage, setErrorMessage] = useState('')
  // Set when the BE refused the sale with `outside_sale_hours` (the
  // store's pos.sale_hours window is closed). Swaps the error step's
  // "Payment Failed" + "Try Again" for a non-retry "Back" -- retrying
  // re-fails until the window opens. Cart stays intact so the cashier
  // can Hold it. Cleared whenever the step leaves 'error'.
  const [errorIsSaleHours, setErrorIsSaleHours] = useState(false)
  // "Current Store Time" second line for the sale-hours error step.
  const [saleHoursStoreTime, setSaleHoursStoreTime] = useState<string | null>(null)

  // Partial-payment state. paymentAmountInput is the cashier-editable
  // "Amount to Pay" field on the method screen — defaults to the
  // outstanding balance, can be reduced for partials, capped at
  // outstanding + $100 (typo guard against e.g. $25.32 → $253.20).
  // paymentAmountError surfaces validation feedback inline.
  // lastReceipt holds the most recent receipt response so we can render
  // a "payments so far" list across partials.
  const [paymentAmountInput, setPaymentAmountInput] = useState('')
  const [paymentAmountError, setPaymentAmountError] = useState<string | null>(null)
  type LastReceipt = {
    id?: number
    number?: string
    amount_to_pay?: number | string
    payments?: Array<{ id?: number; payment_type?: number; payment_method?: string | null; amount?: string | number; tip_amount?: number | string | null; timestamp?: string; last4?: string; wallet?: string }>
  }
  // Lazy init: in refund mode, seed from `refundSourceReceipt` so the
  // per-method cap math (`refundedSoFarByMethod` reads
  // `lastReceipt.payments`) renders correctly on the first paint after
  // a close+reopen of the checkout modal. Sale flow keeps starting at
  // null -- `lastReceipt` for sales gets populated after the first
  // tender lands and isn't subject to the same mid-flow modal-close
  // hazard.
  const [lastReceipt, setLastReceipt] = useState<LastReceipt | null>(
    () => (isRefundMode && refundSourceReceipt
      ? (refundSourceReceipt as LastReceipt)
      : null),
  )
  const setPartialPayments = useCartStore((s) => s.setPartialPayments)

  // The hydration effect that depends on `cartTotal` is declared
  // further down, after `cartTotal` is computed. See the
  // "Hydrate `lastReceipt`" block.

  // Mirror the receipt's payments + remaining balance into the cart store
  // so the CFD broadcast pipeline picks them up. The store is the single
  // source of truth for what the customer-facing display renders; pushing
  // through it (rather than firing a separate broadcast) reuses the
  // existing transport + lets `cartStore.clear()` wipe payments
  // automatically on transaction end.
  //
  // Skip when the receipt is fully paid (`amount_to_pay <= 0`): the
  // success branch of `processPaymentFlow` runs `cartStore.clear()`
  // synchronously before this effect commits, and re-pushing the final
  // receipt's payments here would repopulate the just-wiped partials —
  // the next sale would then start with the previous customer's
  // payment rows still on the CFD. Skip when no receipt yet too, so
  // reopening the modal mid-partial doesn't clobber the cart's existing
  // partial state.
  useEffect(() => {
    if (!lastReceipt) return
    const amountToPayNum = Number(lastReceipt.amount_to_pay ?? 0)
    if (amountToPayNum <= 0) return
    const payments = (lastReceipt.payments ?? []).map((p) => ({
      payment_method: p.payment_method ?? null,
      amount: String(p.amount ?? '0'),
      // Carry the terminal-tip portion so the CFD can label the
      // payment "(incl. $X tip)" -- `amount` is tip-inclusive.
      tip_amount: String(p.tip_amount ?? '0'),
    }))
    setPartialPayments(
      payments.length > 0 ? payments : null,
      String(lastReceipt.amount_to_pay ?? ''),
    )
  }, [lastReceipt, setPartialPayments])

  // Gift card state
  const [giftCardNumber, setGiftCardNumber] = useState('')
  const [giftCardBalance, setGiftCardBalance] = useState<number | null>(null)
  // Set after ANY failed gift-card payment: the card may have been
  // touched (BE resume/dedup make retries SAFE, but the cashier must
  // verify what actually happened before firing again). Cleared by a
  // successful balance Check or re-entering the gift-card step.
  const [gcNeedsRecheck, setGcNeedsRecheck] = useState(false)
  // GiftCard row PK from check_balance. The legacy pay() (still backing
  // /cart/commit + /receipts/{id}/payments for type 251) looks up the
  // card by id, not number — without this the BE raises
  // "No gift card with id #None found." See V2-GIFT-CARD-BIRCHMOUNT.md.
  const [giftCardId, setGiftCardId] = useState<number | null>(null)
  const [giftCardIsBirchmount, setGiftCardIsBirchmount] = useState(false)
  // Cashier-entered PIN for Birchmount cards. Required server-side at
  // /cart/commit time -- v2 sends this through and the BE validates it
  // against Birchmount's stored PIN before redeeming. Required only
  // when the card is Birchmount; Barnet (local-only) cards skip it.
  const [giftCardPin, setGiftCardPin] = useState('')
  // Inline lookup error (not-found, service-down, etc). Renders below
  // the input so the cashier sees it without missing a transient toast.
  const [giftCardLookupError, setGiftCardLookupError] = useState<string | null>(null)


  const estimated = cart.reduce((acc, item) => {
    const qty = typeof item.quantity === 'number' ? item.quantity : parseFloat(String(item.quantity)) || 0
    const sign = qty < 0 ? -1 : 1
    const absQty = Math.abs(qty)
    const lineGross = typeof item.line_total === 'number' ? item.line_total : parseFloat(String(item.line_total)) || 0

    const lineSubtotal = lineGross
    const lineTax = 0
    const depositUnit = Number(item.product.deposit_price ?? item.product.recycling_fee ?? 0) || 0
    const lineDeposit = depositUnit * absQty * sign

    acc.subtotal += lineSubtotal
    acc.tax += lineTax
    acc.deposit += lineDeposit
    return acc
  }, { subtotal: 0, tax: 0, deposit: 0 })

  const cartTotal = serverTotals !== null
    ? serverTotals.total
    : previewTotals !== null
      ? parseFloat(previewTotals.total) || 0
      : estimated.subtotal + estimated.tax + estimated.deposit
  const taxAmount = serverTotals !== null
    ? serverTotals.taxes
    : previewTotals !== null
      ? parseFloat(previewTotals.taxes) || 0
      : 0
  const subtotal = serverTotals !== null
    ? serverTotals.subtotal
    : previewTotals !== null
      ? parseFloat(previewTotals.subtotal) || 0
      : estimated.subtotal
  // Cart-level discount surfacing (mirrors CartSummary). Amount is the
  // redemption's own value for a SpringBig percent offer (it stacks on
  // sale/aged discounts), else the BE's discount_amount;
  // loyaltyDiscountApplied is true only for a SpringBig percent
  // offer/reward (carries offer_id/reward_id), so the banner shows
  // "applied" instead of "available".
  const cartDiscountAmount = cartDiscountDisplayAmount(previewTotals, cartDiscount)
  const loyaltyDiscountApplied =
    !isRefundMode
    && !!(cartDiscount && (cartDiscount.offer_id || cartDiscount.reward_id))
  const loyaltyDiscountLabel = cartDiscount?.reason_other || 'SpringBig offer'
  // Prices are confirmed only when the BE is authoritative (serverTotals — e.g.
  // a partial-payment continuation) or the live preview has settled
  // (previewStatus 'idle'). While a preview is in-flight ('fetching'), the cart
  // changed but hasn't re-priced ('stale'), or it failed/offline ('error'), the
  // on-screen totals are unconfirmed — block tendering until they settle so the
  // cashier can't charge a stale/extrapolated amount. (The BE would
  // price-drift-reject it, but that's a dead-end after they've tapped through.)
  // Refund mode runs its own BE-driven totals, so it's exempt.
  const pricesUnconfirmed =
    !isRefundMode && serverTotals === null && previewStatus !== 'idle'
  // The preview says the applied SpringBig offer takes nothing off (e.g. the
  // store combines receipt discounts by MAX, or it replaced a bigger line
  // discount). It is still spent at commit, so warn the cashier instead of
  // showing a value-less "Loyalty applied". Reads the last preview, like the
  // banner's dollar figure, so it doesn't flicker while a refresh is in
  // flight; a just-applied offer has no value yet and never false-warns.
  const loyaltySavesNothing =
    loyaltyDiscountApplied && redemptionSavesNothing(previewTotals, cartDiscount)
  // Cart discounts are locked once a payment is on the receipt
  // (cartStore.setDiscount no-ops), so Clear/Change can't help there.
  const loyaltyLocked = isReceiptPartiallyPaid(serverTotals, isRefundMode)

  // Hydrate `lastReceipt` on mount when a partial payment landed in a
  // previous modal session and the cashier reopened the modal. The
  // remaining balance survives via `posStore.serverTotals.amountToPay`
  // (used by the Paid/Remaining panel), but the per-payment list lives
  // only on the receipt itself — without this fetch the cashier sees
  // the partial subtracted but no row to void it from. Sale mode only;
  // refund mode already seeds from `refundSourceReceipt` synchronously.
  //
  // Skips when `lastReceipt` is already set (a tender just landed
  // during this modal session). Deps use stable primitives so the
  // effect doesn't refetch on every render — `serverTotals` is an
  // object that gets a new identity on each `setServerTotals` call,
  // so we extract `amountToPay` as a number.
  const amountToPayPrimitive = serverTotals?.amountToPay ?? null
  const hasPartialPaid =
    amountToPayPrimitive !== null && amountToPayPrimitive < cartTotal - 0.005
  useEffect(() => {
    if (isRefundMode) return
    if (!currentReceiptId) return
    if (lastReceipt) return
    if (!hasPartialPaid) return

    let cancelled = false
    v2api
      .get<{ receipt?: LastReceipt } & Record<string, unknown>>(
        v2(`/receipts/${currentReceiptId}`),
      )
      .then((live) => {
        if (cancelled) return
        const r = ((live?.receipt ?? live) as LastReceipt) ?? null
        if (r && Array.isArray(r.payments) && r.payments.length > 0) {
          setLastReceipt(r)
        }
      })
      .catch(() => {
        // Swallow — the modal still works without the void affordance.
      })
    return () => {
      cancelled = true
    }
  }, [isRefundMode, currentReceiptId, lastReceipt, hasPartialPaid])

  // Auto-advance to 'complete' when the modal mounts on a fully-paid
  // receipt. Path that triggers this: cashier reaches the 'complete'
  // step after a successful tender, walks away, inactivity logout
  // unmounts /pos (CheckoutModal step state lost), PIN re-auth remounts
  // with step defaulting to 'method'. Without this guard the cashier
  // sees payment buttons against a $0 outstanding balance and gets
  // stuck — Void hits the BE which refuses ("Cannot void a receipt
  // with payments"). Refund mode is gated out: refund receipts at
  // amount_to_pay=0 are also "fully refunded" and need the same
  // routing, so we keep the check broad and let the 'complete' step
  // render its refund-aware copy.
  useEffect(() => {
    if (step !== 'method') return
    if (amountToPayPrimitive === null) return
    if (Math.abs(amountToPayPrimitive) > 0.005) return
    if (!lastReceipt?.payments?.length) return
    // No captureCheckoutCompleted here on purpose: this is a re-entry into
    // an ALREADY-settled receipt, not a settlement. The tail that actually
    // took the money fired the event; firing again on every remount would
    // double-count the sale and put two events on the same receipt_id.
    setStep('complete')
  }, [step, amountToPayPrimitive, lastReceipt])

  // The outstanding balance the cashier still has to collect (sale) or
  // refund (refund mode). Prefer `serverTotals.amountToPay` when it's
  // set: that's the BE's authoritative remaining-balance figure --
  // updated after each partial-tender response, persisted via posStore
  // so it survives a CheckoutModal close+reopen. Falling back to
  // `cartTotal` would re-show the full ticket total on reopen even
  // when the receipt is half-paid (the bug the user hit).
  const outstandingFromServer =
    serverTotals !== null
    && serverTotals.amountToPay !== undefined
    && serverTotals.amountToPay !== null
      ? serverTotals.amountToPay
      : null
  const [amountDue, setAmountDue] = useState(
    outstandingFromServer !== null ? outstandingFromServer : cartTotal,
  )

  // Snap `amountDue` to the live outstanding figure whenever the cart
  // mutates (cartTotal changes) or the BE reports a new balance
  // (serverTotals.amountToPay changes after a partial tender lands).
  // Same precedence as the initial state above.
  useEffect(() => {
    setAmountDue(outstandingFromServer !== null ? outstandingFromServer : cartTotal)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartTotal, outstandingFromServer])

  // Keep the Amount-to-Pay input in sync with `amountDue` minus any
  // staged SpringBig redemption. The redemption posts as a separate
  // POINTS payment before the cashier's tender, so the cash/card the
  // cashier collects is the net figure — pre-filling the input to the
  // gross would have them collect the redemption amount twice (once in
  // points, once in cash). The cashier can still override for a
  // partial; the next state change snaps it back.
  useEffect(() => {
    const netDue = Math.max(amountDue - stagedLoyaltyAmount, 0)
    if (netDue > 0) {
      setPaymentAmountInput(netDue.toFixed(2))
    } else if (amountDue > 0) {
      // Loyalty fully covers the receipt — leave the input blank, the
      // tender step short-circuits in processPaymentFlow when the
      // post-loyalty amount is <= 0.
      setPaymentAmountInput('')
    } else {
      setPaymentAmountInput('')
    }
    setPaymentAmountError(null)
  }, [amountDue, stagedLoyaltyAmount])

  // Terminal errors: the receipt is in a state the backend won't let us
  // recover from (already paid, voided, locked). "Try Again" would just
  // re-fail with the same 409/500, and clicking Void would 500 because
  // backend void requires status=NEW. So when one of these surfaces we
  // hold the error on screen briefly so the cashier sees what happened,
  // then drop a local-only clearReceipt() + closeModal() so they can
  // start a fresh transaction. clearReceipt is purely local — it doesn't
  // touch the backend, so it's safe to run against a paid/locked
  // receipt.
  const isTerminalError = (msg: string): boolean => {
    if (!msg) return false
    const m = msg.toLowerCase()
    return (
      m.includes('no outstanding balance')
      || m.includes('is locked')
      || m.includes('cannot be modified')
      || m.includes('already paid')
      || m.includes('cannot_cancel_paid')
      || m.includes('already_terminal')
    )
  }
  const errorIsTerminal = step === 'error' && isTerminalError(errorMessage)

  useEffect(() => {
    if (!errorIsTerminal) return
    // Clear the stale receipt pointer immediately. Any concurrent action
    // before the modal closes (re-tap Pay, Void, recall) must target a
    // fresh cart, not the dead receipt the error references — otherwise
    // the next pay attempt fires against the same locked id and the
    // cashier hits "Receipt is locked (status=-1)" again.
    clearReceipt()
    // Hold the message on screen briefly so the cashier reads it before
    // we close the modal.
    const timer = window.setTimeout(() => {
      closeModal()
    }, 2500)
    return () => window.clearTimeout(timer)
  }, [errorIsTerminal, clearReceipt, closeModal])

  // Auto-clear register_busy recovery state on any transition AWAY
  // from the error step. handleRetry + handleCancelBusyAndRetry
  // both clear inline, but the header back arrow does a bare
  // setStep('method') -- and any future programmatic step change
  // could miss the cleanup too. Without this guard, the busyTerminal*
  // values from an old register_busy can survive into a later,
  // unrelated payment error in the same checkout session and falsely
  // render the "Cancel Previous & Retry" button against stale IDs
  // (cashier clicks, BE 404s on the dead session uuid, confusion).
  //
  // The effect fires AFTER render, so the busy-state-set +
  // setStep('error') batched updates inside handleStartError both
  // settle in the same render cycle: when the effect runs, step is
  // already 'error' and we don't clear. Subsequent navigation away
  // (back arrow, retry, etc.) re-runs the effect with step !==
  // 'error' and the clear fires.
  useEffect(() => {
    if (step !== 'error') {
      setBusyTerminalSessionId(null)
      setBusyTerminalReceiptId(null)
      setErrorIsSaleHours(false)
      setSaleHoursStoreTime(null)
    }
  }, [step])

  // Truncate raw money input to at most two decimal places. <input
  // type="number"> happily accepts "12.00000" (e.g. when the up/down
  // spinner increments a 5-decimal seed value) and we don't want
  // those extra zeros surfacing in the UI or rounding off later. The
  // regex keeps optional sign + integer + up to 2 decimals; anything
  // past the second decimal is dropped at the keystroke level so the
  // user can never type a third digit. Empty / lone-minus passes
  // through so the input doesn't fight typing.
  const clampToCents = (raw: string): string => {
    if (raw === '' || raw === '-') return raw
    const m = raw.match(/^-?\d*(\.\d{0,2})?/)
    return m ? m[0] : ''
  }

  // Resolve the validated wire amount from the input. Validation is
  // pure so we can re-run it at submit time without depending on
  // setState having committed the latest input value.
  const validatePaymentAmount = (rawInput: string): { value: number; error: string | null } => {
    const trimmed = rawInput.trim()
    if (!trimmed) return { value: 0, error: 'Enter an amount.' }
    const value = parseFloat(trimmed)
    if (!Number.isFinite(value)) return { value: 0, error: 'Enter a valid amount.' }
    if (value < 0.01) return { value, error: 'Amount must be at least $0.01.' }
    // Typo guard: cap at outstanding + $100. Catches the
    // "$25.32 → $253.20" class of mis-keys while still allowing a
    // legitimate cash overpay scenario up to a sane ceiling.
    const cap = amountDue + 100
    if (value > cap) {
      return { value, error: `Amount cannot exceed $${formatPrice(cap)} (outstanding + $100).` }
    }
    return { value, error: null }
  }

  const processPayment = useProcessPayment()
  const commitCart = useCommitCart()
  const printReceipt = usePrintReceipt()
  // Drawer kick fired explicitly on cash transactions — independent of
  // the auto-print path. In 'ask' / 'nothing' the receipt never prints,
  // so we can't rely on the print buffer's drawer pulse to open it.
  const openCashDrawer = useOpenCashDrawer()
  // pos.receipt.complete_action — 'ask' (default), 'print', 'nothing'.
  // Decides whether the post-tender screen auto-prints and what buttons
  // it shows. Default to 'ask' until config loads (matches BE default).
  // `posConfig` itself is fetched earlier (see staged-loyalty derivation).
  const completeAction = posConfig?.posReceiptCompleteAction ?? 'ask'
  const checkGiftCardBalance = useCheckGiftCardBalance()
  const voidPayment = useV2VoidPayment()
  const settleReward = useSettleRewardShare()
  const updateReceiptItem = useUpdateReceiptItem()
  const deleteReceiptItem = useDeleteReceiptItem()
  const applyLoyaltyPoints = useV2ApplyLoyaltyPoints()

  // SpringBig snapshot for the active customer — drives the in-checkout
  // loyalty banner (rewards-available CTA + staged-redemption pill). Same
  // 30s-cached query the customer drawer fires, so opening checkout right
  // after closing the drawer is a cache hit. Refund mode is gated out:
  // loyalty doesn't apply when money is going back to the customer.
  const { data: checkoutSpringbig } = useCustomerSpringbig(
    !isRefundMode && selectedCustomer?.id ? selectedCustomer.id : null,
  )
  const checkoutHasRedemptions =
    !!checkoutSpringbig?.customer_enrolled
    && (
      (checkoutSpringbig.rewards?.length ?? 0) > 0
      || (checkoutSpringbig.offers?.length ?? 0) > 0
    )
  // A redemption already landed on this receipt as a POINTS payment (prior
  // partial). The offer/reward still lists as available until the BE
  // consumes it at close, so suppress the re-apply CTA and show a "redeemed"
  // note instead — the apply paths also hard-block a second redemption.
  const receiptHasLoyaltyPayment =
    !isRefundMode && hasLoyaltyPayment(lastReceipt?.payments)

  // ---- Integrated card-terminal session ----
  // Drives the new V2 terminal-payments flow (see api/terminalPayments.ts).
  // The cashier picks "Credit/Debit", we commit the cart (no payment) to
  // get a stable receipt id, then start a session against the physical
  // terminal. The polling query resolves to one of the terminal states
  // (approved / declined / cancelled / timeout / error) — see
  // `handleTerminalSessionFinal` for dispatch.
  //
  // `handledSessionIdRef` blocks double-dispatch when the polling query
  // yields the same final-state payload twice (e.g. cache replay on a
  // re-render).
  const startTerminalPayment = useStartTerminalPayment()
  const cancelTerminalPayment = useCancelTerminalPayment()
  const [terminalSessionId, setTerminalSessionId] = useState<string | null>(null)
  const [terminalReceiptId, setTerminalReceiptId] = useState<number | null>(null)
  const handledSessionIdRef = useRef<string | null>(null)
  // Last start request (receiptId + body + method) — held so that
  // recoverable 409s (price_drift) and network blips can retry against
  // the BE with the SAME idempotency_key. (overpayment_unconfirmed is
  // hard-blocked upfront for cards so no retry path uses it.) Cleared
  // once the session reaches a terminal state or the modal closes.
  const lastStartRef = useRef<{
    receiptId: number
    body: StartTerminalPaymentBody
    method: PaymentMethod
  } | null>(null)
  // Synchronous re-entry latch for terminal-payment START. The
  // existing state guards (isProcessing, startTerminalPayment.isPending)
  // settle a render AFTER mutateAsync starts, leaving a window where
  // a double-click on Pay (or Enter + click) can spawn two
  // PaymentSession rows on the same register. The first succeeds,
  // the second 409s with register_busy -- and if the BE's idempotency
  // key check doesn't catch them (different keys because we mint
  // per-attempt), we'd have an orphan pending row blocking the
  // register for 6 minutes. Released in finally so a transient
  // failure doesn't permanently lock the start path.
  const startInFlightRef = useRef<boolean>(false)
  // Captured from the register_busy error envelope's
  // extra.busy_session_id (set BE-side by ops_terminal_payment when
  // it refuses a new session start because the register already has
  // a PENDING row). Drives the "Cancel previous & retry" button on
  // the error step -- one click cancels the orphan and replays the
  // start, instead of waiting up to 6 minutes for the reaper.
  // Cleared on a normal Try Again retry, on cancel completion, and
  // when the cashier closes the error step.
  const [busyTerminalSessionId, setBusyTerminalSessionId] = useState<string | null>(null)
  const [busyTerminalReceiptId, setBusyTerminalReceiptId] = useState<number | null>(null)
  // True while handleCancelBusyAndRetry is running its full
  // cancel-then-poll-then-decide sequence. Drives the
  // "Cancelling previous…" / "Verifying…" disabled button state.
  // The state-driven UI plus a synchronous ref latch (below) keeps
  // a double-click from spawning two parallel cancel+poll
  // sequences -- cancelTerminalPayment.isPending alone would let
  // the button re-enable the moment mutateAsync resolves, before
  // the poll loop completes.
  const [isVerifyingCancel, setIsVerifyingCancel] = useState<boolean>(false)
  const verifyingCancelRef = useRef<boolean>(false)
  const terminalSessionQuery = useTerminalPaymentSession(
    terminalReceiptId,
    terminalSessionId,
  )
  const terminalSession: TerminalPaymentSession | undefined = terminalSessionQuery.data

  // Counts consecutive silent retries on the price_drift recovery path
  // (cart/commit rejected, FE refreshed preview, totals matched the old
  // ones so we retry without prompting). Reset on any successful
  // commit response (partial OR full) so a future commit in the same
  // session starts fresh. After PRICE_DRIFT_MAX_SILENT_RETRIES, surface
  // a friendly error rather than recurse forever -- protects against a
  // BE that keeps rejecting for the same reason on every attempt.
  // Modal-close reset is implicit: CheckoutModal is conditionally
  // rendered on `activeModal === 'checkout'`, so closing unmounts the
  // component and the ref is garbage-collected -- next mount inits
  // fresh via useRef(0).
  const priceDriftRetryRef = useRef(0)

  const cashTenderedNum = parseFloat(cashTendered) || 0
  const absoluteAmountDue = Math.abs(amountDue)

  // ---- Refund method caps ----
  // Sum of source-payment amounts grouped by FE method id. These are
  // the upper bounds on how much can be refunded to each method —
  // a customer who paid $20 visa + $10 cash can only get $20 back via
  // visa and $10 back via cash. Source payment types that don't map to
  // a refundable method (rounding diff, change, points, e-transfer,
  // AR, cheque, springbig) are intentionally ignored: the refund flow
  // doesn't support them as destinations.
  const refundCapsBySource = useMemo<Partial<Record<PaymentType, number>>>(() => {
    if (!isRefundMode || !refundSourcePayments) return {}
    const caps: Partial<Record<PaymentType, number>> = {}
    for (const sp of refundSourcePayments) {
      // Synthetic online (Moneris Checkout) entry: its payment_type is
      // the card BRAND int, but it must never feed the in-store card
      // rails — an integrated/standalone terminal can't credit the
      // customer's online card. It drives the dedicated Online Card
      // method (onlineCardRemaining) and, per store policy (card or
      // cash), also opens the CASH rail for the captured amount — the
      // BE mirrors that in its method-cap math.
      if (sp.online) {
        const onlineAmt = Math.max(
          0,
          Math.abs(parseFloat(String(sp.remaining ?? sp.amount ?? '0'))) || 0,
        )
        if (onlineAmt > 0) caps['cash'] = (caps['cash'] ?? 0) + onlineAmt
        continue
      }
      const methodId = PAYMENT_TYPE_TO_METHOD_ID[Number(sp.payment_type)]
      if (!methodId) continue
      // Caps are the MERCHANDISE share only: tips are not refundable
      // through the return flow, so a tipped card payment ($100 sale +
      // $10 tip = $110 amount) caps the card rail at $100. The overall
      // refund is already bounded by the return receipt's
      // amount_to_pay; this keeps the per-rail split from routing the
      // tip's worth of refund onto the card.
      const gross = Math.abs(parseFloat(String(sp.amount ?? '0'))) || 0
      // Refund-integrity BE sends per-payment `remaining` (cross-receipt:
      // refunds recorded by EARLIER return receipts already subtracted).
      // Prefer it over the raw amount so a split refund's second receipt
      // shows honest caps. Session refunds still subtract below via
      // refundedSoFarByMethod. Falls back to gross for old BEs.
      const base = sp.remaining != null ? Math.abs(Number(sp.remaining)) : gross
      const tip = Math.abs(parseFloat(String(sp.tip_amount ?? '0'))) || 0
      const amt = Math.max(0, base - tip)
      if (amt <= 0) continue
      caps[methodId] = (caps[methodId] ?? 0) + amt
    }
    return caps
  }, [isRefundMode, refundSourcePayments])

  // Sum of refund payments already applied this session, grouped by
  // FE method id. Read from the refund receipt's own payments array
  // (canonical, ordered) so a partial split that's halfway done shows
  // the correct remaining cap on the next render.
  const refundedSoFarByMethod = useMemo<Partial<Record<PaymentType, number>>>(() => {
    if (!isRefundMode || !lastReceipt?.payments) return {}
    const refunded: Partial<Record<PaymentType, number>> = {}
    for (const p of lastReceipt.payments) {
      const methodId = PAYMENT_TYPE_TO_METHOD_ID[Number(p.payment_type)]
      if (!methodId) continue
      const amt = Math.abs(parseFloat(String(p.amount ?? '0'))) || 0
      refunded[methodId] = (refunded[methodId] ?? 0) + amt
    }
    return refunded
  }, [isRefundMode, lastReceipt?.payments])

  // Per-method remaining refundable: source cap minus already-refunded.
  const refundRemainingByMethod = useMemo<Partial<Record<PaymentType, number>>>(() => {
    const remaining: Partial<Record<PaymentType, number>> = {}
    for (const k of Object.keys(refundCapsBySource) as PaymentType[]) {
      const cap = refundCapsBySource[k] ?? 0
      const used = refundedSoFarByMethod[k] ?? 0
      remaining[k] = Math.max(0, cap - used)
    }
    return remaining
  }, [refundCapsBySource, refundedSoFarByMethod])

  // Card-rail shared bucket. Standalone (`debit` method id, manual
  // entry) and Credit/Debit (`credit_visa`, integrated terminal) hit
  // the same card. Standalone is the cashier's fallback when the
  // integrated terminal is down, so it has to be selectable whenever
  // the customer paid by ANY card method (Visa/MC/Amex/Debit).
  // Capping them against a unified bucket also stops a partial refund
  // through one rail from leaving stale headroom on the other (e.g.
  // refund $20 via Standalone Interac → debit refunded += $20 but
  // credit_visa cap would still read full source cap and let the
  // cashier double-refund).
  const cardRailRemaining = useMemo(() => {
    const cap = (refundCapsBySource['credit_visa'] ?? 0) + (refundCapsBySource['debit'] ?? 0)
    const used = (refundedSoFarByMethod['credit_visa'] ?? 0) + (refundedSoFarByMethod['debit'] ?? 0)
    return Math.max(0, cap - used)
  }, [refundCapsBySource, refundedSoFarByMethod])

  // The synthetic online (Moneris Checkout) source entry, when the
  // original order was paid online. Drives the Online Card refund
  // method: label (brand/last4), wire payment_type (brand int), and cap.
  const onlineSourcePayment = useMemo(
    () => (isRefundMode
      ? (refundSourcePayments ?? []).find((sp) => sp.online === true) ?? null
      : null),
    [isRefundMode, refundSourcePayments],
  )
  const onlineCardRemaining = useMemo(() => {
    if (!onlineSourcePayment) return 0
    const base = Math.abs(parseFloat(String(
      onlineSourcePayment.remaining ?? onlineSourcePayment.amount ?? '0',
    ))) || 0
    // Session refunds already booked on this return receipt: an online
    // credit's JT carries the card BRAND type, and on an online-paid
    // source the terminal card rails are cap-0, so every card-brand JT
    // here IS an online credit. (The BE enforces the exact remaining on
    // every attempt regardless — this only keeps the button/label
    // honest between refreshes.)
    const used =
      (refundedSoFarByMethod['credit_visa'] ?? 0)
      + (refundedSoFarByMethod['debit'] ?? 0)
      + (refundedSoFarByMethod['credit_mastercard'] ?? 0)
      + (refundedSoFarByMethod['credit_amex'] ?? 0)
    return Math.max(0, base - used)
  }, [onlineSourcePayment, refundedSoFarByMethod])

  // Total refund still wireable to a *real* tender rail (cash + gift
  // card + the unified card rail). When the original sale was partly
  // settled by a store-funded, non-refundable "payment" -- a SpringBig
  // reward (type 299) -- the outstanding refund exceeds this, and that
  // excess has no tender destination. The BE auto-credits the reward back
  // to the store (ops_payment._credit_nonrefundable_share), settling the
  // refund to zero. Surfacing the figure here lets the cash step say
  // "$X reward credited to store" instead of the misleading "Remaining
  // $X must use another method" -- there is no other method.
  //
  // Gate on source_payments: without them (legacy refund receipts) every
  // method is allowed (isMethodAllowed) and the per-method caps are
  // unknown, so we can't attribute any non-refundable share -- treat it
  // as 0 rather than mislabel a fully-refundable balance as a reward
  // credited to the store.
  const hasRefundCaps =
    isRefundMode && !!refundSourcePayments && refundSourcePayments.length > 0
  // NB: the online (Moneris Checkout) share is NOT added separately —
  // refundCapsBySource already folds it into the cash cap (card-or-cash
  // policy), so adding onlineCardRemaining here would double-count it.
  const totalRefundableRemaining = hasRefundCaps
    ? (refundRemainingByMethod['cash'] ?? 0)
      + (refundRemainingByMethod['gift_card'] ?? 0)
      + cardRailRemaining
    : Infinity
  const nonRefundableRemaining = hasRefundCaps
    ? Math.max(0, absoluteAmountDue - totalRefundableRemaining)
    : 0

  // Reward (299) dollars on the original sale still available to credit
  // back. Store-funded, so they map to no refund rail -- the reason
  // nonRefundableRemaining exists. Prefer the BE's per-payment
  // `remaining` over the gross `amount`, exactly as refundCapsBySource
  // does for the real rails: on a split return an earlier return may
  // already have reclaimed part (or all) of the reward, and gating on
  // the gross would offer a "Complete Refund" the BE is certain to
  // refuse. Falls back to the gross for a pre-`remaining` backend.
  const rewardSourceTotal = useMemo(() => {
    if (!isRefundMode || !refundSourcePayments) return 0
    return refundSourcePayments
      .filter((sp) => Number(sp.payment_type) === PAYMENT_TYPES.SPRINGBIG_POINTS)
      .reduce((sum, sp) => {
        const base = sp.remaining != null ? sp.remaining : sp.amount
        return sum + (Math.abs(parseFloat(String(base ?? '0'))) || 0)
      }, 0)
  }, [isRefundMode, refundSourcePayments])

  // Nothing left that a real tender can pay back, yet the return still
  // owes the customer. Two very different situations look identical from
  // here (every tile disabled at "up to $0.00"), so they get separated
  // below instead of leaving the cashier a wall of dead buttons:
  const railsExhausted =
    hasRefundCaps && absoluteAmountDue > 0.005 && totalRefundableRemaining <= 0.005
  //   1. the balance IS the reward -- settleable, see useSettleRewardShare.
  const canSettleReward = railsExhausted && rewardSourceTotal + 0.05 >= absoluteAmountDue
  //   2. it is not -- the original tenders are accounted for somewhere
  //      else, in practice a refund recorded on an earlier attempt.
  //      Nothing the cashier can fix at the till, so say so.
  const refundBlocked = railsExhausted && !canSettleReward

  // Returns true when this method is selectable in the current refund.
  // Sale mode allows everything; refund mode allows methods with >0
  // remaining cap. When `refundSourcePayments` is missing entirely
  // (legacy receipts pre-source_payments rollout) we leave all methods
  // enabled so the cashier isn't blocked.
  const isMethodAllowed = (method: PaymentMethod): boolean => {
    if (!isRefundMode) return true
    if (!refundSourcePayments || refundSourcePayments.length === 0) return true
    if (method.id === 'debit' || method.id === 'credit_visa') {
      return cardRailRemaining > 0.005
    }
    if (method.id === 'online_card') {
      return onlineCardRemaining > 0.005
    }
    return (refundRemainingByMethod[method.id] ?? 0) > 0.005
  }

  // Cap a refund's wire amount to what's left in the chosen method.
  // Used in cash / card-terminal / gift-card flows so a $30 refund
  // split across $20 visa + $10 cash sends $20 to visa first, then
  // the receipt's amount_to_pay drops to $10 and cash takes the rest.
  const refundCapForMethod = (method: PaymentMethod | null): number => {
    if (!method || !isRefundMode) return Infinity
    if (!refundSourcePayments || refundSourcePayments.length === 0) return Infinity
    if (method.id === 'debit' || method.id === 'credit_visa') return cardRailRemaining
    if (method.id === 'online_card') return onlineCardRemaining
    return refundRemainingByMethod[method.id] ?? 0
  }

  // Effective refund wire amount: full outstanding capped to the
  // selected method's remaining. Used when handing the amount off to
  // processPaymentFlow / startTerminalSession.
  const refundWireAmount = (method: PaymentMethod | null): number => {
    if (!method) return absoluteAmountDue
    const cap = refundCapForMethod(method)
    return Math.min(absoluteAmountDue, cap)
  }

  // Wire amount for the cash step: cashier's "Amount to Pay" entry if
  // valid, else fall back to the full outstanding. Drives both the
  // change calculation and the insufficient-cash check.
  const cashWireAmount = (() => {
    if (isRefundMode) {
      // In refund mode the cash button is enabled only when cash is in
      // the source payments; cap to the per-method remaining so a
      // $30 refund split as $20 visa + $10 cash refunds the right
      // share. Falls back to amountDue when no method is selected
      // yet (e.g. label rendering before the cashier picks Cash).
      return selectedMethod
        ? -refundWireAmount(selectedMethod)
        : amountDue
    }
    const { value, error } = validatePaymentAmount(paymentAmountInput)
    return error ? amountDue : value
  })()
  const calculatedChangeDue = Math.max(0, cashTenderedNum - cashWireAmount)

  // Typo guard on Cash Tendered: when the cashier tenders more than $100
  // over the wire amount, surface a confirm modal before committing the
  // payment. Catches the "$21 → $1000" / "$45.50 → $4550" class of
  // mis-keys that would otherwise pay out a giant change-due from the
  // till. Acknowledge-then-allow flow rather than a hard block, so a
  // legitimate large bill (e.g. $200 for a $30 tab) still goes through —
  // the cashier just confirms once.
  const CASH_OVERPAY_LIMIT = 100
  const cashOverpayCap = cashWireAmount + CASH_OVERPAY_LIMIT
  const isCashOverpaid = !isRefundMode && cashTenderedNum > cashOverpayCap + 0.005

  // Treat an in-flight terminal session as "processing" too — the
  // cashier shouldn't be able to close the modal or back out while the
  // physical terminal is mid-transaction.
  //
  // isVerifyingCancel covers the post-cancel polling + replay window
  // in handleCancelBusyAndRetry. cancelTerminalPayment.isPending
  // ONLY covers the mutateAsync call; it flips false the moment the
  // BE returns, BEFORE the FE polls GET to verify the orphan actually
  // transitioned and BEFORE the conditional retryStartWithBody fires.
  // Without isVerifyingCancel here, backdrop / X close / back nav
  // would re-enable mid-poll -- if the cashier closes during that
  // window, the async handler keeps running, eventually fires
  // retryStartWithBody on an unmounted component, and starts a
  // terminal session the cashier has no UI to see or cancel.
  const isTerminalActive =
    terminalSessionId !== null
    || startTerminalPayment.isPending
    || cancelTerminalPayment.isPending
    || isVerifyingCancel
  // Refund-cart item mutations (PUT qty / DELETE line) round-trip to
  // the BE; the totals shown here only re-seed *after* the response
  // lands. Treat the in-flight window as processing so the cashier
  // can't commit a refund against a stale total. Driven by
  // useRefundCartMutations which mirrors mutation.isPending into the
  // store. Outside refund mode the flag is always false.
  const isRefundMutating = usePOSStore((s) => s.isRefundMutating)
  // settleReward belongs in `isProcessing` (not merely isAnyPending): it
  // is a money-moving POST with no idempotency key, and isProcessing is
  // what hides the modal's close button. Without it the cashier could
  // close and reopen checkout mid-request and fire a second settlement.
  // The BE serializes on a SELECT FOR UPDATE of the receipt and refuses
  // the second attempt ("no outstanding balance to settle"), so this is
  // about not surfacing that confusing error -- not data integrity.
  const isProcessing = processPayment.isPending || commitCart.isPending
    || isTerminalActive || isRefundMutating || settleReward.isPending
  const isPrinting = printReceipt.isPending
  const isPartialPayment = amountDue < cartTotal
  const isAnyPending = isProcessing || isPrinting || checkGiftCardBalance.isPending || voidPayment.isPending

  const handleSelectMethod = (method: PaymentMethod) => {
    if (isAnyPending) return

    // Refund-method gating: customers can only be refunded via the
    // payment methods they originally paid with. Disallowed buttons
    // are visually disabled in the grid below; this guard is the
    // belt-and-suspenders check in case a stale state slips through.
    if (isRefundMode && !isMethodAllowed(method)) {
      showToast({
        type: 'warning',
        title: 'Refund Method Not Allowed',
        message: 'This payment method was not used on the original receipt.',
      })
      return
    }

    // In sale mode, the editable Amount input drives the wire amount
    // (partial payments). Validate before letting the cashier proceed
    // to a method-specific step. Refund mode skips this — the refund
    // amount is fixed by the receipt and not user-editable.
    if (!isRefundMode) {
      const { value, error } = validatePaymentAmount(paymentAmountInput)
      if (error) {
        setPaymentAmountError(error)
        return
      }
      // Hard block on card overpayment: card swipes cannot return cash
      // change, so a card payment above the receipt balance leaves the
      // customer overcharged with no offsetting refund possible. Cash
      // and gift card paths are unaffected.
      if (isCardMethod(method)) {
        const netDue = Math.max(amountDue - stagedLoyaltyAmount, 0)
        if (value > netDue + 0.005) {
          setPaymentAmountError(
            `Card payment cannot exceed $${formatPrice(netDue)}. Reduce the amount or pay the difference with cash.`,
          )
          return
        }
        // % loyalty estimate guard. A staged percent SpringBig offer
        // needs a cart-total base (subtotal or grand_total per the
        // store's pos.apply_rewards_discounts_on_final_total setting) to
        // compute its dollar reduction. Two failure modes block the
        // card path:
        //   - Base = 0: both preview AND serverTotals are missing, the
        //     estimated reduction is 0, and the terminal prompt would
        //     show the pre-loyalty gross.
        //   - posConfig undefined: the setting hasn't resolved yet, so
        //     the percent base defaults to subtotal even at stores that
        //     run on grand_total -- the FE under-discounts by
        //     tax × percent / 100 and the terminal prompt is too high.
        // Either way the cashier's card swipe is irreversible: the BE
        // clamps the recorded payment to amount_to_pay (less than what
        // was charged), but the physical card was already charged the
        // displayed amount. Block until totals + config both load.
        if (
          stagedLoyaltyDiscountType === SPRINGBIG_PERCENT_TYPE
          && (stagedLoyaltyPercentBase === 0 || !posConfig)
        ) {
          showToast({
            type: 'error',
            title: 'Cart totals unavailable',
            message:
              'Wait a moment for store totals to load, or clear the staged percent offer before running a card payment.',
          })
          return
        }
      }
    }

    setSelectedMethod(method)
    setErrorMessage('')

    if (method.id === 'cash') {
      // Pre-fill cash tendered with the amount the cashier said
      // they're collecting. They can override (e.g. customer hands
      // more cash, change is computed locally). Wire amount stays =
      // paymentAmount; cash tendered is purely local. Net of any
      // staged loyalty redemption — the BE applies the redemption as
      // a separate POINTS payment before the cash tender.
      if (!isRefundMode) {
        const { value, error } = validatePaymentAmount(paymentAmountInput)
        const fallbackNet = Math.max(amountDue - stagedLoyaltyAmount, 0)
        setCashTendered(error ? fallbackNet.toFixed(2) : value.toFixed(2))
      }
      setStep('cash')
    } else if (method.id === 'gift_card') {
      setGiftCardNumber('')
      setGiftCardBalance(null)
      setGiftCardId(null)
      setGiftCardIsBirchmount(false)
      setGiftCardPin('')
      setGiftCardLookupError(null)
      setGcNeedsRecheck(false)
      setStep('gift-card')
    } else if (method.id === 'credit_visa') {
      // Integrated card terminal: backend owns the polling loop against
      // Moneris Go / PMT3 / mcore. We commit-no-pay first to get a
      // stable receipt id, then start a session and poll for prompts.
      void startTerminalFlow(method)
    } else if (method.id === 'online_card') {
      // Gateway credit to the online (Moneris Checkout) card — no
      // terminal involved. Rebind the method to the card's actual brand
      // int + a labelled last4 so the JT and the confirm step are
      // honest, then show the confirm step.
      const brand = Number(
        onlineSourcePayment?.online_payment_type
        ?? onlineSourcePayment?.payment_type,
      )
      const last4 = onlineSourcePayment?.last4
      setSelectedMethod({
        ...method,
        label: last4 ? `Online Card (…${last4})` : 'Online Card',
        paymentTypeNumber: (Number.isFinite(brand) && brand > 0
          ? brand
          : method.paymentTypeNumber) as PaymentMethod['paymentTypeNumber'],
      })
      setStep('online-card')
    } else {
      // Standalone (manual): cashier runs the card on a non-integrated
      // terminal. Both sale and refund flow ask which brand was used
      // (Visa / MC / AMEX / Interac) so the JT records the matching
      // payment_type and the receipt label is honest. Refund-cap math
      // keys off `method.id` (still 'debit' for Standalone regardless
      // of brand), so the picker doesn't disturb the per-method
      // remaining-cap arithmetic.
      setStep('card-type')
    }
  }

  // Cashier picked a brand on the standalone card-type step. Mutate the
  // selected method in place so the rest of the flow (cash-step
  // wireAmount, processPaymentFlow's payment_type, the 'card' step's
  // displayed amount) all read the right paymentTypeNumber. We keep
  // method.id='debit' so refund-related lookups (PAYMENT_TYPE_TO_METHOD_ID
  // reverse map, refund cap gating) aren't disturbed -- only the integer
  // we wire to the BE changes.
  const handleSelectStandaloneCardType = (cardType: StandaloneCardType) => {
    if (!selectedMethod || isAnyPending) return
    setSelectedMethod({
      ...selectedMethod,
      label: `Standalone (${cardType.label})`,
      paymentTypeNumber: cardType.paymentTypeNumber,
    })
    setStep('card')
  }

  // Resolve the cashier-entered Amount as a number for the wire-side
  // override. Returns null when in refund mode (we use the backend
  // amount_to_pay directly) or when validation fails.
  const resolveOverrideAmount = (): number | null => {
    if (isRefundMode) return null
    const { value, error } = validatePaymentAmount(paymentAmountInput)
    if (error) return null
    return value
  }

  // Refund mode: push FE qty/removal adjustments to the BE refund receipt
  // before processing the refund payment. /copy-with-refund seeded the
  // BE receipt with all source items at full qty; if the cashier reduced
  // a line or removed it entirely, the BE doesn't know yet. Without this
  // step the refund payment uses the BE's stale `amount_to_pay` (full
  // original total) and tax calculations stay anchored to the un-trimmed
  // item set. Returns the latest receipt response so the caller picks up
  // the corrected `amount_to_pay` + totals.
  const syncRefundCartToReceipt = async (
    receiptId: number,
    receiptItems: Array<{ id?: number; qty?: number | string }>,
  ) => {
    // Map cart adjustments by BE receipt-item id. Refund receipts store
    // qty as SIGNED NEGATIVE (set by ops_lifecycle.copy_with_refund at
    // qty=-src_item.qty); the FE cart mirrors that same sign in
    // loadReceiptIntoCart. Compare and PUT in signed terms -- sending a
    // positive qty here would positivize the BE refund line and break
    // amount_to_pay (which becomes +2x the refund amount, leaving the
    // modal stuck on the method screen).
    const cartByReceiptItemId = new Map<number, number>()
    for (const item of cart) {
      if (typeof item.receipt_item_id !== 'number') continue
      cartByReceiptItemId.set(item.receipt_item_id, item.quantity)
    }

    let latest: { totals?: Record<string, unknown>; amount_to_pay?: number | string } | null = null

    for (const beItem of receiptItems) {
      if (typeof beItem.id !== 'number') continue
      const beQty =
        typeof beItem.qty === 'number'
          ? beItem.qty
          : parseFloat(String(beItem.qty ?? 0)) || 0
      const cartQty = cartByReceiptItemId.get(beItem.id)

      if (cartQty === undefined) {
        // Item removed from cart — DELETE on the BE.
        const updated = await deleteReceiptItem.mutateAsync({
          receiptId,
          itemId: beItem.id,
        })
        latest = updated as unknown as typeof latest
      } else if (cartQty !== beQty && cartQty !== 0) {
        // Qty changed — PUT the SIGNED qty so the refund-line sign
        // convention is preserved. update_from_request on the BE writes
        // the value verbatim into self.qty (pos.py:5234).
        const updated = await updateReceiptItem.mutateAsync({
          receiptId,
          itemId: beItem.id,
          data: { qty: cartQty },
        })
        latest = updated as unknown as typeof latest
      }
    }

    return latest
  }

  const processPaymentFlow = async (
    method: PaymentMethod,
    overrideAmount?: number,
    giftCardNum?: string,
    giftCardIdArg?: number | null,
    giftCardPinArg?: string,
    confirmOverpayment?: boolean,
    // Cashier confirmed the `possible_duplicate` dialog -- override the
    // BE duplicate-order guard on this retry (and on any price-drift
    // re-entry chained off it, so the cashier isn't re-asked).
    confirmDuplicate?: boolean,
  ) => {
    setStep('processing')
    setErrorMessage('')

    try {
      // Build the payment payload once -- both code paths need the same shape.
      // `effectiveAmount` is what we tell the BE to charge: the user's
      // override (cash exact-amount), the displayed amountDue (default), or
      // backend amount_to_pay (refund mode -- BE is authoritative).
      // confirm_overpayment is the bridge that tells the BE the cashier has
      // explicitly acknowledged a >$100 cash overpayment via the FE confirm
      // dialog -- without it the BE rejects with overpayment_unconfirmed.
      // Snapshot the FE-authoritative cart items so the BE can
      // truncate-and-replace its cart before charging. This is the
      // structural fix for the mid-payment add/remove/swap drift class:
      // a previously-cancelled terminal session leaves the FE's local
      // cart out of sync with the BE's persisted cart, and without
      // items[] on the wire the BE charges the stale total. Refund
      // mode skips items[] -- refunds drive cart state from the BE
      // receipt via syncRefundCartToReceipt, not from the local cart
      // store, so sending the local items here would replace the
      // refund line set with whatever happened to be in the sale-side
      // cart store. cartToRequest produces the same shape /cart/commit
      // uses, so the BE side runs the same replace_cart_items helper
      // for both code paths.
      // Gift-card retry gate: after a failure, force a fresh balance
      // Check before another attempt. Thrown (not returned) so the
      // existing catch handles state/UX consistently.
      if (method.id === 'gift_card' && gcNeedsRecheck) {
        throw new Error(
          'Check the gift card balance before retrying — the previous '
          + 'attempt may have already moved money.',
        )
      }

      // No approvals (`[]`): /payments and /terminal-payments don't gate
      // discounts, so a manager PIN has no business in these bodies.
      const cartItemsForPayment = isRefundMode
        ? undefined
        : cartToRequest(useCartStore.getState(), []).items

      // Bind this refund tender to a concrete SOURCE payment (refund-
      // integrity 2b): prefer a source of the same rail (gift card also
      // matched by card) with enough remaining, else the largest. The BE
      // auto-binds when we send nothing, so undefined is always safe.
      const pickSourceJtId = (amount: number): number | undefined => {
        if (!isRefundMode || !refundSourcePayments?.length) return undefined
        const isCardRail = method.id === 'debit' || method.id === 'credit_visa'
        const refundable = (sp: any): number => {
          const gross = Math.abs(parseFloat(String(sp.amount ?? '0'))) || 0
          const base = sp.remaining != null ? Math.abs(Number(sp.remaining)) : gross
          const tip = Math.abs(parseFloat(String(sp.tip_amount ?? '0'))) || 0
          return Math.max(0, base - tip)
        }
        const cands = (refundSourcePayments as any[]).filter((sp) => {
          // Synthetic online entry: no JT to bind (jt_id null) — the
          // online_card tender uses `online_refund: true` instead.
          if (sp.online) return false
          const m = PAYMENT_TYPE_TO_METHOD_ID[Number(sp.payment_type)]
          if (!m) return false
          const match = isCardRail ? (m === 'debit' || m === 'credit_visa') : m === method.id
          if (!match) return false
          if (method.id === 'gift_card' && giftCardId != null && sp.gift_card_id != null
              && Number(sp.gift_card_id) !== Number(giftCardId)) return false
          return true
        })
        if (!cands.length) return undefined
        const fit = cands.find((sp) => refundable(sp) + 0.005 >= amount)
          ?? [...cands].sort((a, b) => refundable(b) - refundable(a))[0]
        const sid = (fit as any)?.jt_id ?? (fit as any)?.id
        return typeof sid === 'number' && sid > 0 ? sid : undefined
      }

      const buildPaymentData = (amount: number): Parameters<typeof processPayment.mutateAsync>[0]['payment'] => {
        const isOnlineRefund = isRefundMode && method.id === 'online_card'
        const sourceJtId = isRefundMode && !isOnlineRefund
          ? pickSourceJtId(amount)
          : undefined
        return {
          payment_type: method.paymentTypeNumber,
          amount,
          // Online (Moneris Checkout) credit: the BE runs the gateway
          // refund first, then books this JT with the refund txn id.
          ...(isOnlineRefund ? { online_refund: true } : {}),
          ...(sourceJtId ? { source_jt_id: sourceJtId } : {}),
          ...(giftCardNum ? { gift_card_number: giftCardNum } : {}),
          ...(giftCardIdArg ? { gift_card_id: giftCardIdArg } : {}),
          ...(giftCardPinArg ? { gift_card_pin: giftCardPinArg } : {}),
          ...(confirmOverpayment ? { confirm_overpayment: true } : {}),
          ...(cartItemsForPayment !== undefined ? { items: cartItemsForPayment } : {}),
        }
      }

      // Loose receipt shape -- /cart/commit and /v2/payments both return
      // a Receipt-like object, but the field-by-field union of the two
      // type defs is messier than this helper needs. Treated as a record
      // bag here; the only fields read are `id`, `amount_to_pay`,
      // `totals`, `status`.
      type ReceiptShape = {
        id?: number
        amount_to_pay?: number | string
        totals?: Record<string, unknown>
        status?: number
        payments?: Array<{ payment_type?: number }>
      } | undefined | null
      let receipt: ReceiptShape
      let receiptId: number
      // The bill amount the customer owes at the moment this tender lands
      // -- used for change-due math at the bottom of this function. NOT
      // the amount we wire to the BE (which can be higher for cash-overpay,
      // since the BE writes Cash + Change rows; or lower when a SpringBig
      // loyalty clamp shaves the wire amount down to amount_to_pay). Each
      // routing branch below sets this to the bill (atomic: amountDue,
      // loyalty paths: backendAmountToPay/postLoyaltyAmount). Subtracting
      // tendered from this value yields the correct change regardless of
      // whether the FE estimate matched the BE's authoritative math.
      let amountOwed = 0

      // Refund flows never apply a SpringBig redemption — the customer is
      // getting money back, not earning/spending points — so this gates on
      // !isRefundMode.
      const hasPendingLoyalty =
        !isRefundMode && (pendingReward != null || pendingOffer != null)
      const pendingLoyaltyAmount = pendingReward?.amount ?? pendingOffer?.amount ?? 0
      // For percent redemptions the wire `amount` is the percent itself
      // (the BE multiplies by the receipt total at pay-time); the local
      // `amount` is just the display estimate. For flat-dollar both are
      // the same. discountType is undefined unless SpringBig flagged it.
      const pendingLoyaltyDiscountType =
        pendingReward?.discountType ?? pendingOffer?.discountType
      const pendingLoyaltyWireAmount = pendingLoyaltyDiscountType
        ? (pendingReward?.rawAmount ?? pendingOffer?.rawAmount ?? pendingLoyaltyAmount)
        : pendingLoyaltyAmount

      // Routing decision: continuation (POST /v2/payments) pays against the
      // existing BE receipt AS-IS; the commit branch (else, below) re-commits
      // the current cart, which the BE truncate-and-replaces onto the receipt
      // (cart.py:577-674) before charging. Continuation is correct ONLY when
      // the BE receipt already matches the FE cart:
      //   - Refund mode: useRefundCartMutations + syncRefundCartToReceipt
      //     keep BE items in sync per-edit; receipt = source of truth.
      //   - Partial tender: a payment is already on the ledger. A {pay}
      //     re-commit would record a NEW payment on top of the existing
      //     tender (over-payment -- the BE truncate-replace voids item JTs,
      //     not payment JTs, so the prior tender survives and a second one
      //     stacks), so a tendered receipt must stay on continuation. We
      //     detect the tender from BOTH serverTotals (hasPartialPaid) AND
      //     cartStore.partialPayments: serverTotals is volatile (nulled by
      //     cart edits, and only re-set when a payment response carries
      //     `totals`), whereas partialPayments is sourced from the receipt's
      //     payments and survives cart edits -- so it catches a tender that
      //     a stale serverTotals would miss and mis-route here.
      //
      // Any OTHER bound receipt (currentReceiptId set, no tender) may have
      // diverged and MUST re-commit so the BE reflects the current cart:
      //   - fresh commit whose payment was cancelled/deferred, then edited
      //     (select checkout -> cancel -> add item / discount), and
      //   - held-receipt unhold + edits.
      // Continuation there would charge the new cart total against the stale
      // BE total and trip the over/under guard at ops_payment.py. currentReceiptId
      // is the single binding -- set on BOTH a fresh commit (setReceiptId) and an
      // unhold (loadReceiptIntoCart), cleared on every detach/clear -- and
      // buildCommitRequest emits receipt_id from it so the BE replaces the items
      // in place (preserving the receipt entity + its ecom fields) rather than
      // creating a sibling.
      const hasRecordedTender =
        (useCartStore.getState().partialPayments?.length ?? 0) > 0
      const shouldUseContinuation =
        currentReceiptId !== null
        && (isRefundMode || hasPartialPaid || hasRecordedTender)

      if (shouldUseContinuation) {
        // ---- Continuation path ----
        // Receipt already exists and BE state matches FE state. Hit the
        // /v2/receipts/{id}/payments endpoint -- the cart on the FE is
        // not the source of truth here; the receipt server-side is.
        receiptId = currentReceiptId!
        const liveReceipt = await v2api.get<{
          receipt?: ReceiptShape & { items?: Array<{ id?: number; qty?: number | string }> }
        } & ReceiptShape & { items?: Array<{ id?: number; qty?: number | string }> }>(
          v2(`/receipts/${receiptId}`),
        )
        let resolvedReceipt: (ReceiptShape & {
          items?: Array<{ id?: number; qty?: number | string }>
        }) = (liveReceipt?.receipt ?? liveReceipt) as typeof resolvedReceipt

        // Pre-flight: the BE may have locked this receipt under our feet
        // (cashier voided from another path / device, payment finalized
        // in a sibling tab, etc.). Posting a payment against a locked
        // receipt 500s with "Receipt is locked (status=-1) and cannot be
        // modified" — and because the FE held a stale currentReceiptId,
        // the cashier sees that error on what they thought was a fresh
        // transaction. Detach locally and prompt for a new sale instead.
        if (LOCKED_RECEIPT_STATUSES.has(Number(resolvedReceipt?.status))) {
          clearReceipt()
          closeModal()
          showToast({
            type: 'warning',
            title: 'Receipt Closed',
            message: 'This receipt is no longer modifiable. Start a new transaction.',
          })
          return
        }

        // Refund mode: reconcile cart adjustments to the BE receipt
        // before charging the refund payment. Without this the cashier-
        // facing refund total ($35.29) doesn't match what the BE
        // refunds ($77.62 in the reported bug).
        if (isRefundMode && resolvedReceipt?.items) {
          const synced = await syncRefundCartToReceipt(
            receiptId,
            resolvedReceipt.items,
          )
          if (synced) {
            resolvedReceipt = synced as typeof resolvedReceipt
          }
        }

        // SpringBig redemption posts as its own POINTS payment before the
        // cashier's tender — keeps reward_id / offer_id on a single line
        // for the BE's _handle_springbig hook on close, and reduces
        // amount_to_pay so the next tender is for the right amount.
        // Clear the staging slot as soon as the BE accepts the redemption
        // so a partial-tender continuation doesn't double-apply on the
        // next pass through processPaymentFlow.
        //
        // Guard against re-redeeming: the live receipt already carries a
        // POINTS payment when a prior partial applied a redemption. The
        // offer/reward keeps listing as available (BE consumes it only at
        // close), so a re-staged redemption would post a second POINTS JT
        // here. Drop the duplicate instead — the cashier voids the existing
        // loyalty payment if they want to swap it.
        if (
          hasPendingLoyalty
          && pendingLoyaltyWireAmount > 0
          && !hasLoyaltyPayment(resolvedReceipt?.payments)
        ) {
          const loyaltyResult = await applyLoyaltyPoints.mutateAsync({
            receiptId,
            amount: pendingLoyaltyWireAmount,
            rewardId: pendingReward?.id,
            offerId: pendingOffer?.id,
            discountType: pendingLoyaltyDiscountType,
            ...(cartItemsForPayment !== undefined ? { items: cartItemsForPayment } : {}),
          })
          clearPendingLoyalty()
          const loyaltyReceipt = loyaltyResult.data?.receipt as unknown as ReceiptShape
          if (loyaltyReceipt) {
            resolvedReceipt = loyaltyReceipt as typeof resolvedReceipt
          }
        } else if (hasPendingLoyalty && hasLoyaltyPayment(resolvedReceipt?.payments)) {
          clearPendingLoyalty()
          showToast({
            type: 'warning',
            title: 'Loyalty already applied',
            message:
              'A reward or offer is already applied to this sale. Void that payment in Payments to change it.',
          })
        }

        const backendAmountToPay = Number(resolvedReceipt?.amount_to_pay ?? amountDue)
        // After a loyalty redemption the cashier's pre-staged `overrideAmount`
        // (or `amountDue`) may exceed the BE's new amount_to_pay — clamp to
        // the smaller value so we don't overcharge a partial cash payment.
        const baseTenderAmount =
          overrideAmount ?? (isRefundMode ? backendAmountToPay : amountDue)
        const effectiveAmount = hasPendingLoyalty
          ? Math.min(baseTenderAmount, Math.max(backendAmountToPay, 0))
          : baseTenderAmount
        // Bill at moment-of-tender = BE's authoritative amount_to_pay
        // (already reflects any prior partial tender or loyalty hit on
        // this receipt). Independent of what we send below -- the wire
        // amount can exceed the bill for cash overpay; the bill itself
        // is fixed and drives change-due math.
        amountOwed = Math.max(backendAmountToPay, 0)

        if (Math.abs(effectiveAmount) < 0.005) {
          // Nothing to charge: loyalty fully covered a sale, or the
          // remaining refund is already settled. Treat as a successful
          // pay with the current receipt as the final state.
          // Refund tenders are negative — guarding on `<= 0` instead of
          // magnitude silently skipped the POST for every cash/card
          // refund and left the cashier stuck on the method screen with
          // an unchanged remaining balance.
          receipt = resolvedReceipt as unknown as ReceiptShape
        } else {
          const result = await processPayment.mutateAsync({
            receiptId,
            payment: buildPaymentData(effectiveAmount),
          })

          // Existing v2 payment hooks may surface 'already_paid' /
          // 'duplicate_transaction' status flags. /cart/commit doesn't (it's
          // a one-shot atomic op) so this only matters here.
          const status = result.status
          if (status === 'already_paid') {
            clearReceipt()
            closeModal()
            showToast({ type: 'info', title: 'Already Paid', message: 'This receipt was already completed.' })
            return
          }
          if (status === 'duplicate_transaction') {
            showToast({ type: 'warning', title: 'Duplicate Transaction', message: 'This transaction was already processed.' })
            setStep('method')
            return
          }
          receipt = result.data?.receipt as unknown as ReceiptShape
        }
      } else if (hasPendingLoyalty && pendingLoyaltyWireAmount > 0) {
        // ---- Create-then-loyalty-then-pay path ----
        // Fresh sale with a staged SpringBig redemption. Splits the atomic
        // commit into commit (defer_payment) + apply loyalty + apply
        // tender, so reward_id / offer_id ride on the POINTS payment
        // before the cashier's tender lands.
        const v2Cart = useCartStore.getState()
        const commitReq = buildCommitRequest(v2Cart, { commit: true }, { receiptId: currentReceiptId, confirmDuplicate })
        const commitResult = await commitCart.mutateAsync(commitReq)
        const committedReceipt = (commitResult as { data?: { receipt?: ReceiptShape } } | undefined)?.data?.receipt
        receiptId = Number(committedReceipt?.id)
        if (!Number.isFinite(receiptId)) {
          throw new Error('Could not create receipt for loyalty redemption.')
        }
        setReceiptId(receiptId)

        const loyaltyResult = await applyLoyaltyPoints.mutateAsync({
          receiptId,
          amount: pendingLoyaltyWireAmount,
          rewardId: pendingReward?.id,
          offerId: pendingOffer?.id,
          discountType: pendingLoyaltyDiscountType,
          ...(cartItemsForPayment !== undefined ? { items: cartItemsForPayment } : {}),
        })
        clearPendingLoyalty()
        const loyaltyReceipt = loyaltyResult.data?.receipt as unknown as ReceiptShape
        const postLoyaltyAmount = Number(loyaltyReceipt?.amount_to_pay ?? 0)
        const baseTenderAmount = overrideAmount ?? amountDue
        const effectiveAmount = Math.min(baseTenderAmount, Math.max(postLoyaltyAmount, 0))
        // Bill = BE's amount_to_pay AFTER the loyalty redemption landed.
        // This is the dollar value the customer actually has to cover
        // with their cash tender; change-due is computed against it.
        amountOwed = Math.max(postLoyaltyAmount, 0)

        if (effectiveAmount <= 0) {
          receipt = loyaltyReceipt
        } else {
          const result = await processPayment.mutateAsync({
            receiptId,
            payment: buildPaymentData(effectiveAmount),
          })
          const status = result.status
          if (status === 'already_paid') {
            clearReceipt()
            closeModal()
            showToast({ type: 'info', title: 'Already Paid', message: 'This receipt was already completed.' })
            return
          }
          if (status === 'duplicate_transaction') {
            showToast({ type: 'warning', title: 'Duplicate Transaction', message: 'This transaction was already processed.' })
            setStep('method')
            return
          }
          receipt = result.data?.receipt as unknown as ReceiptShape
        }
      } else {
        // ---- Atomic create + pay path ----
        // Fresh sale (or a bound receipt — unhold-edited, or a fresh commit
        // whose payment was cancelled then edited — in which case
        // currentReceiptId is set and buildCommitRequest tacks `receipt_id`
        // onto the body so the BE truncate-and-replaces that receipt's items
        // in place).
        // Single round-trip: receipt + items + JTs + first payment, all
        // committed atomically with finalize(strict=True).
        const v2Cart = useCartStore.getState()
        const effectiveAmount = overrideAmount ?? amountDue
        // Bill = amountDue (cart total). overrideAmount can exceed the
        // bill on a cash-overpay full tender; the BE writes Cash + Change
        // in that case. Keep amountOwed at the bill so changeDue =
        // tendered - bill correctly returns the overpay portion.
        amountOwed = amountDue
        const req = buildCommitRequest(v2Cart, {
          pay: buildPaymentData(effectiveAmount),
        }, { receiptId: currentReceiptId, confirmDuplicate })
        const result = await commitCart.mutateAsync(req)
        receipt = (result as { data?: { receipt?: ReceiptShape } } | undefined)?.data?.receipt
        receiptId = Number(receipt?.id)
        // Pin the new receipt id so a partial-tender continuation routes
        // through the existing /v2/payments endpoint above on the next call.
        if (receiptId) {
          setReceiptId(receiptId)
        }
      }

      const remaining = Number(receipt?.amount_to_pay ?? 0)

      // Capture the receipt for the "payments so far" list rendered on
      // the method screen during a multi-tender flow. Each successful
      // payment overwrites with the current snapshot; the receipt's
      // own `payments` array is the source of truth (canonical, ordered).
      // Mirror it into the store in refund mode so a close+reopen of
      // this modal can re-seed `lastReceipt` synchronously and keep the
      // per-method cap honest without an async fetch.
      if (receipt) {
        setLastReceipt(receipt as LastReceipt)
        if (isRefundMode) updateRefundReceipt(receipt)
      }

      // Update server totals from payment response
      if (receipt?.totals) {
        const t = receipt.totals
        const totals = t as unknown as Record<string, unknown>
        setServerTotals({
          subtotal: Number(t.subtotal ?? 0),
          taxes: extractTaxes(totals),
          deposit: Number(totals.deposit_amount ?? totals.recycling_amount ?? 0),
          total: Number(t.total ?? 0),
          amountToPay: remaining,
        })
      }

      // Successful response landed (partial OR full) -- the current
      // commit attempt is done. Reset the price_drift silent-retry
      // counter so any future commit in this modal session starts
      // fresh; without this, a partial that took N silent retries
      // leaves the counter at N, and a subsequent commit (if a future
      // code path ever issues one in the same modal session) could
      // prematurely hit the max guard. Current routing keeps later
      // tenders on /v2/payments (no drift check), so this is latent-
      // hazard insurance, not a fix for a live bug.
      priceDriftRetryRef.current = 0

      // Partial-tender check uses Math.abs so it covers refunds too.
      // For a sale `remaining` is positive; for a refund it's negative
      // (BE convention: amount_to_pay < 0 means "still owed back to the
      // customer"). Earlier this used `remaining > 0`, which was true
      // only for sales — refund-mode partials silently skipped the
      // method-screen loop and dropped the cashier on the success
      // step after the first tender, leaving 50%+ of the refund
      // unprocessed.
      if (Math.abs(remaining) > 0.005) {
        setAmountDue(remaining)
        setStep('method')
        showToast({
          type: 'info',
          title: isRefundMode ? 'Partial Refund' : 'Partial Payment',
          message: `Remaining balance: $${formatPrice(Math.abs(remaining))}`,
        })
      } else {
        // Refunds never produce change-due — the cashier hands the
        // customer the refund amount, full stop. Skip the calculation
        // (cashTendered is unset in refund mode anyway, so this would
        // otherwise compute a bogus `0 - amountDue` value).
        //
        // Subtract from `amountOwed` (the bill at moment-of-tender)
        // rather than the wire amount we sent. Two cases this matters:
        //   1. Cash overpay (no loyalty): wire = tendered ($40), bill =
        //      $35; BE writes Cash + Change. tendered - wire = 0 would
        //      shortchange the customer. tendered - bill = $5 ✓.
        //   2. Percent loyalty with stale/missing preview: FE shows
        //      pre-loyalty gross on the prompt, cashier tenders that;
        //      BE clamps wire down to amount_to_pay after applying
        //      loyalty; tendered - wire = 0 would also shortchange.
        //      tendered - bill (post-loyalty BE amount_to_pay) = the
        //      true change ✓.
        if (!isRefundMode && method.paymentTypeNumber === PAYMENT_TYPES.CASH) {
          setChangeDue(Math.max(0, cashTenderedNum - amountOwed))
        }
        if (isRefundMode) {
          exitRefundMode()
          queryClient.invalidateQueries({ queryKey: ['receipts', 'refund-search'] })
        }
        // SpringBig redemption was applied as part of this transaction —
        // drop the staging slot and refetch the customer's loyalty balance
        // so the next drawer open shows post-redemption points + history.
        if (hasPendingLoyalty && selectedCustomer?.id) {
          clearPendingLoyalty()
          queryClient.invalidateQueries({
            queryKey: customerSpringbigKey(selectedCustomer.id),
          })
        }
        // Sale fully paid -- reset the register NOW (cart + customer + receipt
        // binding), not deferred to the cashier's "Done" click. This is the
        // shared confirm-first tail (cash / standalone card / gift card), so it
        // mirrors the Moneris terminal path (finalizeApprovedSession) and stops
        // a paid sale's cart + customer from surviving in the recovery-triplet
        // localStorage and rehydrating onto the next sale when the cashier
        // abandons the complete screen or a receipt-print hangs. clearReceipt()
        // runs useCartStore.clear() internally, so the cart.cleared broadcast
        // still precedes the confetti below; lastReceipt/changeDue are local
        // state and survive the reset for the complete screen + reprint.
        clearReceipt()
        // Tell the CFD to celebrate. Skip on refunds — getting money back
        // is not a celebration moment for the customer. Order matters:
        // clear() above triggers cart.cleared via the store subscription,
        // so the CFD has already flipped to the welcome screen by the
        // time confetti fires (no flicker mid-receipt).
        if (!isRefundMode) {
          broadcastPaymentApproved()
        }
        setStep('complete')
        // Fired after the step flip, never before: captureEvent is a
        // swallow-everything no-op, but keeping it downstream of the state
        // change means it can't sit between settlement and the cashier
        // seeing "complete". Reads the response snapshot rather than the
        // stores -- clearReceipt() above already wiped cart + customer +
        // receipt binding.
        captureCheckoutCompleted({
          receipt,
          receiptId,
          isRefund: isRefundMode,
          path: 'tender',
          customerId: selectedCustomer?.id ?? null,
        })
        // Print receipt -- use the resolved receiptId (currentReceiptId may
        // not be updated yet in this closure if we just took the commit path).
        // Gate on `pos.receipt.complete_action`: 'print' = auto-print as before;
        // 'ask' / 'nothing' defer to the cashier.
        const willAutoPrint = completeAction === 'print'
        if (willAutoPrint) {
          printReceipt.mutate({ receiptId })
        }
        // Cash transactions (sale OR refund) need the drawer open so the
        // cashier can make change / hand back cash. The printable buffer
        // already embeds a drawer pulse for cash receipts, so when auto-
        // print fires we skip the explicit kick to avoid double-pulsing.
        // In 'ask' / 'nothing' there's no print to ride along on, so we
        // kick the drawer ourselves.
        if (method.paymentTypeNumber === PAYMENT_TYPES.CASH && !willAutoPrint) {
          openCashDrawer.mutate()
        }
      }
    } catch (error: unknown) {
      let errorMsg = 'Payment failed. Please try again.'
      // V2ApiError is the canonical envelope from /cart/commit and the
      // existing /v2/payments hooks. Surface its `message` so the cashier
      // sees real reasons (price_drift, sanity_check_failed, overpayment_unconfirmed)
      // rather than a generic failure.
      let errorCode: string | null = null
      if (error instanceof V2ApiError) {
        errorCode = error.code
        errorMsg = error.detail || error.message || errorMsg
      } else if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string; message?: string; code?: string } } }
        errorCode = axiosError.response?.data?.code ?? null
        errorMsg = axiosError.response?.data?.error
          || axiosError.response?.data?.message
          || (error instanceof Error ? error.message : errorMsg)
      } else if (error instanceof Error) {
        errorMsg = error.message
      }

      // Refund-integrity: any gift-card failure (or the BE's explicit
      // pending/already-refunded verdicts) arms the balance-recheck gate.
      if (method.paymentTypeNumber === PAYMENT_TYPES.GIFT_CARD
          || errorCode === 'gift_card_operation_pending'
          || errorCode === 'gift_card_already_refunded') {
        setGcNeedsRecheck(true)
      }

      // Duplicate-order guard on /cart/commit: an identical basket was
      // committed on this register for this customer within the guard
      // window. Confirm explicitly with the cashier; on confirm, retry
      // the SAME payment with confirm_duplicate=true (buildCommitRequest
      // mints the fresh idempotency_key the changed body requires). On
      // cancel, drop to the method screen so the cashier can verify the
      // prior receipt in Orders instead of charging the customer twice.
      // Stays in 'processing' -- the dialog renders modally on top, no
      // "Payment Failed" alarm flashes (same UX as price_drift below).
      if (handleDiscountGateError(error)) return

      if (errorCode === 'possible_duplicate' && error instanceof V2ApiError) {
        showDuplicateGuardConfirm(error, {
          onConfirm: () => {
            void processPaymentFlow(
              method,
              overrideAmount,
              giftCardNum,
              giftCardIdArg,
              giftCardPinArg,
              confirmOverpayment,
              true,
            )
          },
          onCancel: () => {
            setStep('method')
          },
        })
        return
      }

      // price_drift recovery for the cash / non-terminal commit path.
      // /cart/commit's _check_price_drift (cart.py:367) rejects with 409
      // when the FE-sent expected_totals differ from the BE-recomputed
      // totals by more than tolerance. The common cause is a time-based
      // discount schedule crossing (sale ending/starting between when
      // the FE last fetched /cart/preview and when the cashier pressed
      // Cash); other causes are mid-transaction catalog edits or tax
      // changes. Without this branch the cashier sees a generic
      // "Payment Failed" alarm and the only recovery is logout +
      // refresh (which wipes the stale FE preview).
      //
      // Mirror the terminal flow's handlePriceDriftRetry: fetch fresh
      // /cart/preview, show "Total Changed — confirm?" dialog, and on
      // confirm re-enter processPaymentFlow with the same method +
      // amount. The re-entry's buildCommitRequest reads the fresh
      // cart.preview.totals we just stamped, so expected_totals matches
      // BE state and the retry's drift check passes. On cancel, drop
      // back to the method screen so the cashier can review or
      // re-tender.
      //
      // Stay in setStep('processing') during the recovery — the error
      // step never renders, no "Payment Failed" alarm flashes. The
      // confirm dialog renders modally on top via ConfirmModal.
      if (errorCode === 'price_drift') {
        // Thread the full original payment context through so the retry
        // is *equivalent* to the cashier's original action: gift_card_*
        // fields (without which a gift-card payment 400s), and
        // confirm_overpayment (without which a large-cash tender re-
        // triggers the overpayment guard). Dropping any of these turned
        // a recoverable drift into a hard payment failure.
        await handleCashPriceDriftRetry(
          method,
          overrideAmount,
          giftCardNum,
          giftCardIdArg,
          giftCardPinArg,
          confirmOverpayment,
          confirmDuplicate,
        )
        return
      }

      // Gift-card-specific overrides — friendlier copy + a refetch of the
      // live balance when the redeem failed because the card was short.
      if (errorCode === 'refund_exceeds_method_cap') {
        // BE per-method cap rejected this refund. The error.detail
        // already explains which method, the original-paid figure, and
        // how much has already been refunded -- pass it through
        // verbatim. Refresh lastReceipt + the in-store refund receipt
        // from the BE so the gating UI re-syncs and the over-cap method
        // goes grey on the next method screen, even after a close+
        // reopen.
        if (currentReceiptId) {
          v2api.get<{ receipt?: LastReceipt } & Record<string, unknown>>(
            v2(`/receipts/${currentReceiptId}`),
          ).then(
            (live) => {
              const r = ((live?.receipt ?? live) as LastReceipt) ?? null
              if (r) {
                setLastReceipt(r)
                if (isRefundMode) updateRefundReceipt(r)
              }
            },
            () => { /* swallow -- error already surfaced */ },
          )
        }
      } else if (errorCode === 'birchmount_unavailable') {
        errorMsg = 'Gift card service is temporarily unavailable. Please try again.'
      } else if (errorCode === 'gift_card_insufficient_balance') {
        errorMsg = 'Gift card balance is not enough to cover this amount. Refresh the balance and try a smaller amount or another tender.'
        // Pull the live balance so the cashier sees what's actually
        // available before they retry.
        if (giftCardNumber.trim()) {
          checkGiftCardBalance.mutateAsync(giftCardNumber.trim()).then(
            (r) => {
              setGiftCardBalance(extractGiftCardBalance(r.card))
              setGiftCardId(extractGiftCardId(r.card))
              setGiftCardIsBirchmount(isBirchmountCard(r.card))
            },
            () => { /* swallow — main error already surfaced */ },
          )
        }
      } else if (errorCode === 'commit_in_progress') {
        // BE claim-early idempotency refused the commit: an attempt with
        // this SAME key is mid-flight, or died mid-commit after possibly
        // taking payment (worker killed during a deploy/OOM — the Bowen
        // Road 2026-07-07 duplicates). The FE typically sees this when the
        // browser transparently re-sent the POST after the original
        // connection died without a response. The sale may or may not
        // have completed — the one thing the cashier must NOT do is
        // blind-ring it again, so the copy sends them to Recent
        // Transactions first. (Continuation receipts don't need the
        // copy: the reconcile below finds the Paid receipt and completes.)
        errorMsg = 'This payment may have already gone through on an '
          + 'earlier attempt. Check Recent Transactions for this sale '
          + 'before charging again — only retry if no matching receipt '
          + 'is there.'
      } else if (errorCode === 'outside_sale_hours') {
        const copy = saleHoursCopy(error, errorMsg)
        errorMsg = copy.message
        setSaleHoursStoreTime(copy.storeTime)
      }

      // Reconcile-on-failure: a commit/payment can fail at the HTTP layer
      // (a transient BE error AFTER the receipt was already committed) even
      // though the charge went through -- the exact shape of the duplicate-
      // receipt incidents. Before dropping the cashier on the error step,
      // where "Try Again" would create a DUPLICATE paid receipt, re-check the
      // bound receipt: if it's already Paid, complete the sale instead of
      // inviting a retry. Only the continuation/partial path carries a
      // currentReceiptId here (a fresh-sale commit that throws before
      // returning has no id to check -- the BE listener fix is what removes
      // that crash class). Skipped in refund mode (different lifecycle).
      if (currentReceiptId && !isRefundMode) {
        try {
          const live = await v2api.get<{ receipt?: Receipt } & Receipt>(
            v2(`/receipts/${currentReceiptId}`),
          )
          const liveReceipt = (live?.receipt ?? live) as Receipt
          if (Number(liveReceipt?.status) === RECEIPT_STATUS.PAID) {
            setLastReceipt(liveReceipt as unknown as LastReceipt)
            useCartStore.getState().clear()
            clearReceipt()
            setStep('complete')
            captureCheckoutCompleted({
              receipt: liveReceipt,
              receiptId: currentReceiptId,
              isRefund: false,
              path: 'reconciled',
              customerId: selectedCustomer?.id ?? null,
            })
            showToast({
              type: 'success',
              title: 'Payment Completed',
              message: 'This sale was already paid — no retry needed.',
            })
            return
          }
        } catch {
          // Re-check failed (network, etc.) -- fall through to the normal
          // error path; the cashier can verify in Orders before retrying.
        }
      }

      setErrorIsSaleHours(errorCode === 'outside_sale_hours')
      setErrorMessage(errorMsg)
      setStep('error')
    }
  }

  // ---- Integrated card-terminal flow ----
  //
  // Happy path:
  //   1. setStep('card-terminal') → cashier sees "Starting payment…".
  //   2. If no `currentReceiptId`, commit-no-pay to create the receipt
  //      and pin its id. (Refund-mode and partial-tender continuations
  //      already have a receipt — skip the commit.)
  //   3. Mint an idempotency_key; stash {receiptId, body, method} in
  //      `lastStartRef` so a network blip or recoverable 409 retry can
  //      reuse the SAME key (BE de-dupes; never charges twice).
  //   4. POST /terminal-payments — backend opens a row + kicks off a
  //      poller against the physical terminal.
  //   5. Set `terminalSessionId`; the polling query takes over and
  //      pushes `display_message` updates into the UI.
  //
  // 409 handling:
  //   - `overpayment_unconfirmed` — HARD FAIL to error step. Card
  //     overpayment is gated upfront in handleSelectMethod; if it
  //     reaches the BE and gets rejected, that's a state-drift edge
  //     case (cart mutated mid-flow) that the cashier must resolve by
  //     reducing the amount. No "Continue anyway" path -- a card swipe
  //     can't return cash change.
  //   - `price_drift` — refetch receipt for the new total, cashier
  //     confirms the new amount, retry with updated `expected_total`
  //     (same idem key so no double-start).
  //
  // Final-state recovery (race-safe):
  //   - On `timeout` / `error`, we re-fetch the receipt + session
  //     before showing an error. The customer may have been charged at
  //     the terminal even if our backend lost the result; in that case
  //     we route through the approved path (`finalizeApprovedSession`)
  //     so the receipt prints and the cashier doesn't double-charge.

  const resetTerminalState = (options?: { preserveLastStart?: boolean }) => {
    setTerminalSessionId(null)
    setTerminalReceiptId(null)
    handledSessionIdRef.current = null
    // The register_busy recovery path (handleCancelBusyAndRetry)
    // needs lastStartRef.current intact to replay the original
    // start with the SAME idempotency_key. Other error branches
    // (overpayment_unconfirmed, processor_unsupported, generic)
    // don't auto-retry, so they leave preserveLastStart undefined
    // and the ref is cleared as before.
    if (!options?.preserveLastStart) {
      lastStartRef.current = null
    }
  }

  // Network-level errors don't carry a v2 envelope (they never reached
  // a v2 handler). Distinguishable from V2ApiError, which is a
  // structured response. We retry exactly once on these — the
  // idempotency_key replay-protects us from a double-start.
  const isNetworkLevelError = (err: unknown): boolean => {
    if (err instanceof V2ApiError) return false
    const e = err as { code?: string; message?: string; response?: unknown }
    if (e?.response !== undefined) return false
    if (e?.code === 'ERR_NETWORK' || e?.code === 'ECONNABORTED') return true
    if (typeof e?.message === 'string' && /network|timeout/i.test(e.message)) return true
    return false
  }

  const startTerminalSession = async (
    receiptId: number,
    body: StartTerminalPaymentBody,
    method: PaymentMethod,
  ): Promise<TerminalPaymentSession> => {
    lastStartRef.current = { receiptId, body, method }
    try {
      return await startTerminalPayment.mutateAsync({ receiptId, body })
    } catch (err) {
      if (!isNetworkLevelError(err)) throw err
      // Replay-safe retry: same idempotency_key returns the cached
      // session if the BE actually got the original request, or starts
      // fresh otherwise. Either way, exactly one terminal transaction.
      return await startTerminalPayment.mutateAsync({ receiptId, body })
    }
  }

  // Promote a `status: approved` session into the success path (cart
  // cleared, complete step, receipt printed). Extracted so the
  // race-safe recovery branch can reuse it when timeout/error turns
  // out to have actually charged the card.
  const finalizeApprovedSession = (receipt: Receipt) => {
    // Trust receipt.status over receipt.amount_to_pay when the BE has
    // already locked the receipt (Paid / Paying). The terminal session
    // response sometimes carries a receipt snapshot whose
    // `amount_to_pay` is the pre-pay value even though pay() has
    // already settled it server-side — without this guard the FE
    // thinks the refund was partial, drops back to the method screen,
    // and the cashier's next click hits the BE with a fresh
    // /terminal-payments POST that responds `already_paid`.
    const isLocked = LOCKED_RECEIPT_STATUSES.has(Number(receipt.status))
    const remaining = isLocked ? 0 : Number(receipt.amount_to_pay ?? 0)

    setLastReceipt(receipt as LastReceipt)
    if (isRefundMode) updateRefundReceipt(receipt)
    if (receipt.totals) {
      const t = receipt.totals as unknown as Record<string, unknown>
      setServerTotals({
        subtotal: Number(t.subtotal ?? 0),
        taxes: extractTaxes(t),
        deposit: Number(t.deposit_amount ?? t.recycling_amount ?? 0),
        total: Number(t.total ?? 0),
        amountToPay: remaining,
      })
    }

    setTerminalSessionId(null)
    lastStartRef.current = null

    // Math.abs covers refunds (amount_to_pay < 0). Same fix as the
    // processPaymentFlow branch above.
    if (Math.abs(remaining) > 0.005) {
      setAmountDue(remaining)
      setStep('method')
      showToast({
        type: 'info',
        title: isRefundMode ? 'Partial Refund' : 'Partial Payment',
        message: `Remaining balance: $${formatPrice(Math.abs(remaining))}`,
      })
      return
    }

    if (isRefundMode) {
      exitRefundMode()
      queryClient.invalidateQueries({ queryKey: ['receipts', 'refund-search'] })
    }
    // Refresh the customer's SpringBig snapshot — terminal flow only
    // gets here for sales, and any redemption applied earlier in
    // startTerminalFlow now lives on the closed receipt.
    if (!isRefundMode && selectedCustomer?.id) {
      queryClient.invalidateQueries({
        queryKey: customerSpringbigKey(selectedCustomer.id),
      })
    }
    // Reset the register NOW that the charge has settled -- do not defer it to
    // the cashier's "Done" click. At the 'complete' step the modal's X is
    // hidden and the backdrop is inert, so if the cashier abandons the screen
    // (or a hung receipt-print freezes "Done") the paid sale's cart + customer
    // would otherwise survive in the recovery-triplet localStorage and
    // rehydrate onto the next sale -- the reported "card sale doesn't clear the
    // cart/profile" bug. clearReceipt() wipes posStore (cart + customer +
    // currentReceiptId) and the v2 cartStore; `lastReceipt` is local state, so
    // the "Payment Complete" screen and reprint still render.
    clearReceipt()
    setStep('complete')
    captureCheckoutCompleted({
      receipt,
      receiptId: terminalReceiptId,
      isRefund: isRefundMode,
      path: 'terminal',
      customerId: selectedCustomer?.id ?? null,
    })
    const receiptIdForPrint = Number(receipt.id ?? terminalReceiptId)
    if (receiptIdForPrint && completeAction === 'print') {
      printReceipt.mutate({ receiptId: receiptIdForPrint })
    }
  }

  // Race-safe reconcile for `timeout` / `error` final states. The
  // terminal+backend may have lost the result mid-handshake while the
  // customer was actually charged, so before showing an error we
  // double-check: (1) read the receipt — if PAID or amount_to_pay <= 0,
  // the customer was charged; (2) re-read the session — covers the
  // very rare race where the session row updated but the receipt
  // update lagged.
  const reconcileAfterTerminalFailure = async (
    receiptId: number,
    sessionId: string,
  ): Promise<Receipt | null> => {
    try {
      const live = await v2api.get<{ receipt?: Receipt } & Partial<Receipt>>(
        v2(`/receipts/${receiptId}`),
      )
      const receipt = (live?.receipt ?? (live as unknown)) as Receipt | undefined
      if (receipt && receipt.id) {
        const remaining = Number(receipt.amount_to_pay ?? 0)
        if (receipt.status === RECEIPT_STATUS.PAID || remaining <= 0) {
          return receipt
        }
      }
    } catch {
      // fall through
    }

    try {
      const env = await v2api.get<{ data: TerminalPaymentSession }>(
        v2(`/receipts/${receiptId}/terminal-payments/${sessionId}`),
      )
      const data = env?.data
      if (data?.status === 'approved' && data.receipt) {
        return data.receipt as Receipt
      }
    } catch {
      // ignore
    }

    return null
  }

  const startTerminalFlow = async (
    method: PaymentMethod,
    // Threaded by drift recovery (handleTerminalCommitPriceDrift) to carry
    // the cashier's tender intent across a retry. Undefined on a normal
    // first entry -- we read the "Amount to Pay" field instead.
    tenderOverride?: number,
    // Cashier confirmed the `possible_duplicate` dialog -- override the
    // BE duplicate-order guard on the commit-no-pay retry.
    confirmDuplicate?: boolean,
  ) => {
    // Synchronous re-entry latch (see startInFlightRef comment).
    // Checked BEFORE any state mutation so a double-click doesn't
    // flicker the UI through 'card-terminal' before bailing.
    if (startInFlightRef.current) return
    startInFlightRef.current = true

    setSelectedMethod(method)
    setErrorMessage('')
    setStep('card-terminal')
    handledSessionIdRef.current = null

    // Capture the cashier's tender intent ONCE, up front. A re-entry from
    // drift recovery passes it explicitly; otherwise read the "Amount to
    // Pay" field. Captured before any /cart/preview refresh can auto-snap
    // paymentAmountInput (the useEffect at L425), so a partial card tender
    // survives drift recovery instead of being upgraded to a full charge.
    const effectiveOverride = tenderOverride !== undefined
      ? tenderOverride
      : resolveOverrideAmount()

    // Is this a FULL tender, or did the cashier deliberately type a partial?
    // The input auto-fills from amountDue (the useEffect at L437), so an
    // untouched field just echoes whatever amountDue held at that moment --
    // which is stale if any cart state failed to invalidate serverTotals.
    // A full tender therefore re-derives its amount from the commit response
    // below; only a hand-entered partial is taken at face value.
    const preCommitNetDue = Math.max(amountDue - stagedLoyaltyAmount, 0)
    const isFullTender = effectiveOverride === null
      || effectiveOverride === undefined
      || Math.abs(effectiveOverride - preCommitNetDue) < 0.005

    try {
      let receiptId: number
      // BE-persisted grand total from /cart/commit, used to anchor the
      // terminal price-drift gate (set in the commit branch below). Stays
      // null on the continuation path (no commit) -> falls back to cartTotal.
      let committedTotal: number | null = null
      // BE-persisted outstanding balance from the same commit. This -- not
      // the pre-commit `amountDue` -- is what a full tender charges, so a
      // stale FE total can never reach the terminal.
      let committedAmountToPay: number | null = null

      // Whether to re-commit the in-memory cart before charging. Mirror
      // processPaymentFlow's predicate (L1163) so the card and cash flows
      // behave identically: only a genuine CONTINUATION -- a refund, or a
      // receipt that already carries a tender -- pays the existing receipt
      // as-is (BE is the source of truth there). A fresh sale OR a recalled
      // receipt with pending edits (e.g. an online-order price match applied
      // after recall) MUST commit first, otherwise the cart's discount/qty
      // edits are charged on the card but never persisted to the receipt --
      // leaving a phantom remaining balance (the original price-match bug).
      const hasRecordedTender =
        (useCartStore.getState().partialPayments?.length ?? 0) > 0
      const shouldUseContinuation =
        currentReceiptId !== null
        && (isRefundMode || hasPartialPaid || hasRecordedTender)

      if (shouldUseContinuation) {
        // ---- Continuation path ----
        // Receipt exists and the BE already reflects the FE cart (no
        // un-committed edits). Charge it directly.
        receiptId = currentReceiptId!

        if (isRefundMode) {
          // Refund-mode reconcile (same as processPaymentFlow) so the
          // BE's amount_to_pay matches what the cashier trimmed in FE
          // before we charge.
          const liveReceipt = await v2api.get<{
            receipt?: { items?: Array<{ id?: number; qty?: number | string }> }
            items?: Array<{ id?: number; qty?: number | string }>
          }>(v2(`/receipts/${receiptId}`))
          const items =
            liveReceipt?.receipt?.items ?? liveReceipt?.items ?? []
          if (items.length > 0) {
            await syncRefundCartToReceipt(receiptId, items)
          }
        }
      } else {
        // ---- Commit-no-pay path ----
        // Fresh sale, OR a bound receipt (recalled online order / unhold-
        // edited / fresh-commit-then-edited) that has no tender yet. Persist
        // the current cart so the receipt reflects exactly what the cashier
        // sees BEFORE the card is charged. buildCommitRequest tacks on
        // receipt_id from currentReceiptId, so the BE truncate-and-replaces
        // that receipt's items in place (preserving the receipt entity + its
        // ecom fields); a null id creates a fresh receipt. expected_totals
        // rides along so the BE price-drift gate runs at commit time too.
        const v2Cart = useCartStore.getState()
        const req = buildCommitRequest(v2Cart, { commit: true }, { receiptId: currentReceiptId, confirmDuplicate })
        let commitResult: Awaited<ReturnType<typeof commitCart.mutateAsync>>
        try {
          commitResult = await commitCart.mutateAsync(req)
        } catch (commitError) {
          // Commit-time price_drift (stale expected_totals vs the BE's
          // recompute -- e.g. a discount-schedule crossing between the last
          // /cart/preview and Checkout) 409s HERE, before
          // startTerminalSession sets lastStartRef -- so handleStartError /
          // handlePriceDriftRetry can't see it and the cashier would drop to
          // generic "Try Again". Route to the commit-flavored recovery
          // (sibling of the cash flow's handleCashPriceDriftRetry).
          // Fire-and-forget + return so this invocation unwinds and clears
          // startInFlightRef before the retry re-enters; the recovery awaits
          // a fresh preview before re-entry, by which point the latch is
          // clear. Non-drift commit errors propagate to the outer catch.
          if (commitError instanceof V2ApiError && commitError.code === 'price_drift') {
            void handleTerminalCommitPriceDrift(method, effectiveOverride, confirmDuplicate)
            return
          }
          // Discount-limit gate: approval flow, not the terminal error step.
          if (handleDiscountGateError(commitError)) {
            resetTerminalState()
            return
          }
          // Duplicate-order guard on the commit-no-pay: same recovery
          // shape as the cash path -- confirm with the cashier, retry
          // this flow with confirm_duplicate=true (fresh idempotency key
          // via buildCommitRequest). Return so this invocation unwinds
          // and clears startInFlightRef (finally below) before the
          // onConfirm re-entry; the dialog renders on top of the
          // 'card-terminal' step, and cancel drops to the method screen.
          if (commitError instanceof V2ApiError && commitError.code === 'possible_duplicate') {
            showDuplicateGuardConfirm(commitError, {
              onConfirm: () => {
                void startTerminalFlow(method, effectiveOverride ?? undefined, true)
              },
              onCancel: () => {
                resetTerminalState()
                setStep('method')
              },
            })
            return
          }
          throw commitError
        }
        // Commit succeeded -- the drift (if any) is resolved, so clear the
        // commit-drift silent-retry guard HERE rather than after the
        // terminal start. Otherwise a commit-succeeds-then-start-fails
        // sequence would carry a stale retry count into the next attempt
        // and hit the cap early.
        priceDriftRetryRef.current = 0
        const committedReceipt = (commitResult as {
          data?: {
            receipt?: {
              id?: number
              amount_to_pay?: string | number | null
              totals?: { total?: string | number }
            }
          }
        } | undefined)?.data?.receipt
        const newId = Number(committedReceipt?.id)
        if (!newId) {
          throw new Error('Backend did not return a receipt id.')
        }
        receiptId = newId
        setReceiptId(newId)
        // Anchor the terminal price-drift gate to the total the BE just
        // persisted -- NOT the render-captured `cartTotal`, which is stale on
        // a drift-recovery retry (the onConfirm closure predates the preview
        // refresh). Without this, the retry re-sends the old total and trips
        // a second, redundant price_drift cycle after the cashier already
        // accepted the new one.
        committedTotal = committedReceipt?.totals?.total != null
          ? (parseFloat(String(committedReceipt.totals.total)) || 0)
          : null
        // Outstanding balance the BE just persisted. Prefer it over
        // `committedTotal` so a receipt that already carries a tender charges
        // the remainder, not the grand total.
        committedAmountToPay = committedReceipt?.amount_to_pay != null
          ? (parseFloat(String(committedReceipt.amount_to_pay)) || 0)
          : committedTotal
      }

      setTerminalReceiptId(receiptId)

      // FE-authoritative cart items, snapshot once for this terminal
      // attempt. Reuse the same snapshot for staged-loyalty posting and
      // terminal start so the BE computes both against identical item
      // state.
      // No approvals (`[]`): /payments and /terminal-payments don't gate
      // discounts, so a manager PIN has no business in these bodies.
      const cartItemsForTerminal = isRefundMode
        ? undefined
        : cartToRequest(useCartStore.getState(), []).items

      // Apply any staged SpringBig redemption before kicking the terminal —
      // mirrors the manual-tender flow above. Refund mode never carries a
      // staged redemption (cashier got money back, not points), so the
      // gate matches `processPaymentFlow`. Skip when the receipt already
      // carries a POINTS payment from a prior partial: re-applying would
      // post a duplicate SpringBig JT while SpringBig records one redemption.
      let postLoyaltyAmountToPay: number | null = null
      // Held for the zero-charge tail below. When loyalty covers the whole
      // balance no terminal session runs and no /payments response comes
      // back, so this snapshot is the only receipt the completion event
      // has to describe the sale with.
      let loyaltySettledReceipt: Receipt | null = null
      if (
        !isRefundMode
        && (pendingReward != null || pendingOffer != null)
        && !hasLoyaltyPayment(lastReceipt?.payments)
      ) {
        const loyaltyAmount = pendingReward?.amount ?? pendingOffer?.amount ?? 0
        const loyaltyDiscountType =
          pendingReward?.discountType ?? pendingOffer?.discountType
        const loyaltyWireAmount = loyaltyDiscountType
          ? (pendingReward?.rawAmount ?? pendingOffer?.rawAmount ?? loyaltyAmount)
          : loyaltyAmount
        if (loyaltyWireAmount > 0) {
          const loyaltyResult = await applyLoyaltyPoints.mutateAsync({
            receiptId,
            amount: loyaltyWireAmount,
            rewardId: pendingReward?.id,
            offerId: pendingOffer?.id,
            discountType: loyaltyDiscountType,
            ...(cartItemsForTerminal !== undefined ? { items: cartItemsForTerminal } : {}),
          })
          clearPendingLoyalty()
          const loyaltyReceipt = loyaltyResult.data?.receipt

          // Sync the just-recorded loyalty payment into lastReceipt +
          // serverTotals so that a subsequent terminal cancel/failure
          // dropping back to the method screen renders the active
          // SpringBig line under "Payments So Far" and shows the
          // correct remaining balance. Without this the cashier sees
          // the cart as if no redemption was applied -- re-applying
          // creates a duplicate SpringBig JT on the BE while SpringBig
          // only records one redemption (customer effectively gets
          // $10 off for 1000 points). Mirrors the post-payment sync
          // in processPaymentFlow at L1173-1187; the terminal path
          // lacks a follow-up /payments response that would otherwise
          // do this implicitly, because /terminal-payments returns a
          // session row -- not a full receipt.
          if (loyaltyReceipt) {
            setLastReceipt(loyaltyReceipt as LastReceipt)
            if (loyaltyReceipt.totals) {
              const t = loyaltyReceipt.totals as unknown as Record<string, unknown>
              setServerTotals({
                subtotal: Number(t.subtotal ?? 0),
                taxes: extractTaxes(t),
                deposit: Number(t.deposit_amount ?? t.recycling_amount ?? 0),
                total: Number(t.total ?? 0),
                amountToPay: Number(loyaltyReceipt.amount_to_pay ?? 0),
              })
            }
          }

          loyaltySettledReceipt = (loyaltyReceipt as Receipt | undefined) ?? null
          postLoyaltyAmountToPay = Number(loyaltyReceipt?.amount_to_pay ?? 0)
        }
      } else if (
        !isRefundMode
        && (pendingReward != null || pendingOffer != null)
        && hasLoyaltyPayment(lastReceipt?.payments)
      ) {
        clearPendingLoyalty()
        showToast({
          type: 'warning',
          title: 'Loyalty already applied',
          message:
            'A reward or offer is already applied to this sale. Void that payment in Payments to change it.',
        })
      }

      const overrideAmount = effectiveOverride
      const baseAmount = isRefundMode
        // Cap to per-method remaining (negative for refund). Without
        // this cap, a $30 refund of a $20 visa + $10 cash sale would
        // try to send $30 to the visa terminal — but the customer's
        // visa was only charged $20, so the FE has to throttle.
        ? -refundWireAmount(method)
        // Full tender: charge the balance the commit above just persisted,
        // never the pre-commit FE figure. Any FE state that went stale
        // between recall and tender (see the serverTotals note in
        // posStore.addToCart) is structurally unable to reach the terminal.
        // A hand-entered partial still wins -- that's a deliberate amount.
        : (isFullTender && committedAmountToPay != null
            ? committedAmountToPay
            : (overrideAmount ?? amountDue))
      // After a loyalty redemption the receipt's amount_to_pay may be
      // less than the cashier-staged amount — clamp so we don't charge
      // the card more than what's actually owed.
      const amount = postLoyaltyAmountToPay != null
        ? Math.min(baseAmount, Math.max(postLoyaltyAmountToPay, 0))
        : baseAmount

      if (Math.abs(amount) < 0.005) {
        // Nothing to charge or refund — loyalty fully covered the sale,
        // or the refund was already settled. Treat as a successful pay
        // and finalize without starting a terminal session. Refund
        // tenders are negative ($-39.36 etc.), so guarding on `<= 0`
        // (instead of magnitude) silently skipped startTerminalSession
        // for every integrated card refund — the modal flipped to
        // "Payment Successful" with no commit posted and the receipt's
        // amount_to_pay unchanged. Mirrors processPaymentFlow's fix.
        // Full register reset (cart + customer + receipt binding), not a
        // cart-only clear -- same settlement-time reset as the other success
        // tails, so this zero-charge finalize can't leave a stale
        // currentReceiptId + customer to rehydrate onto the next sale.
        clearReceipt()
        if (selectedCustomer?.id) {
          queryClient.invalidateQueries({
            queryKey: customerSpringbigKey(selectedCustomer.id),
          })
        }
        setStep('complete')
        captureCheckoutCompleted({
          receipt: loyaltySettledReceipt,
          receiptId,
          isRefund: isRefundMode,
          path: 'zero_charge',
          customerId: selectedCustomer?.id ?? null,
        })
        if (completeAction === 'print') {
          printReceipt.mutate({ receiptId })
        }
        return
      }

      const body: StartTerminalPaymentBody = {
        amount,
        idempotency_key: idempotencyKey(),
        // Anchor the price-drift gate to the receipt total the commit just
        // persisted (committedTotal); fall back to the cashier-displayed
        // cartTotal on the continuation path. For partials the gate is on
        // the receipt total, not the wire amount we're tendering.
        expected_total: committedTotal ?? cartTotal,
        payment_type_hint: method.paymentTypeNumber,
        ...(cartItemsForTerminal !== undefined ? { items: cartItemsForTerminal } : {}),
      }

      const session = await startTerminalSession(receiptId, body, method)
      setTerminalSessionId(session.session_id)
    } catch (error: unknown) {
      handleStartError(error)
    } finally {
      startInFlightRef.current = false
    }
  }

  // Replay the start with a tweaked body but the SAME idempotency_key.
  // Used by the overpayment / price_drift confirm dialogs and by the
  // register_busy cancel-and-retry flow below.
  const retryStartWithBody = async (newBody: StartTerminalPaymentBody) => {
    const last = lastStartRef.current
    if (!last) return
    // Shares the same start latch as startTerminalFlow -- a confirm-
    // dialog click while a fresh start is already mid-flight (or
    // vice-versa) bails before issuing a second POST. Both code paths
    // hit POST /terminal-payments; the shared latch matches their
    // shared exclusion property.
    if (startInFlightRef.current) return
    startInFlightRef.current = true
    setErrorMessage('')
    setStep('card-terminal')
    handledSessionIdRef.current = null
    try {
      const session = await startTerminalSession(last.receiptId, newBody, last.method)
      setTerminalSessionId(session.session_id)
    } catch (error: unknown) {
      handleStartError(error)
    } finally {
      startInFlightRef.current = false
    }
  }

  // Duplicate-order-guard confirm, shared by the cash and terminal
  // commit paths. The BE 409s `possible_duplicate` when an identical
  // basket (same products+qty, same register, same customer) was
  // committed within the guard window -- the signature of a cashier
  // re-ring after a card-terminal hiccup (the July 2026 double-charge
  // cluster). Surface the prior receipt so the cashier can check
  // whether the customer was already charged BEFORE ringing it again;
  // on confirm the caller retries with confirm_duplicate=true.
  const showDuplicateGuardConfirm = (
    error: V2ApiError,
    handlers: { onConfirm: () => void; onCancel: () => void },
  ) => {
    const extra = error.extra ?? {}
    const priorNumber =
      typeof extra.receipt_number === 'string' ? extra.receipt_number : null
    const secondsAgo =
      typeof extra.seconds_ago === 'number' ? extra.seconds_ago : null
    const priorTotalNum =
      extra.total != null ? parseFloat(String(extra.total)) : NaN
    const priorLabel = priorNumber
      ? ` (#${priorNumber}${Number.isFinite(priorTotalNum) ? `, $${formatPrice(priorTotalNum)}` : ''})`
      : ''
    const whenLabel = secondsAgo != null
      ? (secondsAgo < 120 ? `${secondsAgo} seconds` : `${Math.round(secondsAgo / 60)} minutes`)
      : 'moments'
    showConfirm({
      title: 'Possible Duplicate Sale',
      message:
        `An identical sale${priorLabel} was completed on this register `
        + `${whenLabel} ago. If the customer's card was already charged, `
        + `do NOT ring it again — check the receipt in Orders first. `
        + `Ring it as a new sale anyway?`,
      confirmLabel: 'Ring New Sale',
      cancelLabel: 'Cancel',
      variant: 'default',
      onConfirm: handlers.onConfirm,
      onCancel: handlers.onCancel,
    })
  }

  // Recovery for /cart/commit price_drift on the cash / non-terminal
  // path. Fetches a fresh /cart/preview, stamps the new totals into
  // cartStore.preview (so buildCommitRequest's expected_totals picks
  // them up), then either:
  //   - Total moved noticeably: show a "Total Changed" confirm dialog
  //     so the cashier explicitly acknowledges the new amount before
  //     we commit (matters for cash so the drawer pop matches what
  //     the cashier expects to hand back).
  //   - Total matches: retry silently. The BE's drift check fires on
  //     ANY of {total, taxes, subtotal, discount_amount}, so a
  //     discount/subtotal/tax move that nets to the same total still
  //     trips it. Same applies to a transient BE race. The cashier
  //     doesn't need to see a dialog for "the customer pays the same
  //     amount as before" -- just retry.
  //
  // Loop guard: track silent retries via a ref so a BE that keeps
  // rejecting on the same fields surfaces a friendly error after
  // PRICE_DRIFT_MAX_SILENT_RETRIES attempts instead of recursing
  // forever. Reset on any successful commit response (handled in
  // processPaymentFlow's try block, before the partial/full branch).
  // Modal-close reset is implicit via component unmount.
  //
  // Why we don't use the terminal flow's handlePriceDriftRetry below:
  // that one is wired for `lastStartRef` (terminal-session state) and
  // retries via the /terminal-payments endpoint. The cash path commits
  // through /cart/commit, which means the retry needs a fresh
  // idempotency_key (buildCommitRequest mints one) and a fresh preview
  // (buildCommitRequest pulls it from cartStore). Two different
  // retry shapes; one handler each.
  const PRICE_DRIFT_MAX_SILENT_RETRIES = 2
  const handleCashPriceDriftRetry = async (
    method: PaymentMethod,
    overrideAmount: number | undefined,
    // Mirror the full processPaymentFlow signature so the retry can
    // carry the cashier's original intent (gift card, overpay) through
    // to BE -- otherwise the retry isn't the same payment, it's a
    // different one that the BE will reject for a different reason
    // and the cashier sees a confusing error chain.
    giftCardNum: string | undefined,
    giftCardIdArg: number | null | undefined,
    giftCardPinArg: string | undefined,
    confirmOverpayment: boolean | undefined,
    // Carried through so a drift retry chained off a confirmed
    // possible_duplicate doesn't re-trip the BE duplicate guard and
    // re-ask the cashier a question they already answered.
    confirmDuplicate: boolean | undefined,
  ) => {
    const oldTotalRaw = useCartStore.getState().preview?.totals?.total
    const oldTotal = oldTotalRaw != null
      ? Math.abs(parseFloat(String(oldTotalRaw)) || 0)
      : null

    let newTotal: number | null = null
    try {
      const v2Cart = useCartStore.getState()
      const previewBody = cartToRequest(v2Cart)
      const fresh = await fetchCartPreview(previewBody)
      // Stamp the fresh totals into cartStore so the retry's
      // buildCommitRequest -> expected_totals reads the up-to-date
      // values. Using applyPreview with matching fingerprints would
      // bypass the staleness guard; here we want to overwrite
      // unconditionally because we just fetched authoritatively.
      useCartStore.setState({
        preview: fresh,
        previewStatus: 'idle',
        previewedAt: Date.now(),
      })
      newTotal = Math.abs(parseFloat(String(fresh.totals?.total ?? 0)) || 0)
    } catch (err) {
      console.warn('[price_drift] preview refresh failed', err)
      // Network / BE error on the preview itself -- fall back to a
      // friendly retry prompt. No "restart transaction" wording;
      // the cart is intact and the cashier can simply try again.
      priceDriftRetryRef.current = 0
      setErrorMessage('Could not refresh totals. Please try again.')
      setStep('error')
      return
    }

    if (newTotal == null || !Number.isFinite(newTotal)) {
      priceDriftRetryRef.current = 0
      setErrorMessage('Could not refresh totals. Please try again.')
      setStep('error')
      return
    }

    const totalChanged = oldTotal != null && Math.abs(newTotal - oldTotal) >= 0.005

    if (totalChanged) {
      // Different number than the cashier just saw -- they need to
      // explicitly acknowledge before we charge the new amount. The
      // dialog path resets the silent-retry counter: this is a
      // cashier-acknowledged retry, not a silent one, and shouldn't
      // count toward the loop guard.
      priceDriftRetryRef.current = 0
      // Retry-amount logic. The cashier's original intent depends on
      // whether they were paying full, partial, or overpay-with-change:
      //   - Full payment (override undefined OR ~= oldTotal): they
      //     wanted to settle the bill in one shot. On retry, charge
      //     the new total -- the dialog says "Charge the new amount"
      //     and that's what they're confirming.
      //   - Partial (override < oldTotal): they intended to tender a
      //     specific amount that's LESS than the total. The total
      //     moving doesn't change that intent -- they still want to
      //     pay $X partial. Preserve the original override; the BE
      //     will accept it as a partial against the new total.
      //   - Overpay (override > oldTotal): cash overpay scenario --
      //     cashier handed over a large bill and the BE accepts the
      //     extra with confirm_overpayment=true. Preserve the
      //     override; cashTendered is still in the cashier's hand.
      //
      // Note: `newTotal` is captured in this closure (not a state
      // variable), so it survives React's stale-closure capture. We
      // can't rely on the captured `amountDue` here -- that's the
      // pre-refresh stale value.
      const isFullPayment =
        overrideAmount == null
        || (oldTotal != null && Math.abs(overrideAmount - oldTotal) < 0.005)
      const retryAmount = isFullPayment ? newTotal : overrideAmount
      showConfirm({
        title: 'Total Changed',
        message: isFullPayment
          ? `Total changed from $${formatPrice(oldTotal!)} to $${formatPrice(newTotal)}. Charge the new amount?`
          : `Total changed from $${formatPrice(oldTotal!)} to $${formatPrice(newTotal)}. Continue with your $${formatPrice(overrideAmount!)} tender?`,
        confirmLabel: 'Continue',
        cancelLabel: 'Cancel',
        variant: 'default',
        onConfirm: () => {
          void processPaymentFlow(
            method,
            retryAmount,
            giftCardNum,
            giftCardIdArg,
            giftCardPinArg,
            confirmOverpayment,
            confirmDuplicate,
          )
        },
        onCancel: () => {
          setStep('method')
        },
      })
      return
    }

    // Total matched. Drift was on a sub-component (subtotal/tax/
    // discount that net to the same total) or a transient BE race.
    // Retry silently -- the cashier shouldn't see a "Total Changed"
    // dialog for a total that didn't change. Loop guard prevents an
    // unconditionally-rejecting BE from recursing.
    if (priceDriftRetryRef.current >= PRICE_DRIFT_MAX_SILENT_RETRIES) {
      priceDriftRetryRef.current = 0
      setErrorMessage('Could not complete payment. Please try again.')
      setStep('error')
      return
    }
    priceDriftRetryRef.current += 1
    // Same-totals silent retry: preserve every original-call param so
    // the retry payload is byte-identical except for the freshly-
    // minted idempotency key. Anything less and a gift-card or
    // overpay-confirmed call retried here would lose its critical
    // context and fail with a different error than the original
    // drift.
    void processPaymentFlow(
      method,
      overrideAmount,
      giftCardNum,
      giftCardIdArg,
      giftCardPinArg,
      confirmOverpayment,
      confirmDuplicate,
    )
  }

  // Terminal-flow price-drift recovery: fetch the live receipt for
  // the new total, surface a confirm with old vs new, and on confirm
  // retry the terminal start with the new expected_total + same
  // idempotency_key. Sibling to handleCashPriceDriftRetry above; the
  // terminal path needs its own retry shape because it goes through
  // /terminal-payments rather than /cart/commit and has to preserve
  // the original idempotency_key across the retry.
  const handlePriceDriftRetry = async () => {
    const last = lastStartRef.current
    if (!last) {
      setErrorMessage('Total changed; please review the cart and try again.')
      setStep('error')
      resetTerminalState()
      return
    }
    let newTotal = 0
    try {
      const live = await v2api.get<{
        receipt?: { totals?: { total?: string | number }; amount_to_pay?: string | number }
        totals?: { total?: string | number }
        amount_to_pay?: string | number
      }>(v2(`/receipts/${last.receiptId}`))
      const liveTotal =
        live?.receipt?.totals?.total
        ?? live?.totals?.total
        ?? live?.receipt?.amount_to_pay
        ?? live?.amount_to_pay
      newTotal = Number(liveTotal ?? 0)
    } catch {
      // fall through to the error path below
    }
    if (!newTotal) {
      setErrorMessage('Total changed; please review the cart and try again.')
      setStep('error')
      resetTerminalState()
      return
    }
    const oldTotal = Number(last.body.expected_total ?? 0)
    showConfirm({
      title: 'Total Changed',
      message: `Total changed from $${formatPrice(oldTotal)} to $${formatPrice(newTotal)}. Charge the new amount?`,
      confirmLabel: 'Continue',
      cancelLabel: 'Cancel',
      onConfirm: () => {
        void retryStartWithBody({
          ...last.body,
          expected_total: newTotal,
        })
      },
      onCancel: () => {
        resetTerminalState()
        setStep('method')
      },
    })
  }

  // Commit-time price-drift recovery for the terminal flow. Reachable now
  // that recalled/fresh receipts commit-no-pay BEFORE the terminal session
  // starts: if the FE's expected_totals (from a stale /cart/preview)
  // disagree with the BE recompute, /cart/commit 409s with price_drift
  // before lastStartRef is set, so the start-session recovery
  // (handlePriceDriftRetry) can't see it. Sibling of the cash flow's
  // handleCashPriceDriftRetry: refresh /cart/preview, then on a changed
  // total confirm with the cashier and re-enter startTerminalFlow (which
  // re-commits with the fresh expected_totals); on an unchanged total
  // (sub-component drift / transient race) retry silently, bounded by the
  // shared loop guard.
  const handleTerminalCommitPriceDrift = async (
    method: PaymentMethod,
    // The cashier's tender intent captured at the failed attempt (before
    // the preview refresh below snaps paymentAmountInput). null = they left
    // "Amount to Pay" at the full balance. Preserved across the retry so a
    // partial card tender isn't upgraded to a full charge.
    originalOverride: number | null,
    // Carried through so a drift retry chained off a confirmed
    // possible_duplicate doesn't re-trip the BE duplicate guard.
    confirmDuplicate?: boolean,
  ) => {
    const oldTotalRaw = useCartStore.getState().preview?.totals?.total
    const oldTotal = oldTotalRaw != null
      ? Math.abs(parseFloat(String(oldTotalRaw)) || 0)
      : null

    let newTotal: number | null = null
    try {
      const v2Cart = useCartStore.getState()
      const fresh = await fetchCartPreview(cartToRequest(v2Cart))
      // Stamp authoritatively so the retry's buildCommitRequest ->
      // expected_totals reads the up-to-date numbers (mirrors
      // handleCashPriceDriftRetry).
      useCartStore.setState({
        preview: fresh,
        previewStatus: 'idle',
        previewedAt: Date.now(),
      })
      newTotal = Math.abs(parseFloat(String(fresh.totals?.total ?? 0)) || 0)
    } catch (err) {
      console.warn('[price_drift] terminal commit preview refresh failed', err)
      priceDriftRetryRef.current = 0
      setErrorMessage('Could not refresh totals. Please try again.')
      setStep('error')
      resetTerminalState()
      return
    }

    if (newTotal == null || !Number.isFinite(newTotal)) {
      priceDriftRetryRef.current = 0
      setErrorMessage('Could not refresh totals. Please try again.')
      setStep('error')
      resetTerminalState()
      return
    }

    const totalChanged = oldTotal != null && Math.abs(newTotal - oldTotal) >= 0.005

    // Preserve the cashier's tender intent across the retry (mirror
    // handleCashPriceDriftRetry): a FULL payment recharges the NEW total; a
    // PARTIAL keeps the originally-entered amount (the BE accepts it as a
    // partial against the new total). isFullPayment is true when the cashier
    // left the field at the full balance (null) or typed ~= the old total.
    const isFullPayment =
      originalOverride == null
      || (oldTotal != null && Math.abs(originalOverride - oldTotal) < 0.005)
      // Drift can LOWER the total below the cashier's partial (old 100,
      // entered 90, new 80). A card can't overpay, so a partial that now
      // covers the new balance becomes a full charge of the new total --
      // otherwise the retry re-enters startTerminalFlow past the upfront
      // overpayment guard (handleSelectMethod) and hard-fails with
      // overpayment_unconfirmed after the cashier already confirmed.
      || originalOverride >= newTotal - 0.005
    const retryTender = isFullPayment ? newTotal : originalOverride!

    if (totalChanged) {
      // Cashier-acknowledged retry -- doesn't count toward the silent guard.
      priceDriftRetryRef.current = 0
      showConfirm({
        title: 'Total Changed',
        message: isFullPayment
          ? `Total changed from $${formatPrice(oldTotal!)} to $${formatPrice(newTotal)}. Charge the new amount?`
          : `Total changed from $${formatPrice(oldTotal!)} to $${formatPrice(newTotal)}. Continue with your $${formatPrice(originalOverride!)} tender?`,
        confirmLabel: 'Continue',
        cancelLabel: 'Cancel',
        onConfirm: () => {
          void startTerminalFlow(method, retryTender, confirmDuplicate)
        },
        onCancel: () => {
          resetTerminalState()
          setStep('method')
        },
      })
      return
    }

    // Total unchanged: drift was on a sub-component, or a transient BE
    // race. Retry silently so the cashier isn't shown a "Total Changed"
    // dialog for a total that didn't move; the loop guard stops an
    // unconditionally-rejecting BE from recursing forever.
    if (priceDriftRetryRef.current >= PRICE_DRIFT_MAX_SILENT_RETRIES) {
      priceDriftRetryRef.current = 0
      setErrorMessage('Could not complete payment. Please try again.')
      setStep('error')
      resetTerminalState()
      return
    }
    priceDriftRetryRef.current += 1
    void startTerminalFlow(method, retryTender, confirmDuplicate)
  }

  // Dispatch on V2ApiError code:
  //   - `overpayment_unconfirmed` → hard-fail to the error step (no
  //     confirm dialog; card overpayment is unrecoverable).
  //   - `price_drift` → confirm-and-retry with refreshed total.
  //   - `register_busy` / `processor_unsupported` → friendly error step.
  //   - `outside_sale_hours` → sale-hours error step (no retry).
  //   - anything else → generic error step.
  const handleStartError = (error: unknown) => {
    // Checked before the lastStartRef gate: the commit-no-pay 409s
    // before startTerminalSession sets the ref, and start_session can
    // 409 with it set -- both land here.
    if (error instanceof V2ApiError && error.code === 'outside_sale_hours') {
      const copy = saleHoursCopy(error, error.detail || error.message || 'Sales are not allowed at this time.')
      setErrorIsSaleHours(true)
      setSaleHoursStoreTime(copy.storeTime)
      setErrorMessage(copy.message)
      setStep('error')
      resetTerminalState()
      return
    }
    if (error instanceof V2ApiError && lastStartRef.current) {
      const code = error.code
      if (code === 'overpayment_unconfirmed') {
        // Card overpayment is hard-blocked upfront in handleSelectMethod;
        // reaching here means state drifted between method-pick and the
        // BE call (cart mutated, parallel partial-payment, BE-side
        // recalc). Surface the BE message on the error step -- no
        // "Continue anyway" path, since a card swipe can't return cash
        // change. Cashier acks the error, returns to the method screen
        // via the existing handleRetry button, and reduces the amount.
        setErrorMessage(
          error.detail
            || error.message
            || 'Card payment cannot exceed receipt balance.',
        )
        setStep('error')
        resetTerminalState()
        return
      }
      if (code === 'price_drift') {
        void handlePriceDriftRetry()
        return
      }
      if (code === 'register_busy') {
        // Capture the BE-supplied {busy_session_id, busy_receipt_id}
        // (envelope.extra, set by ops_terminal_payment.start_session).
        // busy_receipt_id routes recovery two ways (below) -- it may
        // differ from the receipt the cashier is on because the busy
        // guard scopes by register_id only: SAME receipt -> reattach to
        // our own in-flight payment (Resume); DIFFERENT receipt -> the
        // error step's "Cancel previous & retry" for a genuine orphan
        // (captured before resetTerminalState() nulls our state, so the
        // button has the orphan's ids). Falls back to plain "Try Again"
        // if extra is missing (older BE) or malformed.
        //
        // Strict typeof checks rather than String()/Number() coercion:
        // String(undefined) is "undefined" (truthy garbage), Number(null)
        // is 0 (Finite!), etc. Coercing would let malformed payloads
        // produce truthy IDs that the cancel call would then try to
        // address against the BE and 404. Refuse those at capture time
        // so the FE only ever surfaces the recovery button when we
        // have well-typed values to act on.
        const extra = error.extra
        const rawSid = extra && typeof extra === 'object'
          ? (extra as Record<string, unknown>).busy_session_id
          : undefined
        const rawRid = extra && typeof extra === 'object'
          ? (extra as Record<string, unknown>).busy_receipt_id
          : undefined
        const busySid = typeof rawSid === 'string' && rawSid.length > 0 ? rawSid : null
        const busyRid = typeof rawRid === 'number' && Number.isFinite(rawRid) && rawRid > 0
          ? rawRid
          : null

        // RESUME vs. cancel. busy_receipt_id is the BE's exact
        // discriminator: the busy guard scopes by register_id only, so
        // when the in-flight session is on the very receipt the cashier
        // is paying, it's THEIR OWN payment that a reload (or transient
        // blip) detached the FE from -- not an orphan to cancel. The
        // session is still live on the BE (it keeps polling the terminal,
        // and resume_pending_sessions re-drives it within
        // _RESUME_AFTER_SEC), so we just reattach: seed the session id and
        // let the existing polling query + watcher take over, dropping the
        // cashier back onto the live waiting screen where the reload left
        // off. Nothing is charged here -- GET polling is read-only, and
        // the BE's (receipt_id, idempotency_key) replay guard makes even a
        // stray re-Pay a no-op rather than a double charge. Read
        // currentReceiptId fresh (not the handler's closure) so the match
        // reflects the receipt as of now.
        const liveReceiptId = usePOSStore.getState().currentReceiptId
        if (busySid && busyRid && busyRid === liveReceiptId) {
          handledSessionIdRef.current = null
          setErrorMessage('')
          setBusyTerminalSessionId(null)
          setBusyTerminalReceiptId(null)
          setTerminalReceiptId(busyRid)
          setStep('card-terminal')
          setTerminalSessionId(busySid)
          showToast({
            type: 'info',
            title: 'Payment resumed',
            message: 'Reconnected to the card payment already in progress on this register.',
          })
          return
        }

        // Different receipt -> a genuinely abandoned session on a receipt
        // the cashier already moved on from. Surface the one-click
        // cancel-and-retry recovery (handleCancelBusyAndRetry) on the
        // error step.
        if (busySid && busyRid) {
          setBusyTerminalSessionId(busySid)
          setBusyTerminalReceiptId(busyRid)
        }
        setErrorMessage(
          error.detail
            || error.message
            || 'Another card payment is already in progress on this register. Cancel it before starting a new one.',
        )
        setStep('error')
        // Preserve lastStartRef so handleCancelBusyAndRetry can replay
        // the original start (same body + same idempotency_key) after
        // it cancels the orphan. Without preserveLastStart the ref
        // gets nulled here and the retry path silently degrades to
        // handleRetry() -- defeating the one-click recovery UX.
        resetTerminalState({ preserveLastStart: true })
        return
      }
      if (code === 'processor_unsupported') {
        setErrorMessage(
          "This register's card terminal isn't supported by the v2 flow yet — use legacy POS for card payments here.",
        )
        setStep('error')
        resetTerminalState()
        return
      }
    }

    // Fallback path. If the BE returned a structured response that
    // isn't a v2 envelope (e.g. legacy {error: "..."} or a Tornado
    // 400 with a body field), reach into the axios response so the
    // cashier sees the real reason instead of the generic
    // "Request failed with status code 400".
    let errorMsg = 'Could not start the card payment.'
    if (error instanceof V2ApiError) {
      errorMsg = error.detail || error.message || errorMsg
    } else if (error && typeof error === 'object' && 'response' in error) {
      const ax = error as {
        response?: {
          status?: number
          data?: {
            error?: string
            message?: string
            detail?: string
            code?: string
          } | string
        }
        message?: string
      }
      const data = ax.response?.data
      if (typeof data === 'string' && data.trim()) {
        errorMsg = data
      } else if (data && typeof data === 'object') {
        errorMsg = data.error || data.message || data.detail || ax.message || errorMsg
      } else {
        errorMsg = ax.message || errorMsg
      }
    } else if (error instanceof Error) {
      errorMsg = error.message
    }
    setErrorMessage(errorMsg)
    setStep('error')
    resetTerminalState()
  }

  // Final-state dispatcher for the polling query. Called once per
  // session.session_id (deduped via handledSessionIdRef) when the
  // backend reports a non-pending status.
  const handleTerminalSessionFinal = async (session: TerminalPaymentSession) => {
    handledSessionIdRef.current = session.session_id

    if (session.status === 'approved' && session.receipt) {
      finalizeApprovedSession(session.receipt as Receipt)
      return
    }

    if (session.status === 'cancelled') {
      resetTerminalState()
      setStep('method')
      return
    }

    // timeout / error — race-safe re-check before showing an error.
    // The customer may have been charged at the terminal even though
    // our backend lost the result. (Decline is a clean "card refused"
    // with no charge; skip the reconcile.)
    if (session.status === 'timeout' || session.status === 'error') {
      if (terminalReceiptId) {
        const recovered = await reconcileAfterTerminalFailure(
          terminalReceiptId,
          session.session_id,
        )
        if (recovered) {
          finalizeApprovedSession(recovered)
          return
        }
      }
    }

    setErrorMessage(
      session.error_message
        || (session.status === 'declined' ? 'Card declined.' : 'Payment failed on the terminal.'),
    )
    setStep('error')
    resetTerminalState()
  }

  // Watch the polling query: when status flips off `pending`, dispatch
  // exactly once for that session. Tracks by session_id so a stale
  // re-render doesn't fire the same final state twice.
  useEffect(() => {
    if (!terminalSession) return
    if (terminalSession.status === 'pending') return
    if (handledSessionIdRef.current === terminalSession.session_id) return
    void handleTerminalSessionFinal(terminalSession)
    // We deliberately depend on a small slice of the session, not the
    // whole object — handler is read out via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminalSession?.session_id, terminalSession?.status])

  // One-click recovery from the register_busy 409. Cancels the orphan
  // PENDING PaymentSession the BE pointed us at (via the error's
  // extra.busy_session_id + extra.busy_receipt_id), waits for the BE
  // to actually transition the row to a terminal state, then -- if
  // it's safe to do so -- replays the original start with the SAME
  // idempotency key.
  //
  // Why the wait: cancel_session only sets cancel_requested=1; the
  // status transition (PENDING -> CANCELLED) happens when a driver
  // task picks up the flag. For orphan sessions the BE's cancel
  // handler force-dispatches a fresh driver task, but the FE's
  // cancel response is captured BEFORE that task runs -- typically
  // showing status='pending'. If we retried immediately on a
  // pending response, two failure modes appear:
  //   1. The orphan is still PENDING in the DB when the retry's
  //      register_busy guard runs -- 409s with the same session id.
  //   2. A parallel driver (the orphan's, racing the force-dispatched
  //      task) finalizes as APPROVED while we retry. Customer was
  //      charged at the terminal but our row may end up CANCELLED
  //      (loud error logged at _finalize_approved), and the retry's
  //      fresh charge would double-bill the customer.
  // Polling GET on the session bridges the gap: we wait until the
  // BE has decided the orphan's fate, then act on that.
  //
  // Decision matrix on the settled state:
  //   - approved      => customer was charged on the orphan. DO NOT
  //                      auto-retry. Toast warning so the cashier
  //                      verifies the orphan's receipt before any
  //                      further action. The orphan's receipt may
  //                      differ from the cashier's current receipt
  //                      (busy guard scopes by register only) so we
  //                      can't route to finalizeApprovedSession.
  //   - cancelled     => clean cancel, safe to retry.
  //   - declined / timeout / error => orphan resolved unfavorably,
  //                      no charge; safe to retry.
  //   - poll timeout  => BE took longer than 5s to settle. Don't
  //                      retry blindly; toast and let the cashier
  //                      decide. Most likely the dispatched task
  //                      crashed or DB is slow; another click is
  //                      the right recovery.
  const handleCancelBusyAndRetry = async () => {
    if (
      !busyTerminalSessionId
      || !busyTerminalReceiptId
      || cancelTerminalPayment.isPending
      || verifyingCancelRef.current
    ) return
    // Synchronous re-entry latch. cancelTerminalPayment.isPending
    // covers the mutateAsync window but flips false the moment the
    // cancel resolves -- before our polling loop completes. Without
    // this ref, a double-click could fire two parallel cancel+poll
    // sequences.
    verifyingCancelRef.current = true
    setIsVerifyingCancel(true)

    try {
      let cancelled: TerminalPaymentSession
      try {
        cancelled = await cancelTerminalPayment.mutateAsync({
          receiptId: busyTerminalReceiptId,
          sessionId: busyTerminalSessionId,
          // FE asserts "this session is presumed orphan, please
          // re-dispatch" -- the BE liveness guard
          // (should_force_dispatch) independently verifies via the
          // session's updated_at heartbeat and refuses for live
          // sessions. So this is safe to set even though the FE
          // can't independently prove orphan-ness; the BE won't
          // honor it on a live driver. Without the flag, orphan
          // recovery would wait up to _RESUME_AFTER_SEC (60s) for
          // the reaper to re-dispatch.
          forceDispatch: true,
        })
      } catch (error: unknown) {
        const msg =
          error instanceof V2ApiError
            ? error.detail || error.message
            : error instanceof Error
              ? error.message
              : 'Could not cancel the previous payment.'
        showToast({ type: 'error', title: 'Cancel Failed', message: msg })
        return
      }

      // Wait for the BE to settle the session. Mirrors
      // useTerminalPaymentSession's POLL_INTERVAL_MS (500ms) for
      // consistency with the in-flight polling cadence. 5s total
      // timeout: ample for the dispatched _finalize_cancelled
      // path (~100ms typical) but bounded so we don't strand the
      // cashier on an indefinite spinner if the BE driver crashes.
      let settled: TerminalPaymentSession = cancelled
      if (settled.status === 'pending') {
        const deadlineAt = Date.now() + 5000
        while (Date.now() < deadlineAt) {
          await new Promise((resolve) => setTimeout(resolve, 500))
          try {
            const env = await v2api.get<{ data: TerminalPaymentSession }>(
              v2(`/receipts/${busyTerminalReceiptId}/terminal-payments/${busyTerminalSessionId}`),
            )
            if (env.data.status !== 'pending') {
              settled = env.data
              break
            }
          } catch {
            // Transient fetch error during poll -- continue trying.
            // The cancel call already succeeded; we're just waiting
            // for the BE to commit the status transition.
          }
        }
        if (settled.status === 'pending') {
          showToast({
            type: 'error',
            title: 'Cancel taking longer than expected',
            message:
              'The previous terminal payment is still being cancelled on the backend. '
              + 'Wait a moment and try again.',
          })
          return
        }
      }

      // Approved-during-cancel race. Customer's card was charged on
      // the orphan; auto-retry would double-charge. Toast and stop.
      if (settled.status === 'approved') {
        showToast({
          type: 'error',
          title: 'Previous payment was approved',
          message:
            'The previous terminal payment completed on the customer\'s card before we could cancel it. '
            + 'Verify the receipt before retrying to avoid double-charging.',
        })
        setBusyTerminalSessionId(null)
        setBusyTerminalReceiptId(null)
        return
      }

      // timeout / error are AMBIGUOUS terminal states -- the BE
      // doesn't actually know whether the customer was charged. The
      // normal terminal flow (handleTerminalSessionFinal) reconciles
      // these via reconcileAfterTerminalFailure, which re-reads the
      // receipt + session to detect whether the charge actually
      // landed despite the backend losing the result mid-handshake.
      // The same hazard exists for the busy-session recovery path:
      // if the orphan's driver was mid-tap when it crashed and the
      // terminal subsequently approved, the BE row may show
      // timeout/error while the customer's card was charged. Auto-
      // retrying without reconciling would re-charge the customer.
      //
      // Reconcile against the ORPHAN'S receipt id (busyTerminal-
      // ReceiptId), not the cashier's current receipt -- the busy
      // guard scopes by register_id only, so the orphan may be on a
      // different receipt the cashier abandoned previously. If the
      // reconciler returns non-null (charge landed on the orphan's
      // receipt), warn and stop -- same shape as the approved-race
      // branch above. We deliberately do NOT route through
      // finalizeApprovedSession even when the orphan IS on the
      // cashier's current receipt: that path mutates terminal state
      // (lastReceipt, completeAction print, etc.) under the
      // assumption that the cashier just successfully finished a
      // fresh payment, which isn't true here -- the cashier was
      // recovering from a stuck previous attempt and the charge they
      // discover via reconciliation needs manual verification.
      if (settled.status === 'timeout' || settled.status === 'error') {
        const recovered = await reconcileAfterTerminalFailure(
          busyTerminalReceiptId,
          busyTerminalSessionId,
        )
        if (recovered != null) {
          showToast({
            type: 'error',
            title: 'Previous payment may have charged the card',
            message:
              'The previous terminal payment ended in an ambiguous state, '
              + 'but reconciliation shows the customer\'s card was charged. '
              + 'Verify the receipt before retrying to avoid double-charging.',
          })
          setBusyTerminalSessionId(null)
          setBusyTerminalReceiptId(null)
          return
        }
        // Reconciler returned null -- receipt is unpaid, session
        // didn't flip to approved. Truly no charge; safe to retry.
      }

      // settled.status is cancelled, declined, or a reconciled-safe
      // timeout/error -- no charge on the orphan, safe to retry.
      // Clear busy state regardless of the replay outcome so the
      // button doesn't linger if the cashier opts to back out
      // instead of retrying.
      setBusyTerminalSessionId(null)
      setBusyTerminalReceiptId(null)
      const last = lastStartRef.current
      if (last) {
        // Replay the original start with the same body (same
        // idempotency_key, so a quick re-trigger from a polling
        // driver can't double-charge). retryStartWithBody handles
        // its own error routing -- a fresh failure on the replay
        // falls back to the regular error step.
        void retryStartWithBody(last.body)
      } else {
        // No replay context (modal mid-recover, or lastStartRef was
        // cleared elsewhere). Cancel was the win here; route the
        // cashier back to the method screen to pick a fresh payment.
        handleRetry()
      }
    } finally {
      verifyingCancelRef.current = false
      setIsVerifyingCancel(false)
    }
  }

  // Cancel an in-flight terminal session. Best-effort — the customer
  // may have approved on the terminal in the same tick, in which case
  // the DELETE response comes back as `approved` and the dispatcher
  // routes through `finalizeApprovedSession`.
  const handleCancelTerminal = async () => {
    if (!terminalReceiptId || !terminalSessionId) return
    try {
      const session = await cancelTerminalPayment.mutateAsync({
        receiptId: terminalReceiptId,
        sessionId: terminalSessionId,
      })
      void handleTerminalSessionFinal(session)
    } catch (error: unknown) {
      const msg =
        error instanceof V2ApiError
          ? error.detail || error.message
          : error instanceof Error
            ? error.message
            : 'Could not cancel the payment.'
      showToast({ type: 'error', title: 'Cancel Failed', message: msg })
    }
  }

  const handleCashPayment = () => {
    if (isProcessing) return
    if (isRefundMode) {
      // Cap to the cash share of the source payments. For a $30 refund
      // of a $20 visa + $10 cash sale, this sends $10 to cash and lets
      // the cashier pick another method for the remaining $20. BE
      // expects negative amount for refund payments.
      const refundAmount = -refundWireAmount(selectedMethod!)
      processPaymentFlow(selectedMethod!, refundAmount)
      return
    }
    const override = resolveOverrideAmount()
    const wireAmount = override ?? amountDue
    if (cashTenderedNum < wireAmount) {
      showToast({
        type: 'error',
        title: 'Insufficient Amount',
        message: `Cash tendered must be at least $${formatPrice(wireAmount)}`,
      })
      return
    }
    // For a full final tender where the customer handed over more than
    // the receipt's outstanding balance, send the actual cashTendered
    // amount to the BE so v1 pay() can record both the CASH JT (at the
    // tendered amount) and the CHANGE JT (for the difference). Without
    // this, the BE only ever sees `wireAmount` and never produces a
    // CHANGE row -- the cashier's drawer reconciliation is fine
    // (collected = total) but the receipt history loses the "you paid
    // $170, got $1.27 back" detail and the change-due summary is empty
    // even though physical cash changed hands.
    //
    // Partial tenders (cashier explicitly typed a smaller Amount than
    // amountDue) keep the old wireAmount behavior because v1 pay()
    // doesn't compute change unless amount_to_pay goes <= 0 after the
    // payment lands -- a partial overage stays in `wireAmount` no
    // matter what we send.
    const isFullTender = !override || override >= amountDue - 0.005
    const overpayBy = cashTenderedNum - wireAmount
    const sendAmount =
      isFullTender && overpayBy > 0.005
        ? cashTenderedNum
        : (override ?? undefined)
    // Typo guard — see CASH_OVERPAY_LIMIT comment. When the tender is
    // more than $100 over the wire amount we ask the cashier to
    // confirm rather than committing silently. Most >$100 overages are
    // mis-keys ($21 → $1000); the rare legitimate case (huge bill,
    // small purchase) just costs one extra click.
    if (overpayBy > CASH_OVERPAY_LIMIT + 0.005) {
      showConfirm({
        title: 'Confirm Large Tender',
        message: `Cash tendered ($${formatPrice(cashTenderedNum)}) is $${formatPrice(overpayBy)} over the amount due. Change due will be $${formatPrice(overpayBy)}. Continue?`,
        confirmLabel: 'Confirm Tender',
        cancelLabel: 'Re-enter',
        // Pass confirm_overpayment so the BE bypasses its $100 cash gate.
        // Without this flag the BE rejects with overpayment_unconfirmed
        // and the cashier's confirmation here is decorative.
        onConfirm: () => processPaymentFlow(
          selectedMethod!, sendAmount, undefined, undefined, undefined, true,
        ),
      })
      return
    }
    processPaymentFlow(selectedMethod!, sendAmount)
  }

  const handleCardPayment = () => {
    if (isProcessing) return
    if (isRefundMode) {
      const refundAmount = -refundWireAmount(selectedMethod!)
      processPaymentFlow(selectedMethod!, refundAmount)
      return
    }
    const override = resolveOverrideAmount()
    processPaymentFlow(selectedMethod!, override ?? undefined)
  }

  // Online Card confirm step: fire the gateway credit. Refund-only by
  // construction (the method never renders in sale mode). The BE runs
  // the Moneris refund first and only books the JT if it approves, so
  // a decline surfaces on the error step with nothing recorded.
  const handleOnlineCardPayment = () => {
    if (isProcessing || !selectedMethod) return
    const refundAmount = -refundWireAmount(selectedMethod)
    processPaymentFlow(selectedMethod, refundAmount)
  }

  // Standalone terminal declined / didn't respond. Nothing has been
  // committed at this point (the commit only happens on the Approved
  // button), so this just backs out to the method screen. The toast
  // states that explicitly -- the field failure mode was cashiers not
  // knowing whether the POS had recorded anything after a terminal
  // hiccup, and re-ringing the sale "to be safe".
  const handleCardDeclined = () => {
    if (isProcessing) return
    showToast({
      type: 'info',
      title: 'No Sale Recorded',
      message: 'Nothing was charged in the POS. Retry the card or choose another payment method.',
    })
    setStep('method')
  }

  const handleCheckGiftCardBalance = async () => {
    if (!giftCardNumber.trim() || checkGiftCardBalance.isPending) return

    setGiftCardLookupError(null)

    // Block paying with a card that's also being sold on this receipt —
    // the card hasn't been activated yet, so its balance at redeem time
    // would be wrong (or zero). v1 enforced this server-side; v2 doesn't.
    if (currentReceiptId) {
      const cached = queryClient.getQueryData<Receipt>(receiptKeys.detail(currentReceiptId))
      const target = giftCardNumber.trim()
      const isOnReceipt = !!cached?.items?.some(
        (it) => (it as { gift_card_number?: string | null }).gift_card_number === target,
      )
      if (isOnReceipt) {
        setGiftCardLookupError(
          "Can't pay with a card you're selling on this receipt. Use a different card.",
        )
        return
      }
    }

    try {
      const result = await checkGiftCardBalance.mutateAsync(giftCardNumber.trim())
      setGcNeedsRecheck(false)
      setGiftCardBalance(extractGiftCardBalance(result.card))
      setGiftCardId(extractGiftCardId(result.card))
      setGiftCardIsBirchmount(isBirchmountCard(result.card))
    } catch (err: unknown) {
      // V2ApiError carries the BE envelope's `error` on `.detail`; fall
      // through to axios-shape and Error message just in case.
      const e = err as {
        code?: string
        detail?: string
        message?: string
        response?: { data?: { code?: string; error?: string } }
      }
      const code = e?.code ?? e?.response?.data?.code
      const beError = e?.detail ?? e?.response?.data?.error
      let inline: string
      if (code === 'gift_card_not_found') {
        inline = beError || 'Gift card not found.'
      } else if (code === 'birchmount_unavailable') {
        inline = 'Gift card service is temporarily unavailable. Please try again.'
      } else {
        inline = beError || e?.message || 'Could not retrieve gift card balance.'
      }
      setGiftCardLookupError(inline)
    }
  }

  // Defensive: BE looks up the GiftCard row by id for type-251 redemption.
  // sync_from_birchmount always flushes the row before returning balance,
  // so id should always be present when balance is — but if the contract
  // ever drifts (or a non-Birchmount lookup path appears), silently
  // omitting `gift_card_id` from the body produces a confusing
  // "No gift card with id #None found." 500. Block the Apply path
  // upfront so the cashier sees a clear message instead.
  const giftCardMissingId = giftCardBalance !== null && giftCardId === null
  // Birchmount cards require a cashier-entered PIN at redeem time. Block
  // Apply (and refund-onto-card) until the cashier has entered something;
  // BE re-validates against Birchmount's stored PIN, but failing fast
  // here saves a round-trip and surfaces the requirement inline.
  const giftCardPinRequired = giftCardIsBirchmount && !giftCardPin.trim()

  const handleGiftCardPayment = () => {
    if (!giftCardNumber.trim() || giftCardBalance === null || isProcessing) return
    if (giftCardMissingId) {
      setGiftCardLookupError(
        "This card can't be redeemed at the register. Re-check the balance, or contact support if it persists.",
      )
      return
    }
    if (giftCardPinRequired) {
      setGiftCardLookupError('Enter the gift card PIN to redeem.')
      return
    }
    const pinForPayload = giftCardIsBirchmount ? giftCardPin.trim() : undefined
    if (isRefundMode) {
      // Refund onto a gift card: cap to the per-method remaining and
      // also to the card's available balance (in case the cashier is
      // crediting back a card that's since been redeemed). BE expects
      // a negative amount for refund payments.
      const refundAmount = -refundWireAmount(selectedMethod!)
      processPaymentFlow(
        selectedMethod!,
        refundAmount,
        giftCardNumber.trim(),
        giftCardId,
        pinForPayload,
      )
      return
    }
    // Override = min(gift card balance, cashier-entered amount). The
    // gift card never tenders more than its balance, AND never more
    // than what the cashier asked to apply. processPaymentFlow then
    // caps against the backend's outstanding balance (Math.min) for
    // safety: a $50 card with cashier entering $30 on a $200 ticket
    // applies $30 (cashier-bounded); a $50 card on a $5 ticket
    // applies $5 (backend-bounded).
    const cashierOverride = resolveOverrideAmount()
    const cap = cashierOverride !== null ? Math.min(giftCardBalance, cashierOverride) : giftCardBalance
    processPaymentFlow(
      selectedMethod!,
      cap ?? undefined,
      giftCardNumber.trim(),
      giftCardId,
      pinForPayload,
    )
  }

  // Cancel a partial payment via DELETE v2('/receipts/{id}/payments/{jt_id}').
  // BE routes the refund by payment type and returns a `refund` disposition
  // the cashier UI uses to drive its post-cancel toast:
  //   - completed   System ran the refund (Moneris void / Birchmount /
  //                 SpringBig). Spinner-only flow; no manual step.
  //   - manual_cash Cash JT voided. Toast prompts "Hand back $X".
  //   - manual_card Card JT voided with no integrated refund path. The
  //                 confirm modal warns the cashier to refund on the
  //                 standalone terminal physically BEFORE clicking
  //                 Confirm — system state lags the physical refund.
  // Complete a return whose whole remaining balance is a store-funded
  // reward. There is no tender to take here -- the BE books the reward
  // reversal itself and closes the receipt (and returns the member's
  // points), refusing if any original method could still pay the
  // customer instead. Mirrors the fully-settled tail of
  // processPaymentFlow: exit refund mode, reset the register, land on
  // the complete screen, honour the auto-print config.
  const handleSettleReward = () => {
    if (!currentReceiptId || isAnyPending || settleReward.isPending) return
    const receiptId = currentReceiptId
    const amount = absoluteAmountDue

    showConfirm({
      title: 'Complete Refund?',
      message:
        `$${formatPrice(amount)} of this sale was paid with a reward, which can't be given back as cash or to a card. `
        + 'Completing credits it back to the store and returns the customer\'s points. No money leaves the till.',
      confirmLabel: 'Complete Refund',
      cancelLabel: 'Back',
      onConfirm: async () => {
        try {
          const receipt = await settleReward.mutateAsync({ receiptId })
          setLastReceipt(receipt as LastReceipt)
          updateRefundReceipt(receipt)
          exitRefundMode()
          queryClient.invalidateQueries({ queryKey: ['receipts', 'refund-search'] })
          if (selectedCustomer?.id) {
            queryClient.invalidateQueries({
              queryKey: customerSpringbigKey(selectedCustomer.id),
            })
          }
          clearReceipt()
          setStep('complete')
          captureCheckoutCompleted({
            receipt,
            receiptId,
            isRefund: true,
            path: 'tender',
            customerId: selectedCustomer?.id ?? null,
          })
          if (completeAction === 'print') {
            printReceipt.mutate({ receiptId })
          }
          showToast({
            type: 'success',
            title: 'Refund Completed',
            message: `$${formatPrice(amount)} reward credited back to the store; the customer's points have been returned.`,
          })
        } catch (err: unknown) {
          showToast({
            type: 'error',
            title: 'Could Not Complete Refund',
            // The BE's refusals carry the actionable detail (how much a
            // real method can still pay back, how much of the balance
            // the reward actually covers), so surface it verbatim.
            message: v2ErrorText(err, 'Could not complete this refund.'),
          })
        }
      },
    })
  }

  const handleVoidPayment = (payment: { id?: number; payment_type?: number; payment_method?: string | null; amount?: string | number; last4?: string; transaction_id?: string | null }) => {
    if (!currentReceiptId) return
    if (!payment.id) return  // need a JT id to cancel by reference
    const jtId = payment.id
    const amount = Math.abs(Number(payment.amount ?? 0))
    if (!Number.isFinite(amount) || amount <= 0) return

    const isCash = payment.payment_type === PAYMENT_TYPES.CASH
    const isCard = (
      payment.payment_type === PAYMENT_TYPES.VISA
      || payment.payment_type === PAYMENT_TYPES.MASTER_CARD
      || payment.payment_type === PAYMENT_TYPES.AMEX
      || payment.payment_type === PAYMENT_TYPES.DEBIT
    )
    // Per-JT signal: the JT's own processor reference is the truth,
    // not the current register's processor. A receipt can mix a
    // standalone Visa leg + a Moneris Visa leg, and the cancel flow
    // for each must follow the JT that recorded it -- the cashier may
    // even have switched registers between them.
    const isProcessorBacked = isCard && !!payment.transaction_id
    const processor = selectedRegister?.payment_endpoint || ''
    const isMonerisCard = isProcessorBacked && processor === 'moneris_go'

    // Single shared mutation runner -- used by both the immediate
    // (Moneris) path and the confirm-first (cash / standalone card)
    // path so toast handling stays in one place.
    const runCancel = async () => {
      try {
        const result = await voidPayment.mutateAsync({ receiptId: currentReceiptId, jtId })
        const receipt = result.data?.receipt
        if (!receipt) {
          showToast({ type: 'error', title: 'Cancel Failed', message: 'Backend did not return updated receipt.' })
          return
        }
        setLastReceipt(receipt as LastReceipt)
        if (isRefundMode) updateRefundReceipt(receipt)
        const remaining = Number(receipt.amount_to_pay ?? 0)
        if (receipt.totals) {
          const t = receipt.totals
          const totals = t as unknown as Record<string, unknown>
          setServerTotals({
            subtotal: Number(t.subtotal ?? 0),
            taxes: extractTaxes(totals),
            deposit: Number(totals.deposit_amount ?? totals.recycling_amount ?? 0),
            total: Number(t.total ?? 0),
            amountToPay: remaining,
          })
        }
        setAmountDue(remaining)

        // Toast copy keyed off the BE's actual disposition (the FE's
        // pre-confirm hint can be wrong if the JT's processor differs
        // from the current register's processor).
        const refund = result.data.refund
        if (refund === 'manual_cash') {
          showToast({
            type: 'success',
            title: 'Cash Payment Cancelled',
            // Direction flips on a refund: the tender being cancelled
            // was money going OUT to the customer, so undoing it means
            // the cash comes back to the till.
            message: isRefundMode
              ? `Return $${formatPrice(amount)} to the till if it was already handed over.`
              : `Hand back $${formatPrice(amount)} to the customer.`,
          })
        } else if (refund === 'manual_card') {
          showToast({
            type: 'success',
            title: 'Card Payment Cancelled',
            message: isRefundMode
              ? `If the card refund of $${formatPrice(amount)} already went through, reverse it on the standalone terminal.`
              : `Refund $${formatPrice(amount)} manually on the standalone terminal.`,
          })
        } else {
          showToast({
            type: 'success',
            title: 'Payment Refunded',
            message: `$${formatPrice(amount)} refunded${payment.last4 ? ` to card ending ${payment.last4}` : ''}.`,
          })
        }
      } catch (err: unknown) {
        const code = v2ErrorCode(err)
        const detail = v2ErrorText(err, '')
        let message: string
        if (code === 'payment_not_found') {
          message = 'Payment was already cancelled. Receipt will refresh.'
        } else if (code === 'birchmount_unavailable') {
          message = 'Gift card service is temporarily unavailable. Please try again.'
        } else if (code === 'processor_void_failed') {
          // Moneris refused the void. The JT was NOT voided; cashier
          // can retry, refund manually on the terminal, or escalate.
          message = detail || 'Card refund failed on terminal. Refund manually or contact support.'
        } else {
          message = detail || 'Could not cancel this payment.'
        }
        showToast({ type: 'error', title: 'Cancel Failed', message })
      }
    }

    // Moneris Go cards: BE drives the void atomically inside the DELETE
    // (no manual step), so skip the confirm modal and go straight to
    // spinner + DELETE. Same for non-card automatic paths (Birchmount
    // gift card, SpringBig) which also have no manual step.
    if (isMonerisCard) {
      runCancel()
      return
    }

    // Confirm-first for everything else: cash (cashier physically hands
    // back cash) and non-Moneris cards (cashier refunds on the
    // standalone terminal first, THEN clicks Confirm).
    let preConfirmMessage: string
    if (isRefundMode) {
      // Cancelling a REFUND tender, not a payment: the money was going
      // out to the customer. Only the cashier knows whether it actually
      // left the till, and that is exactly the decision this dialog is
      // asking them to make -- so say it plainly rather than reusing the
      // sale-side copy, which reads backwards here.
      preConfirmMessage = `Cancel the ${payment.payment_method || 'refund'} of $${formatPrice(amount)} on this return? If the customer already received it, collect it back first — cancelling here only removes the record.`
    } else if (isCash) {
      preConfirmMessage = `Cancel the cash payment of $${formatPrice(amount)}? You'll need to hand $${formatPrice(amount)} back to the customer.`
    } else if (isCard) {
      preConfirmMessage = `Cancel the ${payment.payment_method || 'card'} payment of $${formatPrice(amount)}? Refund the customer on the standalone terminal first, THEN click Confirm.`
    } else {
      preConfirmMessage = `Cancel the ${payment.payment_method || 'payment'} of $${formatPrice(amount)}?`
    }

    showConfirm({
      title: 'Cancel Payment?',
      message: preConfirmMessage,
      confirmLabel: 'Cancel Payment',
      cancelLabel: 'Keep',
      variant: 'danger',
      onConfirm: runCancel,
    })
  }

  const handleClose = () => {
    // A fully-tendered sale must reset the register even while the receipt is
    // still printing. printReceipt is fire-and-forget -- it keeps running after
    // the modal closes -- so gating the reset on it stranded paid card sales:
    // the cart + customer stayed bound to a Paid receipt whenever the print
    // lagged behind the terminal's post-payment work (or a server-side print
    // hung). Cancel / error steps keep the original guard.
    if (isPrinting && step !== 'complete') return
    if (isRefundMode) {
      exitRefundMode()
      queryClient.invalidateQueries({ queryKey: ['receipts', 'refund-search'] })
    }
    // Reset product filters once a sale is fully tendered and dismissed.
    // Keyed off step === 'complete' so cancel / error paths preserve the
    // cashier's filter context for a retry. The next customer at the
    // counter sees the full catalog.
    if (step === 'complete') {
      setUIFilters(DEFAULT_UI_FILTERS)
    }
    resetTerminalState()
    clearReceipt()
    closeModal()
  }

  // On-demand print from the post-tender screen, used by the 'ask' and
  // 'print_and_menu'-style flows where auto-print didn't run (or the
  // cashier wants a reprint). Idempotent — `usePrintReceipt` is a one-shot
  // mutation, so a second click during isPending no-ops.
  const handlePrintFromComplete = () => {
    const id = lastReceipt?.id ?? null
    if (id == null || isPrinting) return
    printReceipt.mutate({ receiptId: id })
  }

  const handleRetry = () => {
    resetTerminalState()
    // Clear any register_busy recovery state so the next failure
    // (which may be a different code entirely) doesn't render with
    // stale Cancel Previous & Retry buttons.
    setBusyTerminalSessionId(null)
    setBusyTerminalReceiptId(null)
    setStep('method')
    setErrorMessage('')
  }

  // Cash quick-tender amounts: the exact change-due, then the next round
  // multiple at each common bill denomination ($5 / $10 / $20 / $50).
  // Mirrors how cashiers think when accepting bills — "exact, or the next
  // round bill the customer might hand over." Deduped so a perfectly-round
  // amount (e.g. $20.00 → all four ceil steps land on $20) doesn't render
  // identical buttons.
  const ceilTo = (n: number, step: number) => Math.ceil(n / step) * step
  const exactDue = Number(absoluteAmountDue.toFixed(2))
  const quickAmounts = Array.from(
    new Set([
      exactDue,
      ceilTo(absoluteAmountDue, 5),
      ceilTo(absoluteAmountDue, 10),
      ceilTo(absoluteAmountDue, 20),
      ceilTo(absoluteAmountDue, 50),
    ]),
  ).sort((a, b) => a - b)
  const isBackDisabled = isProcessing

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={!isAnyPending && step !== 'complete' ? closeModal : undefined}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {(step === 'cash' || step === 'card' || step === 'card-type' || step === 'online-card' || step === 'gift-card' || step === 'loyalty' || step === 'error') && !isBackDisabled && (
              <button
                onClick={() => {
                  // Sale-mode standalone routes through card-type → card,
                  // so backing out of 'card' returns to the brand picker
                  // (cashier can change Visa→Interac without re-picking
                  // Standalone). Refund-mode standalone skips card-type
                  // entirely and goes straight to the method screen.
                  if (step === 'card' && !isRefundMode && selectedMethod?.id === 'debit') {
                    setStep('card-type')
                  } else {
                    setStep('method')
                  }
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-900">
              {step === 'complete' ? 'Payment Complete' : step === 'error' && !errorIsSaleHours ? 'Payment Error' : 'Checkout'}
            </h2>
          </div>
          {step !== 'processing' && step !== 'complete' && !isProcessing && (
            <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
              <X size={20} />
            </button>
          )}
        </div>

        <div className="p-6">
          {/* Method Selection */}
          {step === 'method' && (
            <>
              {gateBannerText && (
                <div className="mb-4 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
                  <AlertCircle size={16} className="text-amber-600 shrink-0" />
                  <span className="text-sm text-amber-800 leading-tight flex-1">{gateBannerText}</span>
                  {!isRefundMode && !isReceiptPartiallyPaid(serverTotals, isRefundMode) && (
                    <button
                      type="button"
                      onClick={() => setGateApprovalItemsOpen(gateApprovalItems(gateError?.extra))}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 cursor-pointer shrink-0"
                    >
                      Approve
                    </button>
                  )}
                </div>
              )}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                {!isPartialPayment ? (
                  <>
                    {/* Render signed values verbatim — refund subtotal /
                        tax / total come back negative from /cart/preview;
                        sale values positive. Matches CartSummary so the
                        Total here and the Total in the right sidebar
                        always agree. */}
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Subtotal</span>
                      <span>${formatPrice(subtotal)}</span>
                    </div>
                    {cartDiscount && cartDiscountAmount > 0 && (
                      <div className="flex justify-between text-sm mb-2 text-green-700">
                        <span>
                          {loyaltyDiscountApplied ? 'Loyalty' : 'Discount'}
                          {cartDiscount.percent != null
                            ? ` (${cartDiscount.percent}%)`
                            : cartDiscount.amount != null
                              ? ` (${formatPrice(cartDiscount.amount)})`
                              : ''}
                        </span>
                        <span>−${formatPrice(cartDiscountAmount)}</span>
                      </div>
                    )}
                    {cartDiscount && cartDiscountAmount > 0 && receiptApprovedBy && (
                      <div className="text-xs text-gray-400 -mt-1.5 mb-2">{receiptApprovedBy}</div>
                    )}
                    {!cartDiscount && lineApprovedBy && (
                      <div className="text-xs text-gray-400 mb-2">Item discount {lineApprovedBy}</div>
                    )}
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Tax ({taxName})</span>
                      <span>${formatPrice(taxAmount)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                      <span>{isRefundMode ? 'Refund Total' : 'Total'}</span>
                      <span className={isRefundMode ? 'text-red-600' : 'text-green-600'}>
                        ${formatPrice(cartTotal)}
                      </span>
                    </div>
                    {stagedLoyaltyAmount > 0 && (
                      <>
                        <div className="flex justify-between text-sm mt-2 text-green-700">
                          <span>Loyalty</span>
                          <span>−${formatPrice(stagedLoyaltyAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm font-semibold pt-2 mt-1 border-t border-gray-200">
                          <span>To collect</span>
                          <span className="tabular-nums">
                            ${formatPrice(Math.max(cartTotal - stagedLoyaltyAmount, 0))}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Order Total</span>
                      <span>${formatPrice(cartTotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2 text-green-600">
                      <span>Paid</span>
                      <span>-${formatPrice(cartTotal - amountDue)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                      <span>Remaining</span>
                      <span className="text-amber-600">${formatPrice(amountDue)}</span>
                    </div>
                    {stagedLoyaltyAmount > 0 && (
                      <>
                        <div className="flex justify-between text-sm mt-2 text-green-700">
                          <span>Loyalty</span>
                          <span>−${formatPrice(stagedLoyaltyAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm font-semibold pt-2 mt-1 border-t border-gray-200">
                          <span>To collect</span>
                          <span className="tabular-nums">
                            ${formatPrice(Math.max(amountDue - stagedLoyaltyAmount, 0))}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Payments so far — rendered when at least one tender
                  has landed on this receipt during the current
                  checkout session. Source of truth is the receipt's
                  payments array (canonical, ordered). */}
              {lastReceipt?.payments && lastReceipt.payments.length > 0 && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-[11px] font-semibold text-blue-900 uppercase tracking-wider mb-2">
                    {isRefundMode ? 'Refunds Recorded' : 'Payments So Far'}
                  </p>
                  <ul className="space-y-1 text-sm">
                    {lastReceipt.payments
                      // Hide system-computed entries that aren't real
                      // tenders the cashier can cancel. CHANGE
                      // (type=218) belongs in the change-due summary,
                      // not in the partial-payment list; ROUNDING_DIFF
                      // (type=280) is a non-cancellable bookkeeping
                      // adjustment. On a REFUND, type=299 is likewise
                      // not a tender -- it is the store reclaiming the
                      // reward it funded (in a sale it IS a real applied
                      // reward, and stays cancellable).
                      .filter((p) =>
                        p.payment_type !== PAYMENT_TYPES.CHANGE
                        && p.payment_type !== PAYMENT_TYPES.ROUNDING_DIFF
                        && !(isRefundMode && p.payment_type === PAYMENT_TYPES.SPRINGBIG_POINTS)
                      )
                      .map((p, i) => (
                      <li
                        key={p.id ?? `pay-${i}`}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="text-gray-700 flex items-center gap-1.5">
                          <PaymentMethodLabel
                            label={p.payment_method || `Payment ${i + 1}`}
                            paymentType={p.payment_type}
                            wallet={p.wallet}
                            last4={p.last4}
                          />
                        </span>
                        <span className="flex items-center gap-2">
                          {/* Amount stacked over the tip note so the
                              note never wraps mid-phrase against the
                              cancel button. */}
                          <span className="flex flex-col items-end">
                            <span className="text-gray-900 font-medium tabular-nums">
                              ${formatPrice(Math.abs(Number(p.amount ?? 0)))}
                            </span>
                            {Number(p.tip_amount ?? 0) > 0.005 && (
                              <span className="text-blue-700 text-xs font-normal whitespace-nowrap">
                                incl. ${formatPrice(Number(p.tip_amount ?? 0))} tip
                              </span>
                            )}
                          </span>
                          {/* Cancel partial payment via DELETE
                              /receipts/{id}/payments {amount}. Backend
                              voids the most-recent matching JT and
                              returns the updated receipt. */}
                          <button
                            type="button"
                            onClick={() => handleVoidPayment(p)}
                            disabled={isAnyPending || voidPayment.isPending}
                            title="Cancel this payment"
                            className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                          >
                            {voidPayment.isPending
                              ? <Loader2 size={14} className="animate-spin" />
                              : <Trash2 size={14} />
                            }
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Editable amount field (sale mode only). Pre-fills
                  with the outstanding balance; cashier can reduce it
                  for a partial. Capped at outstanding + $100 to
                  prevent typos like $25.32 → $253.20. */}
              {!isRefundMode && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Pay
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg font-medium">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={paymentAmountInput}
                      onChange={(e) => {
                        setPaymentAmountInput(clampToCents(e.target.value))
                        setPaymentAmountError(null)
                      }}
                      onBlur={(e) => {
                        const { error } = validatePaymentAmount(e.target.value)
                        setPaymentAmountError(error)
                      }}
                      placeholder="0.00"
                      disabled={isAnyPending}
                      className={`w-full pl-8 pr-4 py-3 text-lg font-bold border-2 rounded-xl focus:ring-0 outline-none disabled:bg-gray-50 disabled:cursor-not-allowed ${
                        paymentAmountError ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-green-500'
                      }`}
                    />
                  </div>
                  {paymentAmountError && (
                    <p className="text-xs text-red-600 mt-1.5">{paymentAmountError}</p>
                  )}
                  {!paymentAmountError && (() => {
                    const { value } = validatePaymentAmount(paymentAmountInput)
                    // The staged SpringBig redemption posts as its own
                    // POINTS payment, so it counts against the outstanding
                    // balance just like the cashier's tender. Subtract it
                    // here so a tender of (amountDue − redemption) reads
                    // as a full payment, not a partial.
                    const remaining = amountDue - value - stagedLoyaltyAmount
                    if (remaining > 0.005) {
                      return (
                        <p className="text-xs text-amber-700 mt-1.5">
                          Partial — ${formatPrice(remaining)} will remain after this tender.
                        </p>
                      )
                    }
                    if (remaining < -0.005) {
                      return (
                        <p className="text-xs text-amber-700 mt-1.5">
                          ${formatPrice(-remaining)} over balance. Allowed for cash (becomes change); card payments will be blocked.
                        </p>
                      )
                    }
                    return null
                  })()}
                </div>
              )}

              {/* Original payments — shown in refund mode so the cashier
                  sees how the customer originally paid. Source of truth
                  is /copy-with-refund's source_payments. Non-tender lines
                  (rounding diff, change) get muted styling so they don't
                  read as refund options. */}
              {isRefundMode && refundSourcePayments && refundSourcePayments.length > 0 && (
                <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider mb-2">Original Payments</p>
                  <ul className="space-y-1 text-sm">
                    {refundSourcePayments.map((p, i) => {
                      const methodId = PAYMENT_TYPE_TO_METHOD_ID[Number(p.payment_type)]
                      // The synthetic online entry carries payment_type
                      // null by design (old-build safety) but IS
                      // refundable — via the Online Card method.
                      const isRefundable = !!methodId || p.online === true
                      const amt = Math.abs(parseFloat(String(p.amount ?? '0'))) || 0
                      const label = p.description || (isRefundable ? methodId : `Type ${p.payment_type}`)
                      return (
                        <li
                          key={p.id ?? `src-${i}`}
                          className={`flex items-center justify-between gap-2 ${isRefundable ? 'text-gray-700' : 'text-gray-400'}`}
                        >
                          <span className="flex items-center gap-2">
                            {label}
                            {p.last4 && (
                              <span className="text-xs text-gray-500">•••• {p.last4}</span>
                            )}
                            {!isRefundable && (
                              <span className="text-[10px] uppercase tracking-wide text-gray-400">
                                non-refundable
                              </span>
                            )}
                          </span>
                          <span className="font-medium tabular-nums">
                            ${formatPrice(amt)}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              {/* SpringBig redemption staging — surfaces the staged
                  reward/offer (with a clear button) or, when nothing is
                  staged but redemptions are available, a CTA that opens
                  the picker as a sub-screen. Hidden in refund mode and
                  when there are no redemptions worth showing. */}
              {!isRefundMode && (pendingReward || pendingOffer || loyaltyDiscountApplied || checkoutHasRedemptions || receiptHasLoyaltyPayment) && (
                <div className="mb-4">
                  {pendingReward || pendingOffer ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                      <div className="w-9 h-9 rounded-lg bg-green-100 text-green-700 flex items-center justify-center shrink-0">
                        <Gift size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-green-900 uppercase tracking-wide">
                          {pendingReward ? 'Reward staged' : 'Offer staged'}
                        </p>
                        <p className="text-sm font-medium text-green-900 truncate">
                          {pendingReward?.label ?? pendingOffer?.label}
                          <span className="ml-1 tabular-nums">
                            − ${formatPrice(stagedLoyaltyAmount)}
                          </span>
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => clearPendingLoyalty()}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-white border border-green-200 text-green-700 hover:bg-green-100 transition-colors cursor-pointer shrink-0"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => setStep('loyalty')}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors cursor-pointer shrink-0"
                      >
                        Change
                      </button>
                    </div>
                  ) : loyaltySavesNothing ? (
                    <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                        <AlertCircle size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-amber-900 uppercase tracking-wide">
                          Loyalty saves $0.00
                        </p>
                        <p className="text-sm font-medium text-amber-900">
                          {loyaltyLocked
                            ? `${loyaltyDiscountLabel} takes nothing off this sale and is already on it. Void it under Payments to change it.`
                            : `${loyaltyDiscountLabel} takes nothing off this sale but will still be used up. Clear it to keep it for a later visit.`}
                        </p>
                      </div>
                      {!loyaltyLocked && (
                        <>
                          <button
                            type="button"
                            onClick={() => setCartDiscount(null)}
                            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-white border border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer shrink-0"
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              // The picker refuses a second redemption while
                              // this one holds the slot, so free it first.
                              setCartDiscount(null)
                              showToast({
                                type: 'info',
                                title: 'Offer removed',
                                message: 'Pick another, or go back to continue without one.',
                              })
                              setStep('loyalty')
                            }}
                            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors cursor-pointer shrink-0"
                          >
                            Change
                          </button>
                        </>
                      )}
                    </div>
                  ) : loyaltyDiscountApplied ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                      <div className="w-9 h-9 rounded-lg bg-green-100 text-green-700 flex items-center justify-center shrink-0">
                        <Gift size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-green-900 uppercase tracking-wide">
                          Loyalty applied
                        </p>
                        <p className="text-sm font-medium text-green-900 truncate">
                          {loyaltyDiscountLabel}
                          {cartDiscountAmount > 0 && (
                            <span className="ml-1 tabular-nums">− ${formatPrice(cartDiscountAmount)}</span>
                          )}
                        </p>
                        {loyaltyLocked && (
                          <p className="text-xs text-green-800">
                            Already on this sale. Void it under Payments to change it.
                          </p>
                        )}
                      </div>
                      {!loyaltyLocked && (
                        <>
                          <button
                            type="button"
                            onClick={() => setCartDiscount(null)}
                            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-white border border-green-200 text-green-700 hover:bg-green-100 transition-colors cursor-pointer shrink-0"
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              // Same as the amber banner: the picker refuses a
                              // second redemption while this one holds the slot.
                              setCartDiscount(null)
                              showToast({
                                type: 'info',
                                title: 'Offer removed',
                                message: 'Pick another, or go back to continue without one.',
                              })
                              setStep('loyalty')
                            }}
                            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors cursor-pointer shrink-0"
                          >
                            Change
                          </button>
                        </>
                      )}
                    </div>
                  ) : receiptHasLoyaltyPayment ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                      <div className="w-9 h-9 rounded-lg bg-green-100 text-green-700 flex items-center justify-center shrink-0">
                        <Gift size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-green-900 uppercase tracking-wide">
                          Loyalty redeemed
                        </p>
                        <p className="text-sm font-medium text-green-900 truncate">
                          A reward or offer is applied to this sale. Void it under Payments to change it.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setStep('loyalty')}
                      className="w-full flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-xl transition-colors cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                        <Gift size={18} className="animate-pulse" />
                      </div>
                      <div className="min-w-0 flex-1 text-left">
                        <p className="text-[11px] font-semibold text-amber-900 uppercase tracking-wide">
                          Loyalty available
                        </p>
                        <p className="text-sm font-medium text-amber-900 truncate">
                          {redemptionCountLabel(checkoutSpringbig?.rewards?.length ?? 0, checkoutSpringbig?.offers?.length ?? 0)}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-amber-700 shrink-0">Apply →</span>
                    </button>
                  )}
                </div>
              )}

              <p className="text-sm text-gray-500 mb-3">
                {isRefundMode
                  ? 'Select refund method:'
                  : isPartialPayment
                    ? 'Select additional payment method:'
                    : 'Select payment method:'}
              </p>

              {pricesUnconfirmed && (
                <p className="text-xs text-amber-600 mb-3 -mt-1">
                  {previewStatus === 'error'
                    ? 'Prices unavailable — check your connection before taking payment.'
                    : 'Confirming prices…'}
                </p>
              )}

              {/* Reward-only balance: no rail can take it, so the grid
                  below is entirely disabled. Without this the refund is
                  simply impossible to finish — the reward credit only
                  ever rode along with a real tender. */}
              {canSettleReward && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-[11px] font-semibold text-green-900 uppercase tracking-wider mb-1">
                    Paid by reward
                  </p>
                  <p className="text-sm text-green-900 mb-3">
                    The remaining ${formatPrice(absoluteAmountDue)} was paid with a
                    reward, so there's no payment method to send it back to.
                    Complete the refund to credit it to the store and return the
                    customer's points.
                  </p>
                  <button
                    type="button"
                    onClick={handleSettleReward}
                    disabled={isAnyPending || settleReward.isPending}
                    className="w-full py-3 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    {settleReward.isPending
                      ? 'Completing…'
                      : `Complete Refund — $${formatPrice(absoluteAmountDue)}`}
                  </button>
                </div>
              )}

              {/* Rails at zero but the balance is NOT reward-funded: the
                  original tenders are already spoken for, in practice by
                  a refund recorded on an earlier attempt at this return.
                  Name it — a grid of greyed-out tiles reads as a bug and
                  sent one store through seven retries before anyone
                  escalated. */}
              {refundBlocked && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-[11px] font-semibold text-amber-900 uppercase tracking-wider mb-1">
                    No refund method available
                  </p>
                  <p className="text-sm text-amber-900">
                    ${formatPrice(absoluteAmountDue)} is still outstanding, but the
                    original payments have already been refunded in full. Check
                    "Refunds Recorded" above and this customer's earlier returns
                    before trying again — retrying here won't change it.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {(isRefundMode && onlineSourcePayment
                  ? [...paymentMethods, ONLINE_CARD_METHOD]
                  : paymentMethods
                ).map((method) => {
                  const Icon = method.icon
                  // Refund-method gating: gray out methods that weren't
                  // used in the original payment, and methods whose
                  // share is fully refunded already (split-refund mid-
                  // flow). The label below shows the per-method cap so
                  // the cashier sees exactly how much each one will
                  // accept.
                  const allowed = isMethodAllowed(method)
                  // The source-payment cap is one upper bound (how much
                  // of the original tender came in via this method); the
                  // outstanding refund (`absoluteAmountDue`) is the
                  // other. Display the binding one so a $53.93 refund
                  // against an $79.17 cash original reads "up to $53.93"
                  // -- not the original "up to $79.17", which led the
                  // cashier to overshoot when items were removed from
                  // the refund before checkout. refundWireAmount
                  // already does this clamp for the actual amount that
                  // gets wired; this just brings the label in line.
                  const sourceCap = isRefundMode
                    ? (method.id === 'debit' || method.id === 'credit_visa'
                        ? cardRailRemaining
                        : method.id === 'online_card'
                          ? onlineCardRemaining
                          : refundRemainingByMethod[method.id])
                    : undefined
                  const cap = sourceCap !== undefined
                    ? Math.min(sourceCap, absoluteAmountDue)
                    : undefined
                  const showCap =
                    isRefundMode
                    && refundSourcePayments
                    && refundSourcePayments.length > 0
                    && cap !== undefined
                  // Disabled = pending, not allowed for this sale, or prices
                  // not yet confirmed. Branch the VISUAL on this (not just
                  // `allowed`) so a prices-unconfirmed button reads as disabled
                  // (dimmed) instead of keeping its interactive look.
                  const isMethodDisabled =
                    isAnyPending || !allowed || pricesUnconfirmed
                  return (
                    <button
                      key={method.id}
                      onClick={() => handleSelectMethod(method)}
                      disabled={isMethodDisabled}
                      title={
                        pricesUnconfirmed
                          ? (previewStatus === 'error'
                              ? 'Prices unavailable — check your connection'
                              : 'Confirming prices…')
                          : !allowed && isRefundMode
                            ? 'Not used on the original payment'
                            : undefined
                      }
                      className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl transition-all ${
                        !isMethodDisabled
                          ? 'border-gray-200 hover:border-green-500 hover:bg-green-50 cursor-pointer'
                          : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-50'
                      } disabled:cursor-not-allowed`}
                    >
                      <div className={`p-3 ${!isMethodDisabled ? method.color : 'bg-gray-300'} rounded-xl text-white`}>
                        <Icon size={24} />
                      </div>
                      <span className="font-medium text-gray-900">{method.label}</span>
                      {showCap && (
                        <span className={`text-xs tabular-nums ${allowed ? 'text-gray-600' : 'text-gray-400'}`}>
                          up to ${formatPrice(cap ?? 0)}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {/* Cash */}
          {step === 'cash' && (
            <>
              <div className="mb-6 text-center">
                <p className="text-gray-500 mb-1">{isRefundMode ? 'Amount to Refund' : 'Amount Due'}</p>
                <p className={`text-4xl font-bold ${isRefundMode ? 'text-red-600' : 'text-gray-900'}`}>
                  {/* Refund mode shows the per-method capped amount so
                      a $30 refund split as $20 visa + $10 cash reads
                      "$10" on the cash step, not the full $30. */}
                  {isRefundMode ? '-' : ''}${formatPrice(isRefundMode ? Math.abs(cashWireAmount) : cashWireAmount)}
                </p>
                {isRefundMode && (() => {
                  // What's left after this cash share splits in two: an
                  // amount another *real* tender (card/gift) can still
                  // take, and the store-funded reward that has no tender
                  // destination -- the BE credits the latter back to the
                  // store automatically, so don't tell the cashier to
                  // "use another method" for it.
                  const otherMethod = Math.max(
                    0,
                    absoluteAmountDue - Math.abs(cashWireAmount) - nonRefundableRemaining,
                  )
                  return (
                    <>
                      {otherMethod > 0.005 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Cash share of the original payment. Remaining ${formatPrice(otherMethod)} must use another method.
                        </p>
                      )}
                      {nonRefundableRemaining > 0.005 && (
                        <p className="text-xs text-gray-500 mt-1">
                          ${formatPrice(nonRefundableRemaining)} was paid by a non-refundable reward — credited back to the store automatically, not refunded to the customer.
                        </p>
                      )}
                    </>
                  )
                })()}
                {!isRefundMode && (amountDue - cashWireAmount - stagedLoyaltyAmount) > 0.005 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Partial payment of ${formatPrice(amountDue - cashWireAmount - stagedLoyaltyAmount)} will remain after this tender.
                  </p>
                )}
              </div>

              {!isRefundMode && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cash Tendered</label>
                  <input
                    type="number"
                    value={cashTendered}
                    onChange={(e) => setCashTendered(clampToCents(e.target.value))}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      cashTenderedNum >= cashWireAmount &&
                      handleCashPayment()
                    }
                    placeholder="0.00"
                    disabled={isProcessing}
                    className={`w-full px-4 py-3 text-2xl text-center font-bold border-2 rounded-xl focus:ring-0 outline-none disabled:bg-gray-50 disabled:cursor-not-allowed ${
                      isCashOverpaid
                        ? 'border-amber-400 focus:border-amber-500'
                        : 'border-gray-200 focus:border-green-500'
                    }`}
                    autoFocus
                  />
                  {isCashOverpaid && (
                    <p className="mt-2 text-xs text-amber-700 flex items-center gap-1.5">
                      <AlertCircle size={12} />
                      Tender is over ${formatPrice(cashOverpayCap)} (amount + $100). You'll be asked to confirm.
                    </p>
                  )}
                </div>
              )}

              {!isRefundMode && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {quickAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCashTendered(amount.toFixed(2))}
                      disabled={isProcessing}
                      className="flex-1 min-w-[70px] py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors cursor-pointer"
                    >
                      {/* Whole-dollar amounts render as `$95`, exact-cents
                          renders as `$91.27` — keeps the round-up buttons
                          visually distinct from the exact-change one. */}
                      ${Number.isInteger(amount) ? amount : formatPrice(amount)}
                    </button>
                  ))}
                </div>
              )}

              {!isRefundMode && cashTenderedNum >= cashWireAmount && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-center">
                  <p className="text-sm text-gray-500">Change Due</p>
                  <p className="text-3xl font-bold text-green-600">${formatPrice(roundCashChange(calculatedChangeDue))}</p>
                </div>
              )}

              <button
                onClick={handleCashPayment}
                disabled={(!isRefundMode && cashTenderedNum < cashWireAmount) || isProcessing}
                className="w-full py-4 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                {isProcessing && <Loader2 size={20} className="animate-spin" />}
                {isProcessing ? 'Processing...' : isRefundMode ? 'Complete Cash Refund' : 'Complete Cash Payment'}
              </button>
            </>
          )}

          {/* Card brand picker — Standalone, both sale and refund. The
              cashier tells us which card was run on the non-integrated
              terminal so the JT records the matching payment_type.
              Picking here transitions to the 'card' confirm step. */}
          {step === 'card-type' && (
            <>
              <div className="mb-6 text-center">
                <p className="text-gray-500 mb-1">{isRefundMode ? 'Refund Amount' : 'Amount Due'}</p>
                <p className="text-3xl font-bold text-gray-900">
                  {isRefundMode ? '-' : ''}${formatPrice(Math.abs(cashWireAmount))}
                </p>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                {isRefundMode ? 'Select card type for refund:' : 'Select card type:'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {STANDALONE_CARD_TYPES.map((cardType) => (
                  <button
                    key={cardType.id}
                    onClick={() => handleSelectStandaloneCardType(cardType)}
                    disabled={isAnyPending}
                    className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <div className={`p-3 ${cardType.color} rounded-xl text-white`}>
                      <CreditCard size={24} />
                    </div>
                    <span className="font-medium text-gray-900">{cardType.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Card — Standalone (manual entry, non-integrated terminal) */}
          {/* Card — Standalone (manual) terminal. Two explicit outcomes
              instead of a single "Payment Complete" button: the POS books
              the sale ONLY when the cashier confirms the terminal showed
              APPROVED. The single-button version trained cashiers to
              press it before the terminal finished; when the terminal
              then declined, the already-booked receipt had no visible
              exit and they re-rang the whole sale — the July 2026
              double-charge cluster. "Declined" books NOTHING (the commit
              hasn't happened yet) and returns to the method screen so
              the cashier can retry or switch tender. */}
          {step === 'card' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {isProcessing
                  ? <Loader2 size={40} className="text-blue-600 animate-spin" />
                  : <CreditCard size={40} className="text-blue-600" />
                }
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isProcessing ? 'Processing...' : 'Run Card on Terminal'}
              </h3>
              <p className="text-gray-500 mb-2">
                {isProcessing
                  ? 'Please wait while we record the payment.'
                  : `Run the amount below on the ${isRefundMode ? 'terminal as a refund' : 'standalone terminal'} and wait for the result.`}
              </p>
              <p className="text-3xl font-bold text-gray-900 mb-6">${formatPrice(cashWireAmount)}</p>
              {!isProcessing && (
                <p className="text-gray-700 font-medium mb-3">
                  Did the terminal show APPROVED?
                </p>
              )}
              <button
                onClick={handleCardPayment}
                disabled={isProcessing}
                className="w-full py-4 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                {isProcessing && <Loader2 size={20} className="animate-spin" />}
                {isProcessing ? 'Processing...' : 'Approved — Complete Sale'}
              </button>
              {!isProcessing && (
                <button
                  onClick={handleCardDeclined}
                  className="w-full mt-3 py-3 bg-white border border-red-300 hover:bg-red-50 text-red-600 font-medium rounded-xl transition-colors cursor-pointer"
                >
                  Declined / Didn&apos;t Work
                </button>
              )}
            </div>
          )}

          {/* Online Card — gateway credit to the customer's original
              online (Moneris Checkout) card. No terminal involved; one
              confirm, then the BE refunds at the gateway and books the
              tender only if it approves. */}
          {step === 'online-card' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {isProcessing
                  ? <Loader2 size={40} className="text-indigo-600 animate-spin" />
                  : <CreditCard size={40} className="text-indigo-600" />
                }
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isProcessing ? 'Refunding…' : 'Refund to Online Card'}
              </h3>
              <p className="text-gray-500 mb-2">
                {isProcessing
                  ? 'Contacting the payment gateway — don’t close this window.'
                  : `Credit the amount below back to the customer's card`
                    + `${onlineSourcePayment?.last4 ? ` ending in ${onlineSourcePayment.last4}` : ''}.`
                    + ' It can take a few business days to appear on their statement.'}
              </p>
              <p className="text-3xl font-bold text-gray-900 mb-6">
                ${formatPrice(refundWireAmount(selectedMethod))}
              </p>
              <button
                onClick={handleOnlineCardPayment}
                disabled={isProcessing}
                className="w-full py-4 bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                {isProcessing && <Loader2 size={20} className="animate-spin" />}
                {isProcessing ? 'Refunding…' : 'Refund to Card'}
              </button>
            </div>
          )}

          {/* Card — Integrated terminal (V2 terminal-payments session) */}
          {step === 'card-terminal' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 size={40} className="text-blue-600 animate-spin" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {/* Backend-driven prompt — "Insert card", "Enter PIN",
                    "Approving…", etc. Falls back to a starting message
                    while the start mutation is in flight (sessionId not
                    set yet). */}
                {terminalSession?.display_message
                  || (startTerminalPayment.isPending || commitCart.isPending
                    ? 'Starting payment…'
                    : 'Waiting for terminal…')}
              </h3>
              <p className="text-gray-500 mb-2">
                Follow the prompts on the card terminal.
              </p>
              <p className="text-3xl font-bold text-gray-900 mb-6">
                {/* Prefer the session's authoritative amount: it's what
                    the terminal is actually charging. Matters when we
                    reattach to an in-flight session via register_busy
                    Resume, where the live session — not the local cart —
                    is the source of truth for the amount. */}
                ${formatPrice(Math.abs(
                  terminalSession?.amount != null
                    ? Number(terminalSession.amount)
                    : cashWireAmount,
                ))}
              </p>
              <button
                onClick={handleCancelTerminal}
                disabled={
                  !terminalSessionId
                    || cancelTerminalPayment.isPending
                    || startTerminalPayment.isPending
                }
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                {cancelTerminalPayment.isPending && <Loader2 size={18} className="animate-spin" />}
                {cancelTerminalPayment.isPending ? 'Cancelling…' : 'Cancel Payment'}
              </button>
            </div>
          )}

          {/* Gift Card */}
          {step === 'gift-card' && (
            <>
              <div className="mb-6 text-center">
                <p className="text-gray-500 mb-1">Amount Due</p>
                <p className="text-3xl font-bold text-gray-900">${formatPrice(cashWireAmount)}</p>
                {(amountDue - cashWireAmount - stagedLoyaltyAmount) > 0.005 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Partial payment of ${formatPrice(amountDue - cashWireAmount - stagedLoyaltyAmount)} will remain after this tender.
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Gift Card Number</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={giftCardNumber}
                    onChange={(e) => { setGiftCardNumber(e.target.value); setGiftCardBalance(null); setGiftCardId(null); setGiftCardIsBirchmount(false); setGiftCardPin(''); setGiftCardLookupError(null) }}
                    onKeyDown={(e) => e.key === 'Enter' && handleCheckGiftCardBalance()}
                    placeholder="Enter card number"
                    disabled={isProcessing}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-0 outline-none disabled:bg-gray-50"
                    autoFocus
                  />
                  <button
                    onClick={handleCheckGiftCardBalance}
                    disabled={!giftCardNumber.trim() || checkGiftCardBalance.isPending || isProcessing}
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors cursor-pointer flex items-center gap-2"
                  >
                    {checkGiftCardBalance.isPending
                      ? <Loader2 size={18} className="animate-spin" />
                      : <ScanLine size={18} />
                    }
                    Check
                  </button>
                </div>
              </div>

              {giftCardLookupError && (
                <div className="mb-4 p-3 rounded-xl border bg-red-50 border-red-200 text-red-700 text-sm">
                  {giftCardLookupError}
                </div>
              )}

              {giftCardBalance !== null && (
                <div className={`mb-6 p-4 rounded-xl border text-center ${giftCardBalance >= amountDue
                    ? 'bg-green-50 border-green-200'
                    : 'bg-amber-50 border-amber-200'
                  }`}>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <p className="text-sm text-gray-500">Card Balance</p>
                    {giftCardIsBirchmount && (
                      <span className="text-[11px] font-semibold uppercase tracking-wide bg-white border border-current text-gray-600 px-1.5 py-0.5 rounded">
                        via Birchmount
                      </span>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-gray-900">${formatPrice(giftCardBalance)}</p>
                  {giftCardBalance < cashWireAmount && (
                    <p className="text-sm text-amber-600 mt-1">
                      Partial payment of ${formatPrice(giftCardBalance)} will be applied
                    </p>
                  )}
                </div>
              )}

              {giftCardBalance !== null && giftCardIsBirchmount && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">PIN</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    autoFocus
                    value={giftCardPin}
                    onChange={(e) => { setGiftCardPin(e.target.value); setGiftCardLookupError(null) }}
                    onKeyDown={(e) => e.key === 'Enter' && handleGiftCardPayment()}
                    placeholder="Enter PIN from card"
                    disabled={isProcessing}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-0 outline-none disabled:bg-gray-50"
                  />
                </div>
              )}

              <button
                onClick={handleGiftCardPayment}
                disabled={!giftCardNumber.trim() || giftCardBalance === null || giftCardMissingId || giftCardPinRequired || isProcessing || gcNeedsRecheck}
                title={gcNeedsRecheck ? 'Check the card balance before retrying — the previous attempt may have moved money.' : undefined}
                className="w-full py-4 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                {isProcessing && <Loader2 size={20} className="animate-spin" />}
                {isProcessing
                  ? 'Processing...'
                  : giftCardBalance !== null && giftCardBalance < cashWireAmount
                    ? `Apply $${formatPrice(giftCardBalance)}`
                    : 'Complete Gift Card Payment'
                }
              </button>
            </>
          )}

          {/* Loyalty redemption picker — sub-screen for staging a
              SpringBig reward/offer. Selecting a row sets the staging
              slot in posStore and routes back to the method screen via
              the picker's onApplied callback. */}
          {step === 'loyalty' && selectedCustomer && (
            <div>
              <p className="text-sm text-gray-500 mb-3">
                Pick a redemption to apply at checkout. A percent
                offer/reward applies as a pre-tax discount; a flat-dollar one
                is recorded as a points payment before the cashier's tender.
              </p>
              <LoyaltyRedemptionPicker
                customer={selectedCustomer}
                onApplied={() => setStep('method')}
                hideEmptySections
              />
            </div>
          )}

          {/* Processing */}
          {step === 'processing' && (
            <div className="text-center py-12">
              <Loader2 size={48} className="text-green-500 animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Processing Payment</h3>
              <p className="text-gray-500">
                {commitCart.isPending
                  ? 'Committing receipt and processing payment...'
                  : 'Processing payment...'}
              </p>
            </div>
          )}

          {/* Error */}
          {step === 'error' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={40} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {errorIsTerminal ? 'Receipt Closed' : errorIsSaleHours ? 'Outside Sale Hours' : 'Payment Failed'}
              </h3>
              <p className="text-gray-500 mb-6">
                {errorMessage || 'An error occurred while processing your payment.'}
                {errorIsSaleHours && saleHoursStoreTime && (
                  <span className="block">Current Store Time: {saleHoursStoreTime}</span>
                )}
              </p>
              {errorIsSaleHours ? (
                // outside_sale_hours -- nothing was charged and Try Again
                // would re-fail until the window opens. Back returns to the
                // method screen with the cart intact (cashier can Hold it).
                <button
                  onClick={handleRetry}
                  className="w-full py-4 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-xl transition-colors cursor-pointer"
                >
                  Back
                </button>
              ) : errorIsTerminal ? (
                // Terminal error — Try Again would re-fail and Void would
                // 500 (backend rejects ops on paid/locked receipts). The
                // useEffect above auto-dismisses after 2.5s; this hint
                // tells the cashier what's about to happen.
                <p className="text-xs text-gray-400">
                  Resetting cart for a fresh transaction…
                </p>
              ) : busyTerminalSessionId && busyTerminalReceiptId ? (
                // register_busy recovery: the BE pointed us at the
                // orphan PENDING PaymentSession that's blocking this
                // register. Cancel + poll-for-settle + replay; Try
                // Again as a fallback if the cashier wants the pre-
                // fix behavior (wait for reaper / call support).
                // Button label transitions through two phases:
                //   1. "Cancelling previous…" while the DELETE is in
                //      flight (cancelTerminalPayment.isPending).
                //   2. "Verifying…" while we poll GET to confirm the
                //      BE transitioned the row out of PENDING (covers
                //      the race where the orphan's driver might
                //      have approved at the same moment).
                <div className="space-y-3">
                  <button
                    onClick={handleCancelBusyAndRetry}
                    disabled={cancelTerminalPayment.isPending || isVerifyingCancel}
                    className="w-full py-4 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
                  >
                    {(cancelTerminalPayment.isPending || isVerifyingCancel) && <Loader2 size={20} className="animate-spin" />}
                    {cancelTerminalPayment.isPending
                      ? 'Cancelling previous…'
                      : isVerifyingCancel
                        ? 'Verifying…'
                        : 'Cancel Previous & Retry'}
                  </button>
                  <button
                    onClick={handleRetry}
                    disabled={cancelTerminalPayment.isPending || isVerifyingCancel}
                    className="w-full py-3 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-xl transition-colors cursor-pointer"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleRetry}
                  className="w-full py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-colors cursor-pointer"
                >
                  Try Again
                </button>
              )}
            </div>
          )}

          {/* Complete */}
          {step === 'complete' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {isPrinting
                  ? <Loader2 size={40} className="text-green-600 animate-spin" />
                  : <Check size={40} className="text-green-600" />
                }
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isPrinting ? 'Printing Receipt...' : 'Payment Successful!'}
              </h3>
              {/* Receipt number — pulled from the payment response
                  captured into `lastReceipt`. Fall back to the receipt
                  id if number isn't on the payload. Skipped during
                  printing so the success copy doesn't get crowded. */}
              {!isPrinting && (lastReceipt?.number || lastReceipt?.id) && (
                <p className="text-md font-medium text-gray-700 mb-1 select-text">
                  Order #{lastReceipt?.number ?? lastReceipt?.id}
                </p>
              )}
              <p className="text-gray-500 mb-6">
                {isPrinting
                  ? 'Sending receipt to printer.'
                  : selectedMethod?.paymentTypeNumber === PAYMENT_TYPES.CASH && changeDue > 0
                    ? `Change due: $${formatPrice(roundCashChange(changeDue))}`
                    : 'Thank you for your purchase.'}
              </p>
              {/* Buttons depend on `pos.receipt.complete_action`:
                  - 'ask'     → no auto-print fired; show Print Receipt + Done.
                  - 'print'   → auto-print fired; show only Done (Printing… while pending).
                  - 'nothing' → no auto-print and no print option; show only Done. */}
              {completeAction === 'ask' && !isPrinting && (
                <button
                  onClick={handlePrintFromComplete}
                  disabled={lastReceipt?.id == null}
                  className="w-full py-4 mb-3 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed border-2 border-green-500 text-green-700 font-semibold rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
                >
                  <Printer size={20} /> Print Receipt
                </button>
              )}
              {/* A completed sale can be dismissed even while its receipt is
                  still printing -- the print keeps running in the background and
                  the header above shows its progress. Blocking "Done" on
                  isPrinting was what stranded the cart + customer when a print
                  lagged or hung after a card payment. */}
              <button
                onClick={handleClose}
                className="w-full py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <Check size={20} /> Done
              </button>
            </div>
          )}
        </div>
      </div>
      <ManagerApprovalDialog
        isOpen={gateApprovalItemsOpen !== null}
        onClose={() => setGateApprovalItemsOpen(null)}
        title="Approve discount"
        subtitle={gateApprovalSubtitle}
        initialError={gateDialogInitialError(gateError)}
        items={approvalRows(gateApprovalItemsOpen ?? [])}
        onSubmit={(pin) => approveCartItems(gateApprovalItemsOpen ?? [], pin)}
      />
    </div>
  )
}

// Build a "X reward(s) and Y offer(s)" label for the loyalty CTA. Picks
// only the non-zero halves so we don't say "1 reward and 0 offers."
function redemptionCountLabel(rewardCount: number, offerCount: number): string {
  const parts: string[] = []
  if (rewardCount > 0) parts.push(`${rewardCount} reward${rewardCount === 1 ? '' : 's'}`)
  if (offerCount > 0) parts.push(`${offerCount} offer${offerCount === 1 ? '' : 's'}`)
  if (parts.length === 0) return 'Redemptions available'
  return parts.join(' · ')
}
